import React, { useState, useEffect, createContext, useContext, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Scale, Users, FileText, Briefcase, Shield, Building, Gavel, Mic, MicOff, Send, Upload, LogOut, Menu, X, ChevronRight, Play, Pause, Download, Plus, Search, MessageSquare, FolderOpen, BarChart3, Anchor, Plane, Crown, Globe, HelpCircle, ExternalLink, BookOpen, Star, Folder, Trash2, Leaf, Receipt, Landmark, Tractor, Eye, EyeOff, Phone, PhoneOff, Volume2, Calculator, UserCheck, ScrollText, ChevronDown, ArrowLeft, Info, Key, Target, Clock } from "lucide-react";
import CRMVentas from "./components/CRMVentas";
import AnalyticsTab from "./components/admin/AnalyticsTab";
import EncuestasTab from "./components/admin/EncuestasTab";
import EncuestaPage from "./pages/EncuestaPage";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { ScrollArea } from "./components/ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Analytics Tracker - Registra visitas de páginas
const usePageTracking = () => {
  const location = useLocation();
  
  useEffect(() => {
    const trackPageView = async () => {
      try {
        await fetch(`${API}/analytics/pageview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: location.pathname,
            referrer: document.referrer || null,
            user_agent: navigator.userAgent
          })
        });
      } catch (e) {
        // Silenciar errores de tracking
      }
    };
    trackPageView();
  }, [location.pathname]);
};

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("lila_token"));
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Verificar suscripción
      checkSubscription();
    } catch (error) {
      console.error("Auth error:", error.response?.data || error.message);
      // Solo hacer logout si es un error de autenticación real (401)
      if (error.response?.status === 401) {
        logout();
      } else {
        // Para otros errores (red, servidor), mantener la sesión pero mostrar error
        console.error("Error de conexión, manteniendo sesión local");
        setLoading(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/check-access`);
      setSubscription(response.data);
    } catch (error) {
      setSubscription({ has_access: false, reason: "error" });
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user } = response.data;
    localStorage.setItem("lila_token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(user);
    // Verificar suscripción después del login
    setTimeout(() => checkSubscription(), 500);
    return user;
  };

  const register = async (email, password, full_name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, full_name });
    const { access_token, user } = response.data;
    localStorage.setItem("lila_token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(user);
    // Verificar suscripción después del registro
    setTimeout(() => checkSubscription(), 500);
    return user;
  };

  const logout = () => {
    localStorage.removeItem("lila_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setSubscription(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, subscription, checkSubscription }}>
      {children}
    </AuthContext.Provider>
  );
};

// Subscription Alert Banner
const SubscriptionBanner = () => {
  const { subscription } = useAuth();
  const navigate = useNavigate();
  
  if (!subscription) return null;
  
  // Si tiene acceso, mostrar días restantes del trial
  if (subscription.has_access && subscription.plan === "trial" && subscription.days_left !== undefined) {
    if (subscription.days_left <= 2) {
      return (
        <div className="bg-amber-500/20 border-b border-amber-500/30 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <p className="text-amber-300 text-sm">
              ⚠️ Tu prueba gratuita termina en <strong>{subscription.days_left} día(s)</strong>. ¡Suscríbete para no perder acceso!
            </p>
            <button 
              onClick={() => navigate("/premium")}
              className="text-xs bg-amber-500 text-black px-3 py-1 rounded-full font-medium hover:bg-amber-400"
            >
              Ver Planes
            </button>
          </div>
        </div>
      );
    }
    return null;
  }
  
  // Si no tiene acceso, mostrar alerta roja
  if (!subscription.has_access) {
    return (
      <div className="bg-red-500/20 border-b border-red-500/30 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <p className="text-red-300 text-sm">
            🔒 {subscription.message || "Tu período de prueba ha expirado."}
          </p>
          <button 
            onClick={() => navigate("/premium")}
            className="text-sm bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1.5 rounded-full font-medium hover:from-purple-500 hover:to-pink-500"
          >
            Suscribirse Ahora
          </button>
        </div>
      </div>
    );
  }
  
  return null;
};

// Access Blocked Modal
const AccessBlockedModal = ({ show, onClose }) => {
  const navigate = useNavigate();
  
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/80" onClick={onClose} />
      <div className="relative z-50 w-full max-w-md glass border border-red-500/30 rounded-2xl p-6 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
          <Crown className="text-red-400" size={32} />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">
          Acceso Restringido
        </h2>
        <p className="text-slate-400 mb-6">
          Tu período de prueba ha expirado. Suscríbete a un plan premium para continuar usando todas las funciones de Lila.
        </p>
        <div className="space-y-3">
          <button
            onClick={() => { onClose(); navigate("/premium"); }}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-xl font-medium"
          >
            Ver Planes de Suscripción
          </button>
          <button
            onClick={onClose}
            className="w-full py-2 text-slate-400 hover:text-white"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

const ScaleAvatar = ({ size = 120, animated = true, speaking = false }) => (
  <div className={`relative ${animated ? "animate-float" : ""}`}>
    <div className={`${speaking ? "animate-scale-pulse" : ""}`}>
      <Scale 
        size={size} 
        className="text-cyan-400 scale-icon"
        strokeWidth={1.5}
      />
    </div>
    <div className="absolute inset-0 bg-cyan-400/20 rounded-full blur-3xl -z-10" />
  </div>
);

// Legal Area Icons Map
const legalIcons = {
  familia: Users,
  civil: FileText,
  laboral: Briefcase,
  penal: Shield,
  mercantil: Building,
  constitucional: Scale,
  maritimo: Anchor,
  migratorio: Plane,
  ambiental: Leaf,
  tributario: Receipt,
  administrativo: Landmark,
  agrario: Tractor
};

// ===================== FORGOT PASSWORD PAGE =====================
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Si el email existe, recibirás un enlace de recuperación.");
    } catch (error) {
      toast.error("Error al enviar el email. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-pattern flex items-center justify-center p-4">
      <div className="w-full max-w-md glass border border-slate-700 rounded-2xl p-8">
        <div className="text-center mb-6">
          <Scale className="w-12 h-12 mx-auto text-cyan-400 mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">¿Olvidaste tu contraseña?</h1>
          <p className="text-slate-400 text-sm">
            {sent ? "Revisa tu bandeja de entrada" : "Ingresa tu email para recuperarla"}
          </p>
        </div>

        {!sent ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Email</label>
              <Input
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-900/50 border-slate-700 h-12"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary h-12"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Enviando...
                </>
              ) : (
                "Enviar enlace de recuperación"
              )}
            </Button>
          </form>
        ) : (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-slate-300 mb-4">
              Hemos enviado un enlace de recuperación a <strong className="text-cyan-400">{email}</strong>
            </p>
            <p className="text-slate-500 text-sm mb-6">
              Revisa tu bandeja de entrada y spam. El enlace expira en 1 hora.
            </p>
          </div>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate("/auth")}
            className="text-cyan-400 hover:text-cyan-300 text-sm"
          >
            ← Volver al inicio de sesión
          </button>
        </div>
      </div>
    </div>
  );
};

// ===================== RESET PASSWORD PAGE =====================
const ResetPasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const token = new URLSearchParams(location.search).get('token');

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setTokenValid(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
      setTokenValid(true);
      setUserEmail(response.data.email);
    } catch (error) {
      setTokenValid(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success("¡Contraseña actualizada exitosamente!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar la contraseña");
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-pattern flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-pattern flex items-center justify-center p-4">
        <div className="w-full max-w-md glass border border-red-500/50 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <X className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Enlace inválido o expirado</h1>
          <p className="text-slate-400 mb-6">
            Este enlace de recuperación ya no es válido. Por favor, solicita uno nuevo.
          </p>
          <Button onClick={() => navigate("/forgot-password")} className="btn-primary">
            Solicitar nuevo enlace
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pattern flex items-center justify-center p-4">
      <div className="w-full max-w-md glass border border-slate-700 rounded-2xl p-8">
        <div className="text-center mb-6">
          <Scale className="w-12 h-12 mx-auto text-cyan-400 mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">
            {success ? "¡Listo!" : "Nueva Contraseña"}
          </h1>
          <p className="text-slate-400 text-sm">
            {success ? "Tu contraseña ha sido actualizada" : `Para: ${userEmail}`}
          </p>
        </div>

        {!success ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Nueva Contraseña</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Mínimo 6 caracteres"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-900/50 border-slate-700 h-12 pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Confirmar Contraseña</label>
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Repite la contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-slate-900/50 border-slate-700 h-12"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary h-12"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Actualizando...
                </>
              ) : (
                "Actualizar Contraseña"
              )}
            </Button>
          </form>
        ) : (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <Button onClick={() => navigate("/auth")} className="btn-primary">
              Iniciar Sesión
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Auth Page - Landing Imponente
const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  // Capturar el evento de instalación PWA
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallApp = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('¡App instalada correctamente!');
      }
      setInstallPrompt(null);
    } else {
      // Mostrar instrucciones manuales para iOS o navegadores sin soporte
      toast.info('Para instalar: Abre el menú del navegador y selecciona "Añadir a pantalla de inicio"');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, fullName);
      }
      toast.success(isLogin ? "¡Bienvenido de vuelta!" : "¡Cuenta creada exitosamente!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error en la autenticación");
    } finally {
      setLoading(false);
    }
  };

  const LILA_IMAGE = "https://customer-assets.emergentagent.com/job_justice-scale-law/artifacts/vxot5lqm_WhatsApp%20Image%202026-03-03%20at%2010.06.47%20PM%20%281%29.jpeg";

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="auth-page">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${LILA_IMAGE})`,
          filter: 'brightness(0.4)'
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/70 to-transparent" />
      
      {/* Content */}
      <div className="relative z-10 min-h-screen flex">
        {/* Left Side - Info */}
        <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 py-12">
          <div className="max-w-xl">
            <div className="flex items-center gap-3 mb-6">
              <Scale className="text-cyan-400 w-10 h-10" />
              <span className="text-cyan-400 text-sm font-medium tracking-widest uppercase">Legal Tech Panamá</span>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight">
              <span className="text-white">Conoce a</span>
              <br />
              <span className="gradient-text text-6xl lg:text-8xl">LILA</span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-slate-300 mb-8 leading-relaxed">
              Tu asistente legal con <span className="text-cyan-400 font-semibold">Inteligencia Artificial</span> especializada en las leyes de Panamá
            </p>
            
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              <div className="flex items-center gap-3 text-slate-300">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <FileText className="text-cyan-400" size={20} />
                </div>
                <span>Genera Documentos</span>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <MessageSquare className="text-purple-400" size={20} />
                </div>
                <span>Consultas 24/7</span>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Gavel className="text-yellow-400" size={20} />
                </div>
                <span className="font-semibold text-yellow-400">Jurisprudencia</span>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <BarChart3 className="text-cyan-400" size={20} />
                </div>
                <span>Analiza Casos</span>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Mic className="text-purple-400" size={20} />
                </div>
                <span>Asistente por Voz</span>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Scale className="text-green-400" size={20} />
                </div>
                <span>Leyes Panameñas</span>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Button
                data-testid="start-btn"
                onClick={() => setShowAuthModal(true)}
                className="btn-primary px-8 py-6 text-lg rounded-xl"
              >
                Comenzar Ahora
                <ChevronRight className="ml-2" size={20} />
              </Button>
              <Button
                variant="outline"
                className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 px-8 py-6 text-lg rounded-xl"
                onClick={() => { setIsLogin(true); setShowAuthModal(true); }}
              >
                Ya tengo cuenta
              </Button>
              <Button
                variant="outline"
                onClick={handleInstallApp}
                className="border-green-500/50 text-green-400 hover:bg-green-500/10 px-6 py-6 text-lg rounded-xl"
              >
                <Download className="mr-2" size={20} />
                Descargar App
              </Button>
            </div>
            
            {/* Social Proof - Estadísticas */}
            <div className="mt-8 flex flex-wrap gap-6 text-slate-400">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm">+500 consultas resueltas en Panamá</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="text-yellow-400" size={16} />
                <span className="text-sm">4.9/5 satisfacción</span>
              </div>
            </div>
            
            {/* Testimonio destacado */}
            <div className="mt-8 glass border border-cyan-500/30 rounded-xl p-5 max-w-md">
              <div className="flex items-center gap-1 mb-2">
                {[1,2,3,4,5].map(i => <Star key={i} className="text-yellow-400" size={14} fill="currentColor" />)}
              </div>
              <p className="text-slate-300 text-sm italic mb-3">"El simulador de examen oral es mi salvación. <strong className="text-cyan-400">¡Pasé mi parcial de Civil con 5.0!</strong>"</p>
              <div className="flex items-center gap-3">
                <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="Elena" className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <p className="text-white font-medium text-sm">Elena R.</p>
                  <p className="text-slate-500 text-xs">Estudiante de Derecho, <strong className="text-cyan-400">Panamá</strong></p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right Side - Visual (hidden on mobile, the bg image shows) */}
        <div className="hidden lg:flex flex-1 items-center justify-center">
          {/* The robot image is in the background */}
        </div>
      </div>
      
      {/* Sección de Testimonios */}
      <div className="relative z-10 py-16 px-8 bg-slate-950/80">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-2 text-white">Lo que dicen nuestros usuarios</h2>
          <p className="text-slate-400 text-center mb-10">Abogados y estudiantes de <strong className="text-cyan-400">Panamá</strong> confían en LILA</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Testimonio Destacado - Abogado */}
            <div className="glass border border-amber-500/50 rounded-xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-amber-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">Abogado</div>
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map(i => <Star key={i} className="text-yellow-400" size={16} fill="currentColor" />)}
              </div>
              <p className="text-slate-300 mb-4 italic">"La <strong className="text-amber-400">base de jurisprudencia</strong> de LILA me ahorra horas de investigación. Encuentro sentencias relevantes en segundos. <strong className="text-green-400">Herramienta indispensable</strong> para mi práctica."</p>
              <div className="flex items-center gap-3">
                <img src="/alberto-mendoza.jpg" alt="Licdo. Alberto Mendoza" className="w-12 h-12 rounded-full border-2 border-amber-500/50 object-cover" />
                <div>
                  <p className="text-white font-medium">Licdo. Alberto Mendoza</p>
                  <p className="text-slate-500 text-sm">Abogado, <strong className="text-cyan-400">Panamá</strong></p>
                </div>
              </div>
            </div>
            
            <div className="glass border border-slate-700 rounded-xl p-6 hover:border-cyan-500/50 transition-colors">
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map(i => <Star key={i} className="text-yellow-400" size={16} fill="currentColor" />)}
              </div>
              <p className="text-slate-300 mb-4 italic">"Increíble. Tenía dudas sobre un contrato de arrendamiento comercial y LILA me explicó los puntos clave en segundos. <strong className="text-green-400">Ahorré mucho tiempo y dinero.</strong>"</p>
              <div className="flex items-center gap-3">
                <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Carlos" className="w-12 h-12 rounded-full border-2 border-cyan-500/50 object-cover" />
                <div>
                  <p className="text-white font-medium">Carlos M.</p>
                  <p className="text-slate-500 text-sm">Emprendedor, <strong className="text-cyan-400">Panamá</strong></p>
                </div>
              </div>
            </div>
            
            <div className="glass border border-purple-500/50 rounded-xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">Favorito</div>
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map(i => <Star key={i} className="text-yellow-400" size={16} fill="currentColor" />)}
              </div>
              <p className="text-slate-300 mb-4 italic">"El simulador de examen oral es mi salvación. Me ayuda a perder el miedo y a estructurar mis respuestas como una profesional. <strong className="text-green-400">¡Pasé mi parcial de Civil con 5.0!</strong>"</p>
              <div className="flex items-center gap-3">
                <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="Elena" className="w-12 h-12 rounded-full border-2 border-purple-500/50 object-cover" />
                <div>
                  <p className="text-white font-medium">Elena R.</p>
                  <p className="text-slate-500 text-sm">Estudiante de Derecho, <strong className="text-cyan-400">Panamá</strong></p>
                </div>
              </div>
            </div>
            
            <div className="glass border border-slate-700 rounded-xl p-6 hover:border-green-500/50 transition-colors">
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map(i => <Star key={i} className="text-yellow-400" size={16} fill="currentColor" />)}
              </div>
              <p className="text-slate-300 mb-4 italic">"No sabía cómo calcular mi liquidación tras un despido. LILA me guió paso a paso y llegué al <strong className="text-cyan-400">Ministerio de Trabajo</strong> con toda la información clara."</p>
              <div className="flex items-center gap-3">
                <img src="https://randomuser.me/api/portraits/men/75.jpg" alt="Ricardo" className="w-12 h-12 rounded-full border-2 border-green-500/50 object-cover" />
                <div>
                  <p className="text-white font-medium">Ricardo V.</p>
                  <p className="text-slate-500 text-sm">Ciudadano, <strong className="text-cyan-400">Panamá</strong></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Sección FAQ */}
      <div className="relative z-10 py-16 px-8 bg-slate-900/90">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-2 text-white">Preguntas Frecuentes</h2>
          <p className="text-slate-400 text-center mb-10">Todo lo que necesitas saber sobre LILA</p>
          
          <div className="space-y-4">
            <div className="glass border border-slate-700 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-2">¿LILA conoce las leyes actuales de Panamá?</h3>
              <p className="text-slate-400 text-sm">Sí. LILA está entrenada específicamente con la legislación panameña vigente (Código Civil, Laboral, Judicial, etc.) y se actualiza constantemente para ofrecerte respuestas precisas.</p>
            </div>
            
            <div className="glass border border-slate-700 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-2">¿Es seguro usar LILA para mis dudas legales?</h3>
              <p className="text-slate-400 text-sm">Absolutamente. Tus consultas son privadas y seguras. LILA es una herramienta de orientación inteligente diseñada para ahorrarte tiempo y prepararte mejor antes de acudir a un profesional.</p>
            </div>
            
            <div className="glass border border-slate-700 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-2">¿Cómo funcionan los 5 días de prueba gratis?</h3>
              <p className="text-slate-400 text-sm">Tienes acceso total a todas las funciones (incluyendo el Modo Estudiante) por 5 días. No hay compromiso; queremos que compruebes el poder de la IA legal en Panamá antes de decidirte.</p>
            </div>
            
            <div className="glass border border-slate-700 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-2">¿Puedo usar LILA si ya tengo un abogado?</h3>
              <p className="text-slate-400 text-sm">¡Es lo ideal! LILA te ayuda a resolver dudas simples y organizar tu caso para que, cuando hables con tu abogado, vayas directo al punto y ahorres en honorarios por consultas básicas.</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer con Disclaimer */}
      <footer className="relative z-10 py-8 px-8 bg-slate-950 border-t border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Scale className="text-cyan-400" size={24} />
              <span className="text-white font-bold">LILA</span>
              <span className="text-slate-400 text-sm">Legal Tech Panamá</span>
            </div>
            <div className="flex items-center gap-4 text-slate-400 text-sm">
              <span>© 2025 LILA</span>
              <span>•</span>
              <a href="mailto:soporte@linorttech.com" className="hover:text-cyan-400 transition-colors">soporte@linorttech.com</a>
            </div>
          </div>
          
          {/* Disclaimer Legal */}
          <div className="border-t border-slate-800 pt-6">
            <p className="text-slate-500 text-xs text-center leading-relaxed">
              <span className="text-amber-500 font-medium">IMPORTANTE:</span> LILA es una herramienta de Inteligencia Artificial para orientación legal y académica en Panamá. 
              No constituye asesoría legal profesional ni sustituye la figura de un abogado idóneo. 
              Para decisiones legales definitivas, siempre recomendamos la consulta con un profesional del derecho.
            </p>
          </div>
        </div>
      </footer>
      
      {/* Botón Flotante de WhatsApp */}
      <a
        href="https://wa.me/50769748906?text=Hola,%20tengo%20una%20duda%20sobre%20LILA"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 group"
        title="¿Dudas? Chatea con nosotros"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          ¿Dudas? ¡Chatea con nosotros!
        </span>
      </a>
      
      {/* Auth Modal */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="glass border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl gradient-text">
              {isLogin ? "Bienvenido de Vuelta" : "Únete a Lila"}
            </DialogTitle>
            <DialogDescription className="text-center text-slate-400">
              {isLogin ? "Ingresa tus credenciales para continuar" : "Crea tu cuenta para acceder a Lila"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {!isLogin && (
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Nombre Completo</label>
                <Input
                  data-testid="register-name-input"
                  type="text"
                  placeholder="Lic. Juan Pérez"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="bg-slate-900/50 border-slate-700 h-12"
                  required={!isLogin}
                />
              </div>
            )}
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Email</label>
              <Input
                data-testid="auth-email-input"
                type="email"
                placeholder="abogado@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-900/50 border-slate-700 h-12"
                required
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Contraseña</label>
              <div className="relative">
                <Input
                  data-testid="auth-password-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-900/50 border-slate-700 h-12 pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <Button
              data-testid="auth-submit-btn"
              type="submit"
              className="w-full btn-primary h-12 text-base"
              disabled={loading}
            >
              {loading ? "Cargando..." : isLogin ? "Ingresar" : "Crear Cuenta"}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <button
              data-testid="auth-toggle-btn"
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-cyan-400 hover:text-cyan-300 text-sm"
            >
              {isLogin ? "¿No tienes cuenta? Regístrate" : "¿Ya tienes cuenta? Inicia sesión"}
            </button>
            <div className={`mt-2 ${isLogin ? 'block' : 'hidden'}`}>
              <button
                type="button"
                onClick={() => {
                  setShowAuthModal(false);
                  navigate("/forgot-password");
                }}
                className="text-slate-400 hover:text-cyan-400 text-xs transition-colors"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Dashboard
const Dashboard = () => {
  const { user, logout, subscription } = useAuth();
  const navigate = useNavigate();
  const [legalAreas, setLegalAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);

  const LILA_BG = "https://customer-assets.emergentagent.com/job_justice-scale-law/artifacts/vxot5lqm_WhatsApp%20Image%202026-03-03%20at%2010.06.47%20PM%20%281%29.jpeg";

  // Capturar el evento de instalación PWA
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallApp = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('¡App instalada correctamente!');
      }
      setInstallPrompt(null);
    } else {
      toast.info('Para instalar: Abre el menú del navegador y selecciona "Añadir a pantalla de inicio"');
    }
  };

  // Custom colors for each legal area
  const areaColors = {
    familia: { bg: "bg-pink-500/20", icon: "text-pink-400", border: "border-pink-500/30" },
    civil: { bg: "bg-cyan-500/20", icon: "text-cyan-400", border: "border-cyan-500/30" },
    laboral: { bg: "bg-amber-500/20", icon: "text-amber-400", border: "border-amber-500/30" },
    penal: { bg: "bg-red-500/20", icon: "text-red-400", border: "border-red-500/30" },
    mercantil: { bg: "bg-emerald-500/20", icon: "text-emerald-400", border: "border-emerald-500/30" },
    constitucional: { bg: "bg-purple-500/20", icon: "text-purple-400", border: "border-purple-500/30" },
    maritimo: { bg: "bg-blue-500/20", icon: "text-blue-400", border: "border-blue-500/30" },
    migratorio: { bg: "bg-orange-500/20", icon: "text-orange-400", border: "border-orange-500/30" },
    ambiental: { bg: "bg-green-500/20", icon: "text-green-400", border: "border-green-500/30" },
    tributario: { bg: "bg-yellow-500/20", icon: "text-yellow-400", border: "border-yellow-500/30" },
    administrativo: { bg: "bg-indigo-500/20", icon: "text-indigo-400", border: "border-indigo-500/30" },
    agrario: { bg: "bg-lime-500/20", icon: "text-lime-400", border: "border-lime-500/30" }
  };

  useEffect(() => {
    fetchLegalAreas();
  }, []);

  const fetchLegalAreas = async () => {
    try {
      const response = await axios.get(`${API}/legal-areas`);
      setLegalAreas(response.data);
    } catch (error) {
      console.error("Error fetching legal areas:", error);
    }
  };

  const handleAreaSelect = (area) => {
    // Verificar si tiene acceso
    if (subscription && !subscription.has_access) {
      setShowBlockedModal(true);
      return;
    }
    setSelectedArea(area);
    setShowChat(true);
  };

  return (
    <div className="min-h-screen relative" data-testid="dashboard">
      {/* Background Image */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${LILA_BG})`,
          filter: 'brightness(0.15) blur(2px)'
        }}
      />
      <div className="fixed inset-0 bg-gradient-to-br from-slate-950/80 via-blue-950/50 to-slate-950/80" />
      
      {/* Subscription Banner */}
      <div className="relative z-20">
        <SubscriptionBanner />
      </div>
      
      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="glass sticky top-0 z-50 border-b border-slate-700/50 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ScaleAvatar size={40} animated={false} />
              <div>
                <h1 className="text-xl font-bold gradient-text">Lila</h1>
                <p className="text-xs text-slate-500 hidden sm:block">Asistente Legal Tech</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-6">
              {/* Admin button - solo visible para admins */}
              {user?.email?.toLowerCase() === "admin@linorttech.com" && (
                <button 
                  onClick={() => navigate("/admin")}
                  className="flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm font-medium"
                >
                  <BarChart3 size={16} />
                  Admin
                </button>
              )}
              <button 
                onClick={() => navigate("/premium")}
                className="flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-medium"
              >
                <Crown size={16} />
                Premium
              </button>
              <span className="text-slate-400 text-sm">Hola, {user?.full_name}</span>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-slate-400 hover:text-white"
              >
                <LogOut size={18} className="mr-2" />
                Salir
              </Button>
            </div>
            
            <button
              className="md:hidden text-slate-400"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-slate-800 p-4 animate-slide-up">
              <p className="text-slate-400 text-sm mb-3">Hola, {user?.full_name}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-slate-400 hover:text-white w-full justify-start"
              >
                <LogOut size={18} className="mr-2" />
                Cerrar Sesión
              </Button>
            </div>
          )}
        </header>

        {/* Main Content */}
        <main className="max-w-6xl mx-auto px-4 py-8">
          {!showChat ? (
            <>
              {/* Hero Section */}
              <div className="text-center mb-12">
                <div className="flex justify-center mb-6">
                  <ScaleAvatar size={100} />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-3">
                  <span className="text-white">¿En qué área legal</span>
                  <span className="gradient-text"> necesitas ayuda?</span>
                </h2>
                <p className="text-slate-400 max-w-xl mx-auto">
                  Selecciona una rama del derecho para consultar con Lila
                </p>
              </div>

              {/* Legal Areas Grid - Elegant Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-12" data-testid="legal-areas-grid">
                {legalAreas.map((area, index) => {
                  const IconComponent = legalIcons[area.id] || Gavel;
                  const colors = areaColors[area.id] || { bg: "bg-slate-500/20", icon: "text-slate-400", border: "border-slate-500/30" };
                  return (
                    <div
                      key={area.id}
                      data-testid={`legal-area-${area.id}`}
                      className={`group cursor-pointer rounded-2xl border ${colors.border} bg-slate-900/40 backdrop-blur-sm p-6 transition-all duration-300 hover:bg-slate-800/60 hover:border-opacity-60 hover:scale-[1.02] hover:shadow-xl`}
                      style={{ animationDelay: `${index * 100}ms` }}
                      onClick={() => handleAreaSelect(area)}
                    >
                      {/* Icon Container */}
                      <div className={`w-14 h-14 rounded-xl ${colors.bg} flex items-center justify-center mb-4 transition-transform group-hover:scale-110`}>
                        <IconComponent className={colors.icon} size={28} />
                      </div>
                      
                      {/* Title */}
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-300 transition-colors">
                        {area.name}
                      </h3>
                      
                      {/* Description */}
                      <p className="text-sm text-slate-400 leading-relaxed">
                        {area.description}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Herramientas Adicionales */}
              <div className="mb-24">
                <h3 className="text-lg font-medium text-slate-300 mb-4">Más Herramientas</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <button 
                    onClick={() => navigate("/transcripcion")}
                    className="glass border border-slate-700 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-pink-500/50 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-pink-500/20 flex items-center justify-center">
                      <Mic className="text-pink-400" size={24} />
                    </div>
                    <span className="text-sm text-slate-300">Transcribir Audio</span>
                  </button>
                  <button 
                    onClick={() => navigate("/mapas-mentales")}
                    className="glass border border-slate-700 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-violet-500/50 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
                      <Search className="text-violet-400" size={24} />
                    </div>
                    <span className="text-sm text-slate-300">Mapas Mentales</span>
                  </button>
                  <button 
                    onClick={() => navigate("/organo-judicial")}
                    className="glass border border-slate-700 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-amber-500/50 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <Scale className="text-amber-400" size={24} />
                    </div>
                    <span className="text-sm text-slate-300">Órgano Judicial</span>
                  </button>
                  <button 
                    onClick={() => navigate("/audiencias")}
                    className="glass border border-slate-700 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-red-500/50 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                      <Play className="text-red-400" size={24} />
                    </div>
                    <span className="text-sm text-slate-300">Audiencias</span>
                  </button>
                  <a 
                    href="https://www.organojudicial.gob.pa/appsadd/abogado/s4abi.php"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="glass border border-slate-700 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-green-500/50 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                      <UserCheck className="text-green-400" size={24} />
                    </div>
                    <span className="text-sm text-slate-300">Idoneidad</span>
                  </a>
                  <a 
                    href="https://www.organojudicial.gob.pa/category/remates-judiciales-panama"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="glass border border-slate-700 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-orange-500/50 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
                      <Gavel className="text-orange-400" size={24} />
                    </div>
                    <span className="text-sm text-slate-300">Remates</span>
                  </a>
                  <button 
                    onClick={() => navigate("/recursos")}
                    className="glass border border-slate-700 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-blue-500/50 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Globe className="text-blue-400" size={24} />
                    </div>
                    <span className="text-sm text-slate-300">Portales Gov</span>
                  </button>
                </div>
              </div>

              {/* Quick Actions - Bottom Bar */}
              <div className="fixed bottom-0 left-0 right-0 glass border-t border-slate-700/50 backdrop-blur-xl z-40">
                <div className="max-w-6xl mx-auto px-4 py-3">
                  <div className="flex items-center justify-center gap-4">
                    <button 
                      onClick={() => { setSelectedArea({ id: "general", name: "General" }); setShowChat(true); }}
                      className="flex flex-col items-center gap-1 text-slate-400 hover:text-cyan-400 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                        <MessageSquare size={20} />
                      </div>
                      <span className="text-xs">Chat</span>
                    </button>
                    <button 
                      onClick={() => navigate("/call")}
                      className="flex flex-col items-center gap-1 text-green-400 hover:text-green-300 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                        <Phone size={20} />
                      </div>
                      <span className="text-xs">Llamar</span>
                    </button>
                    <button 
                      onClick={() => navigate("/estudiante")}
                      className="flex flex-col items-center gap-1 text-pink-400 hover:text-pink-300 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-xl bg-pink-500/20 flex items-center justify-center">
                        <BookOpen size={20} />
                      </div>
                      <span className="text-xs">Estudiar</span>
                    </button>
                    <button 
                      onClick={() => navigate("/herramientas")}
                      className="flex flex-col items-center gap-1 text-amber-400 hover:text-amber-300 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                        <Calculator size={20} />
                      </div>
                      <span className="text-xs">Calcular</span>
                    </button>
                    <button 
                      onClick={() => navigate("/jurisprudencia")}
                      className="flex flex-col items-center gap-1 text-emerald-400 hover:text-emerald-300 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                        <Gavel size={20} />
                      </div>
                      <span className="text-xs">Sentencias</span>
                    </button>
                    <button 
                      onClick={() => navigate("/mis-casos")}
                      className="flex flex-col items-center gap-1 text-cyan-400 hover:text-cyan-300 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                        <Folder size={20} />
                      </div>
                      <span className="text-xs">Mis Casos</span>
                    </button>
                    <button 
                      onClick={() => navigate("/premium")}
                      className="flex flex-col items-center gap-1 text-amber-400 hover:text-amber-300 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                        <Crown size={20} />
                      </div>
                      <span className="text-xs">Premium</span>
                    </button>
                    {/* Admin button - solo visible para admins */}
                    {user?.email?.toLowerCase() === "admin@linorttech.com" && (
                      <button 
                        onClick={() => navigate("/admin")}
                        className="flex flex-col items-center gap-1 text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                          <BarChart3 size={20} />
                        </div>
                        <span className="text-xs">Admin</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <ChatInterface 
              legalArea={selectedArea} 
              onBack={() => { setShowChat(false); setSelectedArea(null); }} 
            />
          )}
        </main>
      </div>
      
      {/* Access Blocked Modal */}
      <AccessBlockedModal show={showBlockedModal} onClose={() => setShowBlockedModal(false)} />
    </div>
  );
};

// Chat Interface
const ChatInterface = ({ legalArea, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [showDocGen, setShowDocGen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const scrollRef = React.useRef(null);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    setMessages([{
      role: "assistant",
      content: `¡Hola! Soy Lila, especializada en ${legalArea?.name || "leyes panameñas"}.\n\n¿En qué puedo ayudarte?\n\n📎 Sube fotos/documentos\n🎤 Mantén presionado para hablar`
    }]);
  }, [legalArea]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Speech Recognition for continuous voice input
  const recognitionRef = React.useRef(null);
  const [transcript, setTranscript] = useState("");

  // Toggle voice recording
  const toggleVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      toast.error("Tu navegador no soporta voz");
      return;
    }

    if (isRecording) {
      // Stop recording
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      setIsRecording(false);
      setTranscript("");
      toast.success("Grabación terminada");
    } else {
      // Start recording
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recog = new SpeechRecognition();
      recog.lang = 'es-ES';
      recog.continuous = true;
      recog.interimResults = true;
      recog.maxAlternatives = 1;
      
      let fullText = input;
      
      recog.onresult = (event) => {
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            fullText += result[0].transcript + ' ';
            setInput(fullText);
          } else {
            interimTranscript += result[0].transcript;
          }
        }
        setTranscript(interimTranscript);
      };
      
      recog.onerror = (event) => {
        if (event.error === 'no-speech') {
          // Reiniciar si no hay voz
          if (isRecording && recognitionRef.current) {
            try { recognitionRef.current.start(); } catch(e) {}
          }
        } else if (event.error !== 'aborted') {
          toast.error("Error: " + event.error);
          setIsRecording(false);
        }
      };
      
      recog.onend = () => {
        // Reiniciar automáticamente si aún está grabando
        if (isRecording && recognitionRef.current) {
          try { 
            recognitionRef.current.start(); 
          } catch(e) {
            setIsRecording(false);
          }
        }
      };
      
      recognitionRef.current = recog;
      
      try {
        recog.start();
        setIsRecording(true);
        toast.info("🎤 Grabando... Habla y toca de nuevo para detener");
      } catch (e) {
        toast.error("Error al iniciar");
      }
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const newFiles = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('legal_area', legalArea?.id || 'general');

        const response = await axios.post(`${API}/files/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        newFiles.push({
          id: response.data.id,
          name: file.name,
          type: file.type,
          size: file.size,
          preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
        });

        toast.success(`${file.name} subido`);
      } catch (error) {
        toast.error(`Error al subir ${file.name}`);
      }
    }

    setUploadedFiles(prev => [...prev, ...newFiles]);
    setUploading(false);
    
    if (newFiles.length > 0) {
      setMessages(prev => [...prev, {
        role: "user",
        content: `📎 Archivos: ${newFiles.map(f => f.name).join(', ')}`,
        files: newFiles
      }]);
    }
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    const filesInfo = uploadedFiles.length > 0 
      ? `\n\n[Archivos adjuntos: ${uploadedFiles.map(f => f.name).join(', ')}]`
      : '';
    
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat/send`, {
        message: userMessage + filesInfo,
        legal_area: legalArea?.id || "general",
        session_id: sessionId
      });
      
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: response.data.response
      }]);
      
      setUploadedFiles([]);
    } catch (error) {
      toast.error("Error al enviar");
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Error. Intenta de nuevo." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const playAudio = async (text) => {
    if (playingAudio) {
      playingAudio.pause();
      setPlayingAudio(null);
      return;
    }

    try {
      toast.info("Generando audio...");
      const response = await axios.post(`${API}/tts/generate`, {
        text: text.substring(0, 500),
        voice_id: "kcQkGnn0HAT2JRDQ4Ljp"
      }, {
        responseType: 'blob'
      });
      
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      const audio = new Audio(audioUrl);
      audio.onended = () => {
        setPlayingAudio(null);
        URL.revokeObjectURL(audioUrl);
      };
      audio.onerror = () => {
        toast.error("Error de audio");
        setPlayingAudio(null);
        URL.revokeObjectURL(audioUrl);
      };
      await audio.play();
      setPlayingAudio(audio);
    } catch (error) {
      toast.error("Error al generar audio");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="chat-interface">
      {/* Chat Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button
            data-testid="chat-back-btn"
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-slate-400 hover:text-white"
          >
            ← Volver
          </Button>
          <div className="flex items-center gap-2">
            <ScaleAvatar size={32} animated={false} />
            <div>
              <h2 className="font-semibold">{legalArea?.name || "Chat General"}</h2>
              <p className="text-xs text-slate-500">Lila lista</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="generate-doc-btn"
            variant="outline"
            size="sm"
            onClick={() => setShowDocGen(true)}
            className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
          >
            <Plus size={16} className="mr-1" />
            <span className="hidden sm:inline">Documento</span>
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto pr-4" ref={scrollRef}>
        <div className="space-y-4 pb-4">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-slide-up`}
            >
              <div className={`max-w-[85%] ${msg.role === "user" ? "chat-bubble-user" : "chat-bubble-assistant"} p-4`}>
                <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                {msg.files && msg.files.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {msg.files.map(file => (
                      <div key={file.id} className="relative">
                        {file.preview ? (
                          <img src={file.preview} alt={file.name} className="w-16 h-16 object-cover rounded-lg" />
                        ) : (
                          <div className="w-16 h-16 bg-slate-700 rounded-lg flex items-center justify-center">
                            <FileText size={20} className="text-slate-400" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {msg.role === "assistant" && (
                  <button
                    onClick={() => playAudio(msg.content)}
                    className="mt-2 text-cyan-400 hover:text-cyan-300 text-xs flex items-center gap-1"
                  >
                    {playingAudio ? <Pause size={14} /> : <Play size={14} />}
                    {playingAudio ? "Pausar" : "Escuchar"}
                  </button>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="chat-bubble-assistant p-4">
                <div className="loading-dots flex gap-1">
                  <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
                  <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
                  <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recording indicator */}
      {isRecording && (
        <div className="mb-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-red-400 text-sm">Grabando... {transcript && `"${transcript}"`}</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">Toca el micrófono para detener</p>
        </div>
      )}

      {/* Uploaded Files Preview */}
      {uploadedFiles.length > 0 && (
        <div className="mb-2 p-3 glass rounded-xl">
          <div className="flex flex-wrap gap-2">
            {uploadedFiles.map(file => (
              <div key={file.id} className="relative group">
                {file.preview ? (
                  <img src={file.preview} alt={file.name} className="w-12 h-12 object-cover rounded-lg" />
                ) : (
                  <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                    <FileText size={16} className="text-slate-400" />
                  </div>
                )}
                <button
                  onClick={() => removeFile(file.id)}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center"
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="mt-2 glass rounded-2xl p-3">
        <div className="flex items-end gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            className="hidden"
          />
          <Button
            data-testid="upload-btn"
            variant="ghost"
            size="icon"
            className="text-slate-400 hover:text-cyan-400 rounded-full flex-shrink-0"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Upload size={20} />
            )}
          </Button>
          
          <Textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Escribe o usa el micrófono..."
            className="flex-1 min-h-[44px] max-h-[100px] bg-transparent border-0 resize-none focus:ring-0 text-sm"
            rows={1}
          />
          
          <Button
            data-testid="voice-btn"
            variant="ghost"
            size="icon"
            className={`rounded-full flex-shrink-0 ${isRecording ? "bg-red-500/20 text-red-400 animate-pulse" : "text-slate-400 hover:text-cyan-400"}`}
            onClick={toggleVoiceInput}
          >
            {isRecording ? <MicOff size={20} /> : <Mic size={20} />}
          </Button>
          
          <Button
            data-testid="send-btn"
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="btn-primary rounded-full px-4 flex-shrink-0"
          >
            <Send size={18} />
          </Button>
        </div>
      </div>

      <DocumentGenerator 
        open={showDocGen} 
        onOpenChange={setShowDocGen}
        legalArea={legalArea?.id || "general"}
      />
    </div>
  );
};

// Document Generator
const DocumentGenerator = ({ open, onOpenChange, legalArea }) => {
  const [step, setStep] = useState(1); // 1: datos abogado, 2: datos documento
  const [docType, setDocType] = useState("poder");
  const [details, setDetails] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [clientNationality, setClientNationality] = useState("panameña");
  // Datos del querellado (para casos penales)
  const [querelladoNombre, setQuerelladoNombre] = useState("");
  const [querelladoCedula, setQuerelladoCedula] = useState("");
  const [querelladoNacionalidad, setQuerelladoNacionalidad] = useState("panameña");
  // Datos del abogado
  const [lawyerName, setLawyerName] = useState("");
  const [lawyerId, setLawyerId] = useState("");
  const [lawyerIdoneidad, setLawyerIdoneidad] = useState("");
  const [lawyerAddress, setLawyerAddress] = useState("");
  const [lawyerPhone, setLawyerPhone] = useState("");
  const [lawyerEmail, setLawyerEmail] = useState("");
  const [lawyerProfileLoaded, setLawyerProfileLoaded] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [generatedDoc, setGeneratedDoc] = useState(null);

  // Load lawyer profile from database on open
  useEffect(() => {
    if (open && !lawyerProfileLoaded) {
      loadLawyerProfile();
    }
  }, [open]);

  const loadLawyerProfile = async () => {
    try {
      const response = await axios.get(`${API}/lawyer/profile`);
      const profile = response.data;
      setLawyerName(profile.nombre || '');
      setLawyerId(profile.cedula || '');
      setLawyerIdoneidad(profile.idoneidad || '');
      setLawyerAddress(profile.direccion || '');
      setLawyerPhone(profile.telefono || '');
      setLawyerEmail(profile.email || '');
      setLawyerProfileLoaded(true);
      // Si ya tiene perfil completo, saltar al paso 2
      if (profile.nombre && profile.cedula) {
        setStep(2);
      }
    } catch (error) {
      // Perfil no existe - dejar campos vacíos para que el usuario los llene
      setLawyerName('');
      setLawyerId('');
      setLawyerIdoneidad('');
      setLawyerAddress('');
      setLawyerPhone('');
      setLawyerEmail('');
      setLawyerProfileLoaded(true);
      setStep(1); // Siempre mostrar paso 1 si no hay perfil
    }
  };

  const saveLawyerProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.post(`${API}/lawyer/profile`, {
        nombre: lawyerName,
        cedula: lawyerId,
        idoneidad: lawyerIdoneidad,
        direccion: lawyerAddress,
        telefono: lawyerPhone,
        email: lawyerEmail
      });
      toast.success("Perfil de abogado guardado");
      return true;
    } catch (error) {
      toast.error("Error al guardar el perfil");
      return false;
    } finally {
      setSavingProfile(false);
    }
  };

  const handleClose = () => {
    setGeneratedDoc(null);
    setDetails("");
    setClientName("");
    setClientId("");
    setClientAddress("");
    setClientNationality("panameña");
    setQuerelladoNombre("");
    setQuerelladoCedula("");
    setQuerelladoNacionalidad("panameña");
    setStep(lawyerName && lawyerId ? 2 : 1);
    onOpenChange(false);
  };

  const generateDocument = async () => {
    if (!details.trim()) {
      toast.error("Por favor, describe los detalles del documento");
      return;
    }
    if (!lawyerName.trim() || !lawyerId.trim()) {
      toast.error("Por favor, ingresa los datos del abogado");
      setStep(1);
      return;
    }
    if (!clientName.trim() || !clientId.trim()) {
      toast.error("Por favor, ingresa el nombre y cédula del cliente");
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/documents/generate`, {
        doc_type: docType,
        legal_area: legalArea,
        details: details,
        client_name: clientName,
        client_id: clientId,
        client_address: clientAddress || undefined,
        client_nationality: clientNationality || "panameña",
        querellado_nombre: querelladoNombre || undefined,
        querellado_cedula: querelladoCedula || undefined,
        querellado_nacionalidad: querelladoNacionalidad || "panameña"
      });
      setGeneratedDoc(response.data);
      toast.success("¡Documento generado con éxito!");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al generar documento";
      toast.error(errorMsg);
      if (errorMsg.includes("perfil de abogado")) {
        setStep(1);
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadWord = async () => {
    if (!generatedDoc) return;
    try {
      const response = await axios.get(`${API}/documents/${generatedDoc.id}/word`);
      const { word_b64, filename } = response.data;
      
      const byteCharacters = atob(word_b64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Descargando Word...");
    } catch (error) {
      toast.error("Error al descargar");
    }
  };

  const downloadText = () => {
    if (!generatedDoc) return;
    const blob = new Blob([generatedDoc.content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${generatedDoc.doc_type}_${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Determinar si mostrar campos de querellado (casos penales)
  const showQuerelladoFields = legalArea === "penal" || docType === "querella";

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="document-generator-modal">
      <div className="fixed inset-0 bg-black/80" onClick={handleClose} />
      <div className="relative z-50 w-full max-w-2xl max-h-[90vh] overflow-y-auto glass border border-slate-700 rounded-2xl p-6">
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white"
          data-testid="close-doc-modal"
        >
          <X size={20} />
        </button>
        
        <h2 className="text-xl font-bold gradient-text mb-1">Generar Documento Legal</h2>
        <p className="text-sm text-slate-400 mb-4">
          {step === 1 ? "Paso 1: Datos del Abogado (se guardan en tu perfil)" : "Paso 2: Datos del Documento"}
        </p>
        
        {/* Progress indicator */}
        <div className="flex gap-2 mb-6">
          <div className={`flex-1 h-1 rounded ${step >= 1 ? 'bg-cyan-500' : 'bg-slate-700'}`}></div>
          <div className={`flex-1 h-1 rounded ${step >= 2 ? 'bg-cyan-500' : 'bg-slate-700'}`}></div>
        </div>
        
        {!generatedDoc ? (
          <>
            {step === 1 ? (
              <div className="space-y-4">
                <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                  <p className="text-xs text-cyan-400">
                    <strong>💡 Importante:</strong> Estos datos se guardarán en tu perfil profesional y aparecerán como membrete en todos tus documentos.
                  </p>
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Nombre Completo del Abogado *</label>
                  <input
                    type="text"
                    value={lawyerName}
                    onChange={(e) => setLawyerName(e.target.value)}
                    placeholder="Ej: Licdo. Juan Pérez"
                    className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                    data-testid="lawyer-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 mb-2 block">Cédula *</label>
                    <input
                      type="text"
                      value={lawyerId}
                      onChange={(e) => setLawyerId(e.target.value)}
                      placeholder="Ej: 8-123-456"
                      className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                      data-testid="lawyer-cedula-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 mb-2 block">No. Idoneidad *</label>
                    <input
                      type="text"
                      value={lawyerIdoneidad}
                      onChange={(e) => setLawyerIdoneidad(e.target.value)}
                      placeholder="Ej: 12345"
                      className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                      data-testid="lawyer-idoneidad-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Dirección de la Oficina</label>
                  <input
                    type="text"
                    value={lawyerAddress}
                    onChange={(e) => setLawyerAddress(e.target.value)}
                    placeholder="Ej: Calle 50, Edificio XYZ, Oficina 101"
                    className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                    data-testid="lawyer-address-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 mb-2 block">Teléfono</label>
                    <input
                      type="text"
                      value={lawyerPhone}
                      onChange={(e) => setLawyerPhone(e.target.value)}
                      placeholder="Ej: +507 6000-0000"
                      className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                      data-testid="lawyer-phone-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 mb-2 block">Correo Electrónico</label>
                    <input
                      type="email"
                      value={lawyerEmail}
                      onChange={(e) => setLawyerEmail(e.target.value)}
                      placeholder="abogado@bufete.com"
                      className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                      data-testid="lawyer-email-input"
                    />
                  </div>
                </div>
                
                <button
                  onClick={async () => {
                    if (!lawyerName.trim() || !lawyerId.trim()) {
                      toast.error("Nombre y cédula del abogado son obligatorios");
                      return;
                    }
                    const saved = await saveLawyerProfile();
                    if (saved) {
                      setStep(2);
                    }
                  }}
                  disabled={savingProfile}
                  className="w-full py-3 btn-primary rounded-xl font-medium"
                  data-testid="continue-to-step2-btn"
                >
                  {savingProfile ? "Guardando perfil..." : "Guardar y Continuar →"}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <button 
                  onClick={() => setStep(1)}
                  className="text-sm text-cyan-400 hover:text-cyan-300"
                  data-testid="back-to-step1-btn"
                >
                  ← Modificar datos del abogado
                </button>
                
                {/* Mostrar resumen del abogado */}
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <p className="text-xs text-slate-400 mb-1">Membrete del documento:</p>
                  <p className="text-sm text-white font-medium">{lawyerName.toUpperCase()}</p>
                  <p className="text-xs text-slate-400">Cédula: {lawyerId} | Idoneidad: {lawyerIdoneidad}</p>
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Tipo de Documento</label>
                  <select 
                    value={docType} 
                    onChange={(e) => setDocType(e.target.value)}
                    className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white"
                    data-testid="doc-type-select"
                  >
                    <option value="poder">Poder Especial Simple</option>
                    <option value="minuta">Minuta Poder General (Registro Público)</option>
                    <option value="revocatoria">Revocatoria de Mandato</option>
                    <option value="querella">Querella Criminal</option>
                    <option value="pacto_social">Pacto Social — Sociedad Anónima (S.A.)</option>
                    <option value="solicitud">Solicitud</option>
                  </select>
                  {docType === "minuta" && (
                    <p className="text-xs text-amber-400 mt-1">Este documento va a Notaría y Registro Público</p>
                  )}
                  {docType === "revocatoria" && (
                    <p className="text-xs text-cyan-400 mt-1">Para revocar/cancelar un poder ya otorgado</p>
                  )}
                  {docType === "querella" && legalArea !== "penal" && (
                    <p className="text-xs text-red-400 mt-1">Nota: Las querellas son para casos penales</p>
                  )}
                  {docType === "pacto_social" && (
                    <p className="text-xs text-emerald-400 mt-1">Constituye una S.A. (Ley 32 de 1927). En "Propósito" escribe: <strong>NOMBRE DE LA SOCIEDAD | OBJETO SOCIAL</strong> (separados por "|"). Ej: <em>LINORT TECH | Desarrollo de software legal y consultoría tecnológica</em></p>
                  )}
                </div>
                
                {/* Datos del Cliente/Poderdante */}
                <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-600">
                  <h3 className="text-sm font-medium text-slate-300 mb-3">Datos del Cliente/Poderdante</h3>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Nombre Completo *</label>
                      <input
                        type="text"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        placeholder="Juan Pérez González"
                        className="w-full p-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                        data-testid="client-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Cédula *</label>
                      <input
                        type="text"
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                        placeholder="8-199-777"
                        className="w-full p-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                        data-testid="client-cedula-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Dirección</label>
                      <input
                        type="text"
                        value={clientAddress}
                        onChange={(e) => setClientAddress(e.target.value)}
                        placeholder="San Miguelito, Calle Principal"
                        className="w-full p-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                        data-testid="client-address-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Nacionalidad</label>
                      <input
                        type="text"
                        value={clientNationality}
                        onChange={(e) => setClientNationality(e.target.value)}
                        placeholder="panameña"
                        className="w-full p-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                        data-testid="client-nationality-input"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Datos del Querellado (solo para casos penales) */}
                {showQuerelladoFields && (
                  <div className="p-3 bg-red-900/20 rounded-lg border border-red-500/30">
                    <h3 className="text-sm font-medium text-red-300 mb-3">Datos del Querellado/Demandado</h3>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Nombre</label>
                        <input
                          type="text"
                          value={querelladoNombre}
                          onChange={(e) => setQuerelladoNombre(e.target.value)}
                          placeholder="Nombre del querellado"
                          className="w-full p-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                          data-testid="querellado-name-input"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Cédula/Identificación</label>
                        <input
                          type="text"
                          value={querelladoCedula}
                          onChange={(e) => setQuerelladoCedula(e.target.value)}
                          placeholder="Cédula o pasaporte"
                          className="w-full p-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                          data-testid="querellado-cedula-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Nacionalidad</label>
                      <input
                        type="text"
                        value={querelladoNacionalidad}
                        onChange={(e) => setQuerelladoNacionalidad(e.target.value)}
                        placeholder="panameña"
                        className="w-full p-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                        data-testid="querellado-nationality-input"
                      />
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">
                    {legalArea === "penal" ? "Descripción del Caso / Hechos *" : "Propósito del Poder *"}
                    {legalArea === "penal" && (
                      <span className="text-xs text-cyan-400 ml-2">(Incluye el tipo de delito: "estafa", "apropiación indebida")</span>
                    )}
                  </label>
                  <textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    placeholder={
                      legalArea === "penal" 
                        ? "Ej: Mi cliente fue víctima de estafa por parte de [nombre]..."
                        : legalArea === "laboral"
                        ? "Ej: Solicitar prórroga del permiso de trabajo / Representarme en reclamación laboral contra [empresa]..."
                        : legalArea === "migratorio"
                        ? "Ej: Solicitar permiso de residente permanente por matrimonio con nacional panameña..."
                        : legalArea === "familia"
                        ? "Ej: Representarme en proceso de divorcio / custodia / pensión alimenticia..."
                        : "Ej: Describa el propósito del poder y las facultades que desea otorgar..."
                    }
                    className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 min-h-[140px] resize-none text-sm"
                    data-testid="case-details-input"
                  />
                </div>
                
                <button
                  onClick={generateDocument}
                  disabled={loading}
                  className="w-full py-3 btn-primary rounded-xl font-medium flex items-center justify-center gap-2"
                  data-testid="generate-doc-submit-btn"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Generando documento profesional...
                    </>
                  ) : (
                    <>
                      <FileText size={18} />
                      Generar Documento
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-sm text-green-400">✓ Documento generado con formato profesional panameño</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-4 max-h-[350px] overflow-y-auto font-mono">
              <pre className="whitespace-pre-wrap text-sm text-slate-300" data-testid="generated-doc-preview">
                {generatedDoc.content}
              </pre>
            </div>
            <div className="flex gap-2">
              <button
                onClick={downloadWord}
                className="flex-1 py-3 btn-primary rounded-xl font-medium flex items-center justify-center gap-2"
                data-testid="download-word-btn"
              >
                <Download size={16} />
                Descargar Word (.docx)
              </button>
              <button
                onClick={downloadText}
                className="flex-1 py-3 border border-slate-700 rounded-xl font-medium text-slate-300 hover:bg-slate-800 flex items-center justify-center gap-2"
                data-testid="download-txt-btn"
              >
                <FileText size={16} />
                Texto (.txt)
              </button>
            </div>
            
            {/* Guardar como plantilla */}
            <SaveAsTemplateButton documentId={generatedDoc.id} docType={generatedDoc.doc_type} />
            
            <button
              onClick={() => { setGeneratedDoc(null); setDetails(""); }}
              className="w-full py-2 text-slate-400 hover:text-white"
              data-testid="generate-another-btn"
            >
              Generar Otro Documento
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Save As Template Button Component
const SaveAsTemplateButton = ({ documentId, docType }) => {
  const [showModal, setShowModal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (showModal) {
      loadFolders();
      setTemplateName(`Plantilla ${docType} - ${new Date().toLocaleDateString()}`);
    }
  }, [showModal, docType]);

  const loadFolders = async () => {
    try {
      const response = await axios.get(`${API}/templates/folders`);
      setFolders(response.data);
    } catch (error) {
      console.error("Error loading folders:", error);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const response = await axios.post(`${API}/templates/folders`, {
        name: newFolderName,
        color: "#06b6d4"
      });
      setFolders([...folders, response.data]);
      setSelectedFolder(response.data.id);
      setNewFolderName("");
      setShowNewFolder(false);
      toast.success("Carpeta creada");
    } catch (error) {
      toast.error("Error al crear carpeta");
    }
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Ingresa un nombre para la plantilla");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/templates`, {
        document_id: documentId,
        folder_id: selectedFolder || null,
        name: templateName,
        description: templateDesc || null
      });
      toast.success("¡Plantilla guardada!");
      setShowModal(false);
    } catch (error) {
      toast.error("Error al guardar plantilla");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="w-full py-3 border border-amber-500/30 rounded-xl font-medium text-amber-400 hover:bg-amber-500/10 flex items-center justify-center gap-2"
        data-testid="save-template-btn"
      >
        <Star size={16} />
        Guardar como Plantilla Favorita
      </button>

      {showModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowModal(false)} />
          <div className="relative z-[60] w-full max-w-md glass border border-slate-700 rounded-2xl p-6">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X size={20} />
            </button>
            
            <h3 className="text-lg font-bold text-amber-400 mb-4 flex items-center gap-2">
              <Star size={20} />
              Guardar como Plantilla
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Nombre de la plantilla *</label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Mi plantilla de poder penal"
                  className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                  data-testid="template-name-input"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Descripción (opcional)</label>
                <input
                  type="text"
                  value={templateDesc}
                  onChange={(e) => setTemplateDesc(e.target.value)}
                  placeholder="Poder para casos de estafa"
                  className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Carpeta</label>
                {!showNewFolder ? (
                  <div className="flex gap-2">
                    <select
                      value={selectedFolder}
                      onChange={(e) => setSelectedFolder(e.target.value)}
                      className="flex-1 p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white"
                      data-testid="template-folder-select"
                    >
                      <option value="">Sin carpeta</option>
                      {folders.map(folder => (
                        <option key={folder.id} value={folder.id}>{folder.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowNewFolder(true)}
                      className="p-3 border border-slate-700 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      placeholder="Nombre de nueva carpeta"
                      className="flex-1 p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                      autoFocus
                    />
                    <button
                      onClick={createFolder}
                      className="p-3 btn-primary rounded-lg"
                    >
                      Crear
                    </button>
                    <button
                      onClick={() => { setShowNewFolder(false); setNewFolderName(""); }}
                      className="p-3 border border-slate-700 rounded-lg text-slate-400"
                    >
                      <X size={20} />
                    </button>
                  </div>
                )}
              </div>
              
              <button
                onClick={saveTemplate}
                disabled={saving}
                className="w-full py-3 btn-primary rounded-xl font-medium flex items-center justify-center gap-2"
                data-testid="confirm-save-template-btn"
              >
                {saving ? "Guardando..." : (
                  <>
                    <Star size={16} />
                    Guardar Plantilla
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Templates Page
const TemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUseModal, setShowUseModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Form para usar plantilla
  const [clientName, setClientName] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [querelladoNombre, setQuerelladoNombre] = useState("");
  const [querelladoCedula, setQuerelladoCedula] = useState("");
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generatedDoc, setGeneratedDoc] = useState(null);
  
  // Estados para subir plantilla propia
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploadDocType, setUploadDocType] = useState("poder");
  const [uploadLegalArea, setUploadLegalArea] = useState("general");
  const [uploadFolderId, setUploadFolderId] = useState("");
  const [uploading, setUploading] = useState(false);
  const uploadInputRef = React.useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [selectedFolder]);

  const loadData = async () => {
    try {
      const [foldersRes, templatesRes] = await Promise.all([
        axios.get(`${API}/templates/folders`),
        axios.get(`${API}/templates`)
      ]);
      setFolders(foldersRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const url = selectedFolder === null 
        ? `${API}/templates` 
        : selectedFolder === "none"
        ? `${API}/templates?folder_id=none`
        : `${API}/templates?folder_id=${selectedFolder}`;
      const response = await axios.get(url);
      setTemplates(response.data);
    } catch (error) {
      toast.error("Error al cargar plantillas");
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!confirm("¿Eliminar esta plantilla?")) return;
    try {
      await axios.delete(`${API}/templates/${templateId}`);
      setTemplates(templates.filter(t => t.id !== templateId));
      toast.success("Plantilla eliminada");
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const deleteFolder = async (folderId) => {
    if (!confirm("¿Eliminar esta carpeta? Las plantillas se moverán a 'Sin carpeta'")) return;
    try {
      await axios.delete(`${API}/templates/folders/${folderId}`);
      setFolders(folders.filter(f => f.id !== folderId));
      if (selectedFolder === folderId) setSelectedFolder(null);
      loadTemplates();
      toast.success("Carpeta eliminada");
    } catch (error) {
      toast.error("Error al eliminar carpeta");
    }
  };

  const openUseModal = (template) => {
    setSelectedTemplate(template);
    setShowUseModal(true);
    setClientName("");
    setClientId("");
    setClientAddress("");
    setQuerelladoNombre("");
    setQuerelladoCedula("");
    setAdditionalDetails("");
    setGeneratedDoc(null);
  };

  // Función para subir plantilla propia
  const handleUploadTemplate = async () => {
    if (!uploadFile) {
      toast.error("Selecciona un archivo");
      return;
    }
    if (!uploadName.trim()) {
      toast.error("Ingresa un nombre para la plantilla");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName);
      formData.append('description', uploadDescription || '');
      formData.append('doc_type', uploadDocType);
      formData.append('legal_area', uploadLegalArea);
      formData.append('folder_id', uploadFolderId || '');

      await axios.post(`${API}/templates/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success("¡Plantilla subida exitosamente!");
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadName("");
      setUploadDescription("");
      setUploadDocType("poder");
      setUploadLegalArea("general");
      setUploadFolderId("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir plantilla");
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
      // Auto-completar nombre si está vacío
      if (!uploadName) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
        setUploadName(nameWithoutExt);
      }
    }
  };

  const useTemplate = async () => {
    if (!clientName.trim() || !clientId.trim()) {
      toast.error("Nombre y cédula del cliente son obligatorios");
      return;
    }
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/templates/${selectedTemplate.id}/use`, {
        template_id: selectedTemplate.id,
        client_name: clientName,
        client_id: clientId,
        client_address: clientAddress || undefined,
        querellado_nombre: querelladoNombre || undefined,
        querellado_cedula: querelladoCedula || undefined,
        additional_details: additionalDetails || undefined
      });
      setGeneratedDoc(response.data);
      toast.success("¡Documento generado desde plantilla!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar documento");
    } finally {
      setGenerating(false);
    }
  };

  const downloadWord = async () => {
    if (!generatedDoc) return;
    try {
      const response = await axios.get(`${API}/documents/${generatedDoc.id}/word`);
      const { word_b64, filename } = response.data;
      const byteCharacters = atob(word_b64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Descargando...");
    } catch (error) {
      toast.error("Error al descargar");
    }
  };

  const isPenalTemplate = selectedTemplate?.legal_area === "penal" || selectedTemplate?.doc_type === "querella";

  return (
    <div className="min-h-screen bg-pattern" data-testid="templates-page">
      <header className="glass sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
              ← Volver
            </Button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Star className="text-amber-400" size={24} />
              Mis Plantillas
            </h1>
          </div>
          <Button
            onClick={() => setShowUploadModal(true)}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
            data-testid="upload-template-btn"
          >
            <Upload size={18} className="mr-2" />
            Subir Plantilla
          </Button>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Carpetas */}
          <div className="lg:col-span-1">
            <Card className="glass border-slate-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-slate-400">Carpetas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <button
                  onClick={() => setSelectedFolder(null)}
                  className={`w-full p-3 rounded-lg text-left flex items-center gap-2 transition-colors ${
                    selectedFolder === null ? 'bg-cyan-500/20 text-cyan-400' : 'hover:bg-slate-800 text-slate-300'
                  }`}
                >
                  <Folder size={18} />
                  Todas
                </button>
                <button
                  onClick={() => setSelectedFolder("none")}
                  className={`w-full p-3 rounded-lg text-left flex items-center gap-2 transition-colors ${
                    selectedFolder === "none" ? 'bg-cyan-500/20 text-cyan-400' : 'hover:bg-slate-800 text-slate-300'
                  }`}
                >
                  <FolderOpen size={18} />
                  Sin carpeta
                </button>
                {folders.map(folder => (
                  <div key={folder.id} className="flex items-center gap-1">
                    <button
                      onClick={() => setSelectedFolder(folder.id)}
                      className={`flex-1 p-3 rounded-lg text-left flex items-center gap-2 transition-colors ${
                        selectedFolder === folder.id ? 'bg-cyan-500/20 text-cyan-400' : 'hover:bg-slate-800 text-slate-300'
                      }`}
                    >
                      <Folder size={18} style={{ color: folder.color }} />
                      <span className="truncate">{folder.name}</span>
                      <span className="text-xs text-slate-500 ml-auto">{folder.template_count}</span>
                    </button>
                    <button
                      onClick={() => deleteFolder(folder.id)}
                      className="p-2 text-slate-500 hover:text-red-400"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
          
          {/* Main - Plantillas */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-12">
                <div className="loading-dots flex justify-center gap-1">
                  <span className="w-3 h-3 bg-cyan-400 rounded-full animate-bounce"></span>
                  <span className="w-3 h-3 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></span>
                  <span className="w-3 h-3 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></span>
                </div>
              </div>
            ) : templates.length === 0 ? (
              <Card className="glass border-slate-800">
                <CardContent className="py-12 text-center">
                  <Star className="mx-auto mb-4 text-slate-600" size={48} />
                  <p className="text-slate-400 mb-2">No hay plantillas guardadas</p>
                  <p className="text-sm text-slate-500">Genera un documento y guárdalo como plantilla favorita</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {templates.map(template => (
                  <Card key={template.id} className="glass border-slate-800 hover:border-slate-700 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Star className="text-amber-400" size={16} />
                            <h3 className="font-medium text-white">{template.name}</h3>
                          </div>
                          {template.description && (
                            <p className="text-sm text-slate-400 mb-2">{template.description}</p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <span className="px-2 py-1 bg-slate-800 rounded">{template.doc_type}</span>
                            <span className="px-2 py-1 bg-slate-800 rounded">{template.legal_area}</span>
                            {template.folder_name && (
                              <span className="flex items-center gap-1">
                                <Folder size={12} />
                                {template.folder_name}
                              </span>
                            )}
                            <span>{new Date(template.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => openUseModal(template)}
                            className="btn-primary"
                            data-testid={`use-template-${template.id}`}
                          >
                            Usar
                          </Button>
                          <button
                            onClick={() => deleteTemplate(template.id)}
                            className="p-2 text-slate-500 hover:text-red-400"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modal para usar plantilla */}
      {showUseModal && selectedTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowUseModal(false)} />
          <div className="relative z-50 w-full max-w-lg max-h-[90vh] overflow-y-auto glass border border-slate-700 rounded-2xl p-6">
            <button 
              onClick={() => setShowUseModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X size={20} />
            </button>
            
            <h3 className="text-lg font-bold gradient-text mb-4">
              Usar Plantilla: {selectedTemplate.name}
            </h3>
            
            {!generatedDoc ? (
              <div className="space-y-4">
                <p className="text-sm text-slate-400">
                  Ingresa los datos del nuevo cliente para generar el documento:
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Nombre del Cliente *</label>
                    <input
                      type="text"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Juan Pérez"
                      className="w-full p-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Cédula *</label>
                    <input
                      type="text"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      placeholder="8-123-456"
                      className="w-full p-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Dirección</label>
                  <input
                    type="text"
                    value={clientAddress}
                    onChange={(e) => setClientAddress(e.target.value)}
                    placeholder="San Miguelito, Calle Principal"
                    className="w-full p-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                  />
                </div>
                
                {isPenalTemplate && (
                  <div className="p-3 bg-red-900/20 rounded-lg border border-red-500/30">
                    <h4 className="text-sm font-medium text-red-300 mb-2">Datos del Querellado</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Nombre</label>
                        <input
                          type="text"
                          value={querelladoNombre}
                          onChange={(e) => setQuerelladoNombre(e.target.value)}
                          placeholder="Nombre querellado"
                          className="w-full p-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Cédula</label>
                        <input
                          type="text"
                          value={querelladoCedula}
                          onChange={(e) => setQuerelladoCedula(e.target.value)}
                          placeholder="Cédula"
                          className="w-full p-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Detalles adicionales del caso</label>
                  <textarea
                    value={additionalDetails}
                    onChange={(e) => setAdditionalDetails(e.target.value)}
                    placeholder="Describe los hechos específicos de este caso..."
                    className="w-full p-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm min-h-[80px]"
                  />
                </div>
                
                <button
                  onClick={useTemplate}
                  disabled={generating}
                  className="w-full py-3 btn-primary rounded-xl font-medium"
                >
                  {generating ? "Generando..." : "Generar Documento"}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-sm text-green-400">✓ Documento generado desde plantilla</p>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-3 max-h-[200px] overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-xs text-slate-300">
                    {generatedDoc.content.substring(0, 1000)}...
                  </pre>
                </div>
                <button
                  onClick={downloadWord}
                  className="w-full py-3 btn-primary rounded-xl font-medium flex items-center justify-center gap-2"
                >
                  <Download size={16} />
                  Descargar Word
                </button>
                <button
                  onClick={() => setShowUseModal(false)}
                  className="w-full py-2 text-slate-400 hover:text-white"
                >
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Modal para subir plantilla propia */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowUploadModal(false)} />
          <div className="relative z-50 w-full max-w-lg glass border border-slate-700 rounded-2xl p-6">
            <button 
              onClick={() => setShowUploadModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X size={20} />
            </button>
            
            <h2 className="text-xl font-bold mb-1 flex items-center gap-2">
              <Upload className="text-purple-400" size={24} />
              Subir Plantilla Propia
            </h2>
            <p className="text-sm text-slate-400 mb-4">
              Sube un documento para usarlo como base para futuros documentos
            </p>
            
            <div className="space-y-4">
              {/* Área de subida de archivo */}
              <div
                onClick={() => uploadInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                  uploadFile 
                    ? 'border-green-500/50 bg-green-500/10' 
                    : 'border-slate-700 hover:border-purple-500/50 hover:bg-purple-500/5'
                }`}
              >
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept=".doc,.docx,.pdf,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {uploadFile ? (
                  <div>
                    <FileText className="mx-auto mb-2 text-green-400" size={32} />
                    <p className="text-green-400 font-medium">{uploadFile.name}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {(uploadFile.size / 1024).toFixed(1)} KB
                    </p>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setUploadFile(null); }}
                      className="text-xs text-red-400 mt-2 hover:text-red-300"
                    >
                      Cambiar archivo
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload className="mx-auto mb-2 text-slate-500" size={32} />
                    <p className="text-slate-400">Haz clic para seleccionar archivo</p>
                    <p className="text-xs text-slate-500 mt-1">.doc, .docx, .pdf, .txt</p>
                  </div>
                )}
              </div>
              
              {/* Nombre de la plantilla */}
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Nombre de la Plantilla *</label>
                <input
                  type="text"
                  value={uploadName}
                  onChange={(e) => setUploadName(e.target.value)}
                  placeholder="Ej: Poder para trámite bancario"
                  className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                />
              </div>
              
              {/* Descripción */}
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Descripción (opcional)</label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Descripción breve del uso de esta plantilla..."
                  className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 min-h-[80px] resize-none"
                />
              </div>
              
              {/* Tipo y Área */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Tipo de Documento</label>
                  <select
                    value={uploadDocType}
                    onChange={(e) => setUploadDocType(e.target.value)}
                    className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white"
                  >
                    <option value="poder">Poder</option>
                    <option value="minuta">Minuta</option>
                    <option value="solicitud">Solicitud</option>
                    <option value="querella">Querella</option>
                    <option value="contrato">Contrato</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Área Legal</label>
                  <select
                    value={uploadLegalArea}
                    onChange={(e) => setUploadLegalArea(e.target.value)}
                    className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white"
                  >
                    <option value="general">General</option>
                    <option value="familia">Familia</option>
                    <option value="civil">Civil</option>
                    <option value="laboral">Laboral</option>
                    <option value="penal">Penal</option>
                    <option value="mercantil">Mercantil</option>
                    <option value="migratorio">Migratorio</option>
                    <option value="maritimo">Marítimo</option>
                    <option value="administrativo">Administrativo</option>
                    <option value="tributario">Tributario</option>
                    <option value="ambiental">Ambiental</option>
                    <option value="agrario">Agrario</option>
                  </select>
                </div>
              </div>
              
              {/* Carpeta */}
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Guardar en Carpeta</label>
                <select
                  value={uploadFolderId}
                  onChange={(e) => setUploadFolderId(e.target.value)}
                  className="w-full p-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white"
                >
                  <option value="">Sin carpeta</option>
                  {folders.map(folder => (
                    <option key={folder.id} value={folder.id}>{folder.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Botón de subir */}
              <button
                onClick={handleUploadTemplate}
                disabled={uploading || !uploadFile || !uploadName.trim()}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-medium flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Subiendo...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Subir Plantilla
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component para el icono de estado (evita re-render condicional problemático)
const StatusIcon = ({ isSpeaking, isListening, isProcessing }) => {
  if (isSpeaking) return <Volume2 size={12} className="animate-pulse" />;
  if (isListening) return <Mic size={12} className="animate-pulse" />;
  if (isProcessing) return <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />;
  return null;
};

// ===================== CALL MODE - LLAMAR A LILA =====================
const CallMode = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isCallActive, setIsCallActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [conversation, setConversation] = useState([]);
  const [currentTranscript, setCurrentTranscript] = useState("");
  const [statusText, setStatusText] = useState("Toca para llamar");
  
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const silenceTimerRef = useRef(null);
  
  // Refs para mantener valores actualizados en callbacks
  const stateRef = useRef({
    isCallActive: false,
    isSpeaking: false,
    isProcessing: false
  });
  
  // Mantener refs sincronizados en un solo useEffect
  useEffect(() => {
    stateRef.current = { isCallActive, isSpeaking, isProcessing };
  }, [isCallActive, isSpeaking, isProcessing]);

  // Iniciar llamada
  const startCall = () => {
    console.log("Iniciando llamada...");
    setIsCallActive(true);
    stateRef.current.isCallActive = true;
    setCallDuration(0);
    setConversation([]);
    setStatusText("Conectando...");
    
    // Iniciar timer
    timerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
    
    // Saludo inicial de LILA
    setTimeout(() => {
      const greeting = "Hola, soy Lila, tu asistente legal. ¿En qué puedo ayudarte hoy?";
      setConversation([{ role: "lila", text: greeting }]);
      speakText(greeting);
    }, 1000);
  };

  // Terminar llamada
  const endCall = () => {
    console.log("Finalizando llamada...");
    setIsCallActive(false);
    stateRef.current.isCallActive = false;
    setIsListening(false);
    setIsSpeaking(false);
    stateRef.current.isSpeaking = false;
    setIsProcessing(false);
    stateRef.current.isProcessing = false;
    setStatusText("Llamada finalizada");
    
    if (timerRef.current) clearInterval(timerRef.current);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch(e) {}
      recognitionRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    toast.success(`Llamada finalizada: ${formatTime(callDuration)}`);
  };

  // Formatear tiempo
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Hablar con ElevenLabs
  const speakText = async (text) => {
    console.log("LILA hablando:", text.substring(0, 50) + "...");
    setIsSpeaking(true);
    stateRef.current.isSpeaking = true;
    setStatusText("LILA está hablando...");
    
    try {
      const response = await axios.post(`${API}/tts/generate`, { text }, {
        responseType: 'blob'
      });
      
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      audioRef.current = new Audio(audioUrl);
      
      audioRef.current.onended = () => {
        console.log("Audio terminado, isCallActive:", stateRef.current.isCallActive);
        setIsSpeaking(false);
        stateRef.current.isSpeaking = false;
        URL.revokeObjectURL(audioUrl);
        if (stateRef.current.isCallActive) {
          // Empezar a escuchar inmediatamente (optimizado de 500ms a 200ms)
          console.log("Iniciando escucha después de hablar...");
          setTimeout(() => {
            if (stateRef.current.isCallActive && !stateRef.current.isSpeaking && !stateRef.current.isProcessing) {
              startListening();
            }
          }, 200);
        }
      };
      
      audioRef.current.onerror = (e) => {
        console.error("Error de audio:", e);
        setIsSpeaking(false);
        stateRef.current.isSpeaking = false;
        if (stateRef.current.isCallActive) startListening();
      };
      
      await audioRef.current.play();
    } catch (error) {
      console.error("TTS Error:", error);
      setIsSpeaking(false);
      stateRef.current.isSpeaking = false;
      if (stateRef.current.isCallActive) startListening();
    }
  };

  // Empezar a escuchar
  const startListening = () => {
    console.log("startListening - isCallActive:", stateRef.current.isCallActive, "isSpeaking:", stateRef.current.isSpeaking, "isProcessing:", stateRef.current.isProcessing);
    
    if (!stateRef.current.isCallActive || stateRef.current.isSpeaking || stateRef.current.isProcessing) {
      console.log("Condiciones no cumplidas, no iniciando escucha");
      return;
    }
    
    // Detener reconocimiento anterior si existe
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch(e) {}
      recognitionRef.current = null;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Tu navegador no soporta reconocimiento de voz");
      return;
    }
    
    const recognition = new SpeechRecognition();
    recognition.lang = 'es-PA';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    
    let finalTranscript = "";
    let hasSpoken = false;
    
    recognition.onstart = () => {
      console.log("Recognition started");
      setIsListening(true);
      setStatusText("Escuchando...");
      setCurrentTranscript("");
    };
    
    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript + " ";
          hasSpoken = true;
        } else {
          interim += result[0].transcript;
        }
      }
      setCurrentTranscript(finalTranscript + interim);
      console.log("Transcripción:", finalTranscript + interim);
      
      // Resetear timer de silencio
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      
      // Si hay texto final, esperar 1.2 segundos de silencio para procesar (optimizado)
      if (finalTranscript.trim()) {
        silenceTimerRef.current = setTimeout(() => {
          if (finalTranscript.trim() && stateRef.current.isCallActive && !stateRef.current.isSpeaking) {
            console.log("Procesando mensaje:", finalTranscript.trim());
            try { recognition.stop(); } catch(e) {}
            processUserMessage(finalTranscript.trim());
          }
        }, 1200);
      }
    };
    
    recognition.onerror = (event) => {
      console.log("Recognition error:", event.error);
      if (event.error !== 'aborted' && event.error !== 'no-speech') {
        console.error("Recognition error:", event.error);
      }
      if (event.error === 'no-speech' && stateRef.current.isCallActive && !stateRef.current.isSpeaking && !stateRef.current.isProcessing) {
        // Reintentar después de un momento
        setTimeout(() => {
          if (stateRef.current.isCallActive && !stateRef.current.isSpeaking && !stateRef.current.isProcessing) {
            startListening();
          }
        }, 500);
      }
    };
    
    recognition.onend = () => {
      console.log("Recognition ended, hasSpoken:", hasSpoken, "finalTranscript:", finalTranscript);
      setIsListening(false);
      // Si la llamada sigue activa y no estamos hablando/procesando, reiniciar más rápido
      if (stateRef.current.isCallActive && !stateRef.current.isSpeaking && !stateRef.current.isProcessing && !finalTranscript.trim()) {
        setTimeout(() => {
          if (stateRef.current.isCallActive && !stateRef.current.isSpeaking && !stateRef.current.isProcessing) {
            console.log("Reiniciando escucha...");
            startListening();
          }
        }, 200);
      }
    };
    
    recognitionRef.current = recognition;
    
    try {
      recognition.start();
      console.log("Recognition.start() llamado");
    } catch (e) {
      console.error("Error starting recognition:", e);
      // Reintentar si falla
      setTimeout(() => {
        if (stateRef.current.isCallActive && !stateRef.current.isSpeaking && !stateRef.current.isProcessing) {
          startListening();
        }
      }, 1000);
    }
  };

  // Procesar mensaje del usuario
  const processUserMessage = async (text) => {
    console.log("Procesando mensaje del usuario:", text);
    setIsProcessing(true);
    stateRef.current.isProcessing = true;
    setIsListening(false);
    setStatusText("Procesando...");
    setCurrentTranscript("");
    
    // Agregar mensaje del usuario
    setConversation(prev => [...prev, { role: "user", text }]);
    
    try {
      const response = await axios.post(`${API}/chat/send`, {
        message: text,
        legal_area: "general"
      });
      
      const lilaResponse = response.data.response;
      console.log("Respuesta de LILA:", lilaResponse.substring(0, 50) + "...");
      
      // Agregar respuesta de LILA
      setConversation(prev => [...prev, { role: "lila", text: lilaResponse }]);
      
      setIsProcessing(false);
      stateRef.current.isProcessing = false;
      
      // Hablar la respuesta
      await speakText(lilaResponse);
      
    } catch (error) {
      console.error("Chat error:", error);
      setIsProcessing(false);
      stateRef.current.isProcessing = false;
      const errorMsg = "Disculpa, tuve un problema. ¿Puedes repetir tu pregunta?";
      setConversation(prev => [...prev, { role: "lila", text: errorMsg }]);
      await speakText(errorMsg);
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch(e) {}
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Obtener estilo del avatar según estado
  const getAvatarStyle = () => {
    if (!isCallActive) return 'bg-slate-800';
    if (isSpeaking) return 'bg-gradient-to-br from-cyan-500 to-purple-600 shadow-lg shadow-cyan-500/50';
    if (isListening) return 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg shadow-green-500/50';
    if (isProcessing) return 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/50';
    return 'bg-gradient-to-br from-purple-600 to-pink-600';
  };

  const getStatusBadgeStyle = () => {
    if (isSpeaking) return 'bg-cyan-500/20 text-cyan-400';
    if (isListening) return 'bg-green-500/20 text-green-400';
    if (isProcessing) return 'bg-amber-500/20 text-amber-400';
    return 'bg-purple-500/20 text-purple-400';
  };

  return (
    <div className="min-h-screen bg-pattern flex flex-col" data-testid="call-mode-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
            ← Volver
          </Button>
          <h1 className="text-lg font-bold">Llamar a LILA</h1>
          <div className="w-20"></div>
        </div>
      </header>

      {/* Call Interface */}
      <main className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md">
          
          {/* Avatar y Estado */}
          <div className="text-center mb-8">
            <div className={`relative inline-block ${isSpeaking ? 'animate-pulse' : ''}`}>
              <div className={`w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-4 ${getAvatarStyle()}`}>
                <Scale size={56} className="text-white" />
              </div>
              <div 
                className={`absolute -bottom-1 left-1/2 -translate-x-1/2 transition-opacity duration-200 ${isCallActive ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              >
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeStyle()}`}>
                  <StatusIcon isSpeaking={isSpeaking} isListening={isListening} isProcessing={isProcessing} />
                  <span>{statusText}</span>
                </span>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-1">LILA</h2>
            <p className="text-slate-400 text-sm">Asistente Legal con IA</p>
            
            <p className={`text-2xl font-mono text-cyan-400 mt-4 transition-opacity ${isCallActive ? 'opacity-100' : 'opacity-0'}`}>
              {formatTime(callDuration)}
            </p>
          </div>

          {/* Transcripción en tiempo real - siempre renderizado, controlado por opacidad */}
          <div 
            className={`glass border border-slate-700 rounded-xl p-4 mb-6 transition-all duration-200 ${
              isCallActive && currentTranscript 
                ? 'opacity-100' 
                : 'opacity-0 max-h-0 p-0 mb-0 border-0 overflow-hidden'
            }`}
          >
            <p className="text-sm text-slate-400 mb-1">Escuchando:</p>
            <p className="text-white">{currentTranscript || ' '}</p>
          </div>

          {/* Historial de conversación - siempre renderizado, controlado por opacidad */}
          <div 
            className={`glass border border-slate-700 rounded-xl p-4 mb-6 max-h-48 overflow-y-auto transition-all duration-200 ${
              isCallActive && conversation.length > 0 
                ? 'opacity-100' 
                : 'opacity-0 max-h-0 p-0 mb-0 border-0 overflow-hidden'
            }`}
          >
            <p className="text-xs text-slate-500 mb-2">Conversación:</p>
            {conversation.map((msg, idx) => (
              <div key={`msg-${idx}`} className={`mb-2 ${msg.role === 'user' ? 'text-right' : ''}`}>
                <span className={`inline-block px-3 py-2 rounded-lg text-sm max-w-[85%] ${
                  msg.role === 'user' 
                    ? 'bg-purple-500/20 text-purple-300' 
                    : 'bg-cyan-500/20 text-cyan-300'
                }`}>
                  {msg.text.substring(0, 150)}{msg.text.length > 150 ? '...' : ''}
                </span>
              </div>
            ))}
          </div>

          {/* Botón de llamada */}
          <div className="flex justify-center">
            <button
              onClick={isCallActive ? endCall : startCall}
              className={`w-24 h-24 rounded-full flex items-center justify-center transition-all hover:scale-105 ${
                isCallActive 
                  ? 'bg-gradient-to-br from-red-500 to-rose-600 hover:from-red-400 hover:to-rose-500 shadow-lg shadow-red-500/30'
                  : 'bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 shadow-lg shadow-green-500/30'
              }`}
            >
              {isCallActive ? <PhoneOff size={40} className="text-white" /> : <Phone size={40} className="text-white" />}
            </button>
          </div>

          {/* Instrucciones - siempre renderizado */}
          <div className={`mt-4 text-center transition-opacity ${isCallActive ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'}`}>
            <p className="text-slate-400 text-sm">
              Toca el botón para iniciar una llamada con LILA
            </p>
          </div>

          <div className={`mt-6 text-center text-slate-400 text-xs transition-opacity ${isCallActive ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'}`}>
            <p>Habla naturalmente. LILA te escuchará y responderá.</p>
            <p className="mt-1">Toca el botón rojo para finalizar la llamada.</p>
          </div>
        </div>
      </main>
    </div>
  );
};

// ===================== STUDENT MODE - MODO ESTUDIANTE =====================
const StudentMode = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("quiz");
  const [selectedArea, setSelectedArea] = useState("civil");
  const [loading, setLoading] = useState(false);
  
  // Quiz states
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  
  // Flashcards states
  const [flashcards, setFlashcards] = useState([]);
  const [currentCard, setCurrentCard] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  
  // Explainer states
  const [explainTopic, setExplainTopic] = useState("");
  const [explanation, setExplanation] = useState("");
  const [explainMode, setExplainMode] = useState("normal"); // normal, simple, detailed
  
  // Practice case states
  const [practiceCase, setPracticeCase] = useState(null);
  const [userAnswer, setUserAnswer] = useState("");
  const [caseEvaluation, setCaseEvaluation] = useState(null);
  
  // Oral exam states
  const [oralQuestion, setOralQuestion] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [oralAnswer, setOralAnswer] = useState("");
  const [oralFeedback, setOralFeedback] = useState(null);

  const legalAreas = [
    { id: "civil", name: "Derecho Civil" },
    { id: "penal", name: "Derecho Penal" },
    { id: "familia", name: "Derecho de Familia" },
    { id: "laboral", name: "Derecho Laboral" },
    { id: "constitucional", name: "Derecho Constitucional" },
    { id: "mercantil", name: "Derecho Mercantil" },
    { id: "administrativo", name: "Derecho Administrativo" },
    { id: "procesal", name: "Derecho Procesal" }
  ];

  // Generar Quiz
  const generateQuiz = async () => {
    setLoading(true);
    setQuizStarted(false);
    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowResult(false);
    
    try {
      const response = await axios.post(`${API}/chat/send`, {
        message: `Genera 5 preguntas de opción múltiple sobre ${legalAreas.find(a => a.id === selectedArea)?.name || selectedArea} en Panamá. 
        
        FORMATO OBLIGATORIO (responde SOLO con este JSON, sin texto adicional):
        [
          {
            "question": "¿Pregunta aquí?",
            "options": ["A) Opción 1", "B) Opción 2", "C) Opción 3", "D) Opción 4"],
            "correct": 0,
            "explanation": "Explicación breve de la respuesta correcta"
          }
        ]
        
        Las preguntas deben ser sobre leyes panameñas vigentes.`,
        legal_area: selectedArea
      });
      
      // Parsear respuesta JSON
      const text = response.data.response;
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const questions = JSON.parse(jsonMatch[0]);
        setQuizQuestions(questions);
        setQuizStarted(true);
      } else {
        toast.error("Error al generar quiz, intenta de nuevo");
      }
    } catch (error) {
      toast.error("Error al generar quiz");
    } finally {
      setLoading(false);
    }
  };

  // Verificar respuesta del quiz
  const checkAnswer = (answerIndex) => {
    setSelectedAnswer(answerIndex);
    setShowResult(true);
    if (answerIndex === quizQuestions[currentQuestion].correct) {
      setScore(prev => prev + 1);
    }
  };

  // Siguiente pregunta
  const nextQuestion = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    }
  };

  // Generar Flashcards
  const generateFlashcards = async () => {
    setLoading(true);
    setCurrentCard(0);
    setIsFlipped(false);
    
    try {
      const response = await axios.post(`${API}/chat/send`, {
        message: `Genera 8 flashcards (tarjetas de estudio) sobre conceptos importantes de ${legalAreas.find(a => a.id === selectedArea)?.name || selectedArea} en Panamá.
        
        FORMATO OBLIGATORIO (responde SOLO con este JSON):
        [
          {
            "front": "Concepto o pregunta",
            "back": "Definición o respuesta (máximo 100 palabras)"
          }
        ]`,
        legal_area: selectedArea
      });
      
      const text = response.data.response;
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        setFlashcards(JSON.parse(jsonMatch[0]));
      } else {
        toast.error("Error al generar flashcards");
      }
    } catch (error) {
      toast.error("Error al generar flashcards");
    } finally {
      setLoading(false);
    }
  };

  // Explicar tema
  const explainTopicHandler = async () => {
    if (!explainTopic.trim()) {
      toast.error("Escribe un tema para explicar");
      return;
    }
    
    setLoading(true);
    setExplanation("");
    
    const modeInstructions = {
      simple: "Explícalo de forma MUY simple, como si le explicaras a alguien que no sabe nada de derecho. Usa ejemplos cotidianos.",
      normal: "Explícalo de forma clara y concisa para un estudiante de derecho.",
      detailed: "Explícalo de forma detallada y profunda, incluyendo jurisprudencia y doctrina relevante."
    };
    
    try {
      const response = await axios.post(`${API}/chat/send`, {
        message: `${modeInstructions[explainMode]}
        
        Tema a explicar: ${explainTopic}
        
        Contexto: Derecho panameño, ${legalAreas.find(a => a.id === selectedArea)?.name || selectedArea}.
        
        Incluye:
        - Definición clara
        - Base legal en Panamá (artículos específicos)
        - Ejemplo práctico
        - Puntos clave para recordar`,
        legal_area: selectedArea
      });
      
      setExplanation(response.data.response);
    } catch (error) {
      toast.error("Error al explicar tema");
    } finally {
      setLoading(false);
    }
  };

  // Generar caso práctico
  const generatePracticeCase = async () => {
    setLoading(true);
    setPracticeCase(null);
    setUserAnswer("");
    setCaseEvaluation(null);
    
    try {
      const response = await axios.post(`${API}/chat/send`, {
        message: `Crea un caso práctico de ${legalAreas.find(a => a.id === selectedArea)?.name || selectedArea} para que un estudiante analice y resuelva.
        
        FORMATO OBLIGATORIO (responde SOLO con este JSON):
        {
          "title": "Título del caso",
          "facts": "Hechos del caso (máximo 200 palabras)",
          "question": "¿Qué debe resolver el estudiante?",
          "hints": ["Pista 1", "Pista 2"],
          "model_answer": "Respuesta modelo detallada que usarás para evaluar"
        }
        
        El caso debe basarse en leyes panameñas vigentes.`,
        legal_area: selectedArea
      });
      
      const text = response.data.response;
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        setPracticeCase(JSON.parse(jsonMatch[0]));
      } else {
        toast.error("Error al generar caso");
      }
    } catch (error) {
      toast.error("Error al generar caso práctico");
    } finally {
      setLoading(false);
    }
  };

  // Evaluar respuesta del caso
  const evaluateCase = async () => {
    if (!userAnswer.trim()) {
      toast.error("Escribe tu análisis del caso");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/chat/send`, {
        message: `Evalúa la respuesta de un estudiante a este caso práctico.
        
        CASO: ${practiceCase.title}
        ${practiceCase.facts}
        Pregunta: ${practiceCase.question}
        
        RESPUESTA MODELO: ${practiceCase.model_answer}
        
        RESPUESTA DEL ESTUDIANTE: ${userAnswer}
        
        Proporciona una evaluación constructiva:
        1. Calificación del 1 al 10
        2. Qué hizo bien
        3. Qué le faltó
        4. Sugerencias para mejorar
        
        Sé constructivo y motivador.`,
        legal_area: selectedArea
      });
      
      setCaseEvaluation(response.data.response);
    } catch (error) {
      toast.error("Error al evaluar respuesta");
    } finally {
      setLoading(false);
    }
  };

  // Generar pregunta oral
  const generateOralQuestion = async () => {
    setLoading(true);
    setOralQuestion("");
    setOralAnswer("");
    setOralFeedback(null);
    
    try {
      const response = await axios.post(`${API}/chat/send`, {
        message: `Genera UNA pregunta de examen oral sobre ${legalAreas.find(a => a.id === selectedArea)?.name || selectedArea} en Panamá.
        
        La pregunta debe:
        - Ser clara y directa
        - Requerir una respuesta de 1-2 minutos
        - Basarse en legislación panameña vigente
        
        Responde SOLO con la pregunta, sin explicación adicional.`,
        legal_area: selectedArea
      });
      
      setOralQuestion(response.data.response);
    } catch (error) {
      toast.error("Error al generar pregunta");
    } finally {
      setLoading(false);
    }
  };

  // Evaluar respuesta oral
  const evaluateOralAnswer = async () => {
    if (!oralAnswer.trim()) {
      toast.error("Escribe o dicta tu respuesta");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/chat/send`, {
        message: `Actúa como un profesor de derecho evaluando un examen oral.
        
        PREGUNTA: ${oralQuestion}
        
        RESPUESTA DEL ESTUDIANTE: ${oralAnswer}
        
        Evalúa la respuesta considerando:
        1. Precisión legal (¿citó correctamente las leyes panameñas?)
        2. Estructura de la respuesta
        3. Claridad de expresión
        4. Profundidad del análisis
        
        Proporciona:
        - Calificación del 1 al 10
        - Retroalimentación constructiva
        - La respuesta correcta/completa`,
        legal_area: selectedArea
      });
      
      setOralFeedback(response.data.response);
    } catch (error) {
      toast.error("Error al evaluar respuesta");
    } finally {
      setLoading(false);
    }
  };

  // Reconocimiento de voz para examen oral
  const startVoiceRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Tu navegador no soporta reconocimiento de voz");
      return;
    }
    
    const recognition = new SpeechRecognition();
    recognition.lang = 'es-PA';
    recognition.continuous = true;
    recognition.interimResults = true;
    
    let finalTranscript = "";
    
    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript + " ";
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setOralAnswer(finalTranscript + interim);
    };
    
    recognition.onerror = (event) => {
      if (event.error !== 'aborted') {
        toast.error("Error de grabación: " + event.error);
      }
      setIsRecording(false);
    };
    
    recognition.onend = () => {
      setIsRecording(false);
    };
    
    recognition.start();
    setIsRecording(true);
    
    // Auto-stop after 2 minutes
    setTimeout(() => {
      recognition.stop();
    }, 120000);
    
    window.currentRecognition = recognition;
  };

  const stopVoiceRecording = () => {
    if (window.currentRecognition) {
      window.currentRecognition.stop();
    }
    setIsRecording(false);
  };

  return (
    <div className="min-h-screen bg-pattern" data-testid="student-mode-page">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
              ← Volver
            </Button>
            <h1 className="text-xl font-bold">Modo Estudiante</h1>
          </div>
          <BookOpen className="text-cyan-400" size={24} />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Selector de área legal */}
        <div className="mb-6">
          <label className="text-sm text-slate-400 mb-2 block">Área de estudio:</label>
          <Select value={selectedArea} onValueChange={setSelectedArea}>
            <SelectTrigger className="bg-slate-800 border-slate-700 w-full md:w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {legalAreas.map(area => (
                <SelectItem key={area.id} value={area.id}>{area.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: "quiz", label: "Quiz", icon: "❓" },
            { id: "flashcards", label: "Flashcards", icon: "🎴" },
            { id: "explainer", label: "Explicador", icon: "💡" },
            { id: "cases", label: "Casos", icon: "📋" },
            { id: "oral", label: "Examen Oral", icon: "🎤" }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                activeTab === tab.id
                  ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                  : "bg-slate-800/50 text-slate-400 hover:bg-slate-800"
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Quiz Tab */}
        {activeTab === "quiz" && (
          <Card className="glass border-slate-800">
            <CardHeader>
              <CardTitle className="text-cyan-400">Quiz de Práctica</CardTitle>
              <CardDescription>Pon a prueba tus conocimientos con preguntas de opción múltiple</CardDescription>
            </CardHeader>
            <CardContent>
              {!quizStarted ? (
                <div className="text-center py-8">
                  <p className="text-slate-400 mb-4">Genera un quiz de 5 preguntas sobre {legalAreas.find(a => a.id === selectedArea)?.name}</p>
                  <Button onClick={generateQuiz} disabled={loading} className="btn-primary">
                    {loading ? "Generando..." : "Generar Quiz"}
                  </Button>
                </div>
              ) : (
                <div>
                  {/* Progress */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-slate-400 text-sm">Pregunta {currentQuestion + 1} de {quizQuestions.length}</span>
                    <span className="text-cyan-400 text-sm">Puntuación: {score}/{quizQuestions.length}</span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2 mb-6">
                    <div 
                      className="bg-cyan-500 h-2 rounded-full transition-all" 
                      style={{ width: `${((currentQuestion + 1) / quizQuestions.length) * 100}%` }}
                    />
                  </div>
                  
                  {/* Question */}
                  <h3 className="text-lg text-white mb-4">{quizQuestions[currentQuestion]?.question}</h3>
                  
                  {/* Options */}
                  <div className="space-y-3 mb-6">
                    {quizQuestions[currentQuestion]?.options.map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => !showResult && checkAnswer(idx)}
                        disabled={showResult}
                        className={`w-full p-4 rounded-lg text-left transition-all ${
                          showResult
                            ? idx === quizQuestions[currentQuestion].correct
                              ? "bg-green-500/20 border border-green-500/50 text-green-400"
                              : idx === selectedAnswer
                                ? "bg-red-500/20 border border-red-500/50 text-red-400"
                                : "bg-slate-800/50 text-slate-400"
                            : selectedAnswer === idx
                              ? "bg-cyan-500/20 border border-cyan-500/50"
                              : "bg-slate-800/50 hover:bg-slate-800 border border-transparent"
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                  
                  {/* Explanation */}
                  {showResult && (
                    <div className={`p-4 rounded-lg mb-4 ${
                      selectedAnswer === quizQuestions[currentQuestion].correct
                        ? "bg-green-500/10 border border-green-500/30"
                        : "bg-amber-500/10 border border-amber-500/30"
                    }`}>
                      <p className="text-sm">
                        {selectedAnswer === quizQuestions[currentQuestion].correct ? "✅ ¡Correcto! " : "❌ Incorrecto. "}
                        {quizQuestions[currentQuestion].explanation}
                      </p>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex gap-3">
                    {showResult && currentQuestion < quizQuestions.length - 1 && (
                      <Button onClick={nextQuestion} className="btn-primary">
                        Siguiente Pregunta
                      </Button>
                    )}
                    {showResult && currentQuestion === quizQuestions.length - 1 && (
                      <div className="text-center w-full">
                        <p className="text-xl text-white mb-4">¡Quiz Completado!</p>
                        <p className="text-cyan-400 text-2xl mb-4">Tu puntuación: {score}/{quizQuestions.length}</p>
                        <Button onClick={generateQuiz} className="btn-primary">
                          Nuevo Quiz
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Flashcards Tab */}
        {activeTab === "flashcards" && (
          <Card className="glass border-slate-800">
            <CardHeader>
              <CardTitle className="text-purple-400">Flashcards</CardTitle>
              <CardDescription>Tarjetas de estudio para memorizar conceptos</CardDescription>
            </CardHeader>
            <CardContent>
              {flashcards.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-400 mb-4">Genera flashcards sobre {legalAreas.find(a => a.id === selectedArea)?.name}</p>
                  <Button onClick={generateFlashcards} disabled={loading} className="bg-purple-600 hover:bg-purple-500">
                    {loading ? "Generando..." : "Generar Flashcards"}
                  </Button>
                </div>
              ) : (
                <div>
                  {/* Card counter */}
                  <p className="text-center text-slate-400 mb-4">Tarjeta {currentCard + 1} de {flashcards.length}</p>
                  
                  {/* Flashcard */}
                  <div 
                    onClick={() => setIsFlipped(!isFlipped)}
                    className={`min-h-[200px] p-6 rounded-xl cursor-pointer transition-all duration-500 transform ${
                      isFlipped ? "bg-purple-500/20 border-purple-500/30" : "bg-slate-800 border-slate-700"
                    } border flex items-center justify-center text-center`}
                  >
                    <div>
                      <p className="text-xs text-slate-500 mb-2">{isFlipped ? "Respuesta" : "Pregunta"} - Toca para voltear</p>
                      <p className="text-lg text-white">
                        {isFlipped ? flashcards[currentCard]?.back : flashcards[currentCard]?.front}
                      </p>
                    </div>
                  </div>
                  
                  {/* Navigation */}
                  <div className="flex justify-between mt-6">
                    <Button 
                      variant="outline" 
                      onClick={() => { setCurrentCard(prev => Math.max(0, prev - 1)); setIsFlipped(false); }}
                      disabled={currentCard === 0}
                    >
                      ← Anterior
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => { setCurrentCard(prev => Math.min(flashcards.length - 1, prev + 1)); setIsFlipped(false); }}
                      disabled={currentCard === flashcards.length - 1}
                    >
                      Siguiente →
                    </Button>
                  </div>
                  
                  <div className="text-center mt-4">
                    <Button onClick={generateFlashcards} variant="ghost" className="text-purple-400">
                      Generar Nuevas Tarjetas
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Explainer Tab */}
        {activeTab === "explainer" && (
          <Card className="glass border-slate-800">
            <CardHeader>
              <CardTitle className="text-amber-400">Explicador de Temas</CardTitle>
              <CardDescription>LILA te explica cualquier concepto legal</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Topic input */}
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">¿Qué tema quieres que te explique?</label>
                  <Input
                    value={explainTopic}
                    onChange={(e) => setExplainTopic(e.target.value)}
                    placeholder="Ej: La prescripción en materia civil, el habeas corpus, la responsabilidad extracontractual..."
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                
                {/* Explanation mode */}
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Nivel de explicación:</label>
                  <div className="flex gap-2">
                    {[
                      { id: "simple", label: "Simple", emoji: "🎈" },
                      { id: "normal", label: "Normal", emoji: "📚" },
                      { id: "detailed", label: "Detallado", emoji: "🎓" }
                    ].map(mode => (
                      <button
                        key={mode.id}
                        onClick={() => setExplainMode(mode.id)}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm ${
                          explainMode === mode.id
                            ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                            : "bg-slate-800 text-slate-400"
                        }`}
                      >
                        {mode.emoji} {mode.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <Button onClick={explainTopicHandler} disabled={loading || !explainTopic.trim()} className="w-full bg-amber-600 hover:bg-amber-500">
                  {loading ? "Explicando..." : "Explicar Tema"}
                </Button>
                
                {/* Explanation result */}
                {explanation && (
                  <div className="mt-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                    <h4 className="text-amber-400 font-medium mb-3">Explicación:</h4>
                    <div className="text-slate-300 whitespace-pre-wrap text-sm leading-relaxed">
                      {explanation}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cases Tab */}
        {activeTab === "cases" && (
          <Card className="glass border-slate-800">
            <CardHeader>
              <CardTitle className="text-emerald-400">Casos Prácticos</CardTitle>
              <CardDescription>Analiza y resuelve casos reales</CardDescription>
            </CardHeader>
            <CardContent>
              {!practiceCase ? (
                <div className="text-center py-8">
                  <p className="text-slate-400 mb-4">Genera un caso práctico de {legalAreas.find(a => a.id === selectedArea)?.name} para analizar</p>
                  <Button onClick={generatePracticeCase} disabled={loading} className="bg-emerald-600 hover:bg-emerald-500">
                    {loading ? "Generando..." : "Generar Caso Práctico"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Case details */}
                  <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                    <h4 className="text-emerald-400 font-medium mb-2">{practiceCase.title}</h4>
                    <p className="text-slate-300 text-sm mb-4">{practiceCase.facts}</p>
                    <p className="text-white font-medium">{practiceCase.question}</p>
                    
                    {/* Hints */}
                    {practiceCase.hints && (
                      <div className="mt-3 p-3 bg-amber-500/10 rounded-lg">
                        <p className="text-xs text-amber-400 mb-1">💡 Pistas:</p>
                        <ul className="text-xs text-slate-400">
                          {practiceCase.hints.map((hint, i) => (
                            <li key={i}>• {hint}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  {/* User answer */}
                  <div>
                    <label className="text-sm text-slate-400 mb-2 block">Tu análisis:</label>
                    <Textarea
                      value={userAnswer}
                      onChange={(e) => setUserAnswer(e.target.value)}
                      placeholder="Escribe tu análisis y solución del caso..."
                      className="bg-slate-800 border-slate-700 min-h-[150px]"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button onClick={evaluateCase} disabled={loading || !userAnswer.trim()} className="flex-1 bg-emerald-600 hover:bg-emerald-500">
                      {loading ? "Evaluando..." : "Evaluar mi Respuesta"}
                    </Button>
                    <Button onClick={generatePracticeCase} variant="outline" disabled={loading}>
                      Nuevo Caso
                    </Button>
                  </div>
                  
                  {/* Evaluation */}
                  {caseEvaluation && (
                    <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
                      <h4 className="text-emerald-400 font-medium mb-2">Evaluación del Profesor:</h4>
                      <div className="text-slate-300 text-sm whitespace-pre-wrap">
                        {caseEvaluation}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Oral Exam Tab */}
        {activeTab === "oral" && (
          <Card className="glass border-slate-800">
            <CardHeader>
              <CardTitle className="text-pink-400">Simulador de Examen Oral</CardTitle>
              <CardDescription>Practica respondiendo preguntas como en un examen real</CardDescription>
            </CardHeader>
            <CardContent>
              {!oralQuestion ? (
                <div className="text-center py-8">
                  <p className="text-slate-400 mb-4">Genera una pregunta de examen oral sobre {legalAreas.find(a => a.id === selectedArea)?.name}</p>
                  <Button onClick={generateOralQuestion} disabled={loading} className="bg-pink-600 hover:bg-pink-500">
                    {loading ? "Generando..." : "Generar Pregunta"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Question */}
                  <div className="p-4 bg-pink-500/10 rounded-xl border border-pink-500/30">
                    <p className="text-xs text-pink-400 mb-2">Pregunta del examinador:</p>
                    <p className="text-white text-lg">{oralQuestion}</p>
                  </div>
                  
                  {/* Answer input */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm text-slate-400">Tu respuesta:</label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
                        className={isRecording ? "text-red-400" : "text-pink-400"}
                      >
                        {isRecording ? <><MicOff size={16} className="mr-1" /> Detener</> : <><Mic size={16} className="mr-1" /> Dictar</>}
                      </Button>
                    </div>
                    <Textarea
                      value={oralAnswer}
                      onChange={(e) => setOralAnswer(e.target.value)}
                      placeholder="Escribe o dicta tu respuesta..."
                      className="bg-slate-800 border-slate-700 min-h-[120px]"
                    />
                    {isRecording && (
                      <p className="text-xs text-red-400 mt-1 animate-pulse">🔴 Grabando... Habla tu respuesta</p>
                    )}
                  </div>
                  
                  <div className="flex gap-3">
                    <Button onClick={evaluateOralAnswer} disabled={loading || !oralAnswer.trim()} className="flex-1 bg-pink-600 hover:bg-pink-500">
                      {loading ? "Evaluando..." : "Evaluar Respuesta"}
                    </Button>
                    <Button onClick={generateOralQuestion} variant="outline" disabled={loading}>
                      Nueva Pregunta
                    </Button>
                  </div>
                  
                  {/* Feedback */}
                  {oralFeedback && (
                    <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                      <h4 className="text-pink-400 font-medium mb-2">Retroalimentación del Profesor:</h4>
                      <div className="text-slate-300 text-sm whitespace-pre-wrap">
                        {oralFeedback}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

// ===================== LAWYER TOOLS - HERRAMIENTAS PARA ABOGADOS =====================
const LawyerTools = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("laboral");
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  
  // === CALCULADORA LABORAL ===
  const [laboralData, setLaboralData] = useState({
    salarioMensual: "",
    fechaInicio: "",
    fechaFin: "",
    motivoDespido: "injustificado", // injustificado, justificado, renuncia
    vacacionesPendientes: "0"
  });
  const [laboralResult, setLaboralResult] = useState(null);
  
  // === CALCULADORA PENSIÓN ALIMENTICIA ===
  const [pensionData, setPensionData] = useState({
    ingresosMensuales: "",
    numHijos: "1",
    edadHijos: "menores", // menores, mayores_estudiando
    tieneOtrasPensiones: false,
    otrasPensiones: "0"
  });
  const [pensionResult, setPensionResult] = useState(null);
  
  // === CALCULADORA DE PLAZOS ===
  const [plazosData, setPlazosData] = useState({
    fechaNotificacion: "",
    tipoPlazo: "apelacion", // apelacion, contestacion, recurso, prescripcion
    areaLegal: "civil"
  });
  const [plazosResult, setPlazosResult] = useState(null);

  // Calcular indemnización laboral
  const calcularLaboral = () => {
    const salario = parseFloat(laboralData.salarioMensual) || 0;
    const fechaInicio = new Date(laboralData.fechaInicio);
    const fechaFin = new Date(laboralData.fechaFin);
    const vacacionesPend = parseInt(laboralData.vacacionesPendientes) || 0;
    
    if (!salario || !laboralData.fechaInicio || !laboralData.fechaFin) {
      toast.error("Completa todos los campos");
      return;
    }
    
    // Calcular años y meses trabajados
    const diffTime = Math.abs(fechaFin - fechaInicio);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const years = diffDays / 365;
    const months = (diffDays % 365) / 30;
    
    // Salario diario
    const salarioDiario = salario / 30;
    
    // Cálculos según Código de Trabajo de Panamá
    let preaviso = 0;
    let antiguedad = 0;
    let vacaciones = 0;
    let decimoTercer = 0;
    let primaAntiguedad = 0;
    
    // Preaviso (Art. 212 Código de Trabajo)
    if (laboralData.motivoDespido === "injustificado") {
      if (years < 1) {
        preaviso = salario; // 30 días
      } else if (years < 2) {
        preaviso = salario * 1.5; // 45 días
      } else {
        preaviso = salario * 2; // 60 días
      }
    }
    
    // Indemnización por antigüedad (Art. 225)
    if (laboralData.motivoDespido === "injustificado") {
      antiguedad = salario * 3.4 * years; // 3.4 semanas por año
    }
    
    // Prima de antigüedad (Art. 224) - para todos
    if (years >= 1) {
      primaAntiguedad = salario * (years * (1/52 * 1.5)); // 1 semana por año
    }
    
    // Vacaciones proporcionales
    const diasVacaciones = Math.min(30, 30 * (months / 12)) + vacacionesPend;
    vacaciones = salarioDiario * diasVacaciones;
    
    // XIII mes proporcional
    decimoTercer = salario * (months / 12);
    
    const total = preaviso + antiguedad + vacaciones + decimoTercer + primaAntiguedad;
    
    setLaboralResult({
      añosTrabajados: years.toFixed(1),
      mesesTrabajados: Math.floor(months),
      salarioDiario: salarioDiario.toFixed(2),
      preaviso: preaviso.toFixed(2),
      antiguedad: antiguedad.toFixed(2),
      primaAntiguedad: primaAntiguedad.toFixed(2),
      vacaciones: vacaciones.toFixed(2),
      decimoTercer: decimoTercer.toFixed(2),
      total: total.toFixed(2)
    });
  };

  // Calcular pensión alimenticia
  const calcularPension = () => {
    const ingresos = parseFloat(pensionData.ingresosMensuales) || 0;
    const numHijos = parseInt(pensionData.numHijos) || 1;
    const otrasPensiones = parseFloat(pensionData.otrasPensiones) || 0;
    
    if (!ingresos) {
      toast.error("Ingresa los ingresos mensuales");
      return;
    }
    
    // Base de cálculo (ingresos - otras pensiones)
    const baseCalculo = ingresos - otrasPensiones;
    
    // Porcentajes según Código de Familia de Panamá
    // Art. 377: Entre 25% y 35% de los ingresos
    let porcentajeBase = 0;
    if (numHijos === 1) {
      porcentajeBase = 0.25; // 25%
    } else if (numHijos === 2) {
      porcentajeBase = 0.35; // 35%
    } else if (numHijos >= 3) {
      porcentajeBase = 0.40; // 40% (máximo legal)
    }
    
    // Ajuste por edad
    if (pensionData.edadHijos === "mayores_estudiando") {
      porcentajeBase += 0.05; // +5% si son mayores estudiando
    }
    
    const pensionMensual = baseCalculo * porcentajeBase;
    const pensionPorHijo = pensionMensual / numHijos;
    
    setPensionResult({
      baseCalculo: baseCalculo.toFixed(2),
      porcentaje: (porcentajeBase * 100).toFixed(0),
      pensionTotal: pensionMensual.toFixed(2),
      pensionPorHijo: pensionPorHijo.toFixed(2),
      pensionAnual: (pensionMensual * 12).toFixed(2)
    });
  };

  // Calcular plazos procesales
  const calcularPlazos = () => {
    if (!plazosData.fechaNotificacion) {
      toast.error("Selecciona la fecha de notificación");
      return;
    }
    
    const fechaNotif = new Date(plazosData.fechaNotificacion);
    
    // Plazos según área y tipo (Código Judicial de Panamá)
    const plazos = {
      civil: {
        apelacion: { dias: 5, tipo: "hábiles", nombre: "Apelación (Art. 1131)" },
        contestacion: { dias: 10, tipo: "hábiles", nombre: "Contestación demanda (Art. 1232)" },
        recurso: { dias: 3, tipo: "hábiles", nombre: "Recurso de reconsideración" },
        prescripcion: { dias: 365 * 5, tipo: "calendario", nombre: "Prescripción ordinaria (5 años)" }
      },
      penal: {
        apelacion: { dias: 5, tipo: "hábiles", nombre: "Apelación en proceso penal" },
        contestacion: { dias: 10, tipo: "hábiles", nombre: "Descargos" },
        recurso: { dias: 3, tipo: "hábiles", nombre: "Reposición" },
        prescripcion: { dias: 365 * 10, tipo: "calendario", nombre: "Prescripción (varía según delito)" }
      },
      laboral: {
        apelacion: { dias: 5, tipo: "hábiles", nombre: "Apelación laboral" },
        contestacion: { dias: 15, tipo: "hábiles", nombre: "Contestación demanda laboral" },
        recurso: { dias: 2, tipo: "hábiles", nombre: "Recurso de reconsideración" },
        prescripcion: { dias: 365, tipo: "calendario", nombre: "Prescripción laboral (1 año)" }
      },
      familia: {
        apelacion: { dias: 5, tipo: "hábiles", nombre: "Apelación en familia" },
        contestacion: { dias: 5, tipo: "hábiles", nombre: "Contestación" },
        recurso: { dias: 3, tipo: "hábiles", nombre: "Recurso" },
        prescripcion: { dias: 365 * 2, tipo: "calendario", nombre: "Prescripción alimentos (2 años)" }
      }
    };
    
    const plazoInfo = plazos[plazosData.areaLegal]?.[plazosData.tipoPlazo] || plazos.civil.apelacion;
    
    // Calcular fecha de vencimiento
    let fechaVencimiento = new Date(fechaNotif);
    
    if (plazoInfo.tipo === "hábiles") {
      // Contar solo días hábiles (lunes a viernes)
      let diasContados = 0;
      while (diasContados < plazoInfo.dias) {
        fechaVencimiento.setDate(fechaVencimiento.getDate() + 1);
        const diaSemana = fechaVencimiento.getDay();
        if (diaSemana !== 0 && diaSemana !== 6) { // No domingo ni sábado
          diasContados++;
        }
      }
    } else {
      fechaVencimiento.setDate(fechaVencimiento.getDate() + plazoInfo.dias);
    }
    
    // Calcular días restantes
    const hoy = new Date();
    const diasRestantes = Math.ceil((fechaVencimiento - hoy) / (1000 * 60 * 60 * 24));
    
    setPlazosResult({
      plazoNombre: plazoInfo.nombre,
      diasPlazo: plazoInfo.dias,
      tipoDias: plazoInfo.tipo,
      fechaNotificacion: fechaNotif.toLocaleDateString('es-PA'),
      fechaVencimiento: fechaVencimiento.toLocaleDateString('es-PA'),
      diasRestantes: diasRestantes,
      vencido: diasRestantes < 0
    });
  };

  // Cargar alertas de cambios legales
  const cargarAlertas = async () => {
    setAlertsLoading(true);
    try {
      const response = await axios.post(`${API}/chat/send`, {
        message: `Dame las 5 actualizaciones legales más recientes e importantes de Panamá (2025-2026).
        
        FORMATO OBLIGATORIO (responde SOLO con este JSON):
        [
          {
            "titulo": "Título de la actualización",
            "fecha": "Fecha aproximada (ej: Marzo 2026)",
            "descripcion": "Breve descripción del cambio (máx 100 palabras)",
            "area": "Área legal afectada",
            "importancia": "alta/media/baja"
          }
        ]
        
        Incluye cambios reales en el Código Penal, Procesal Civil, Laboral, Migración, etc.`,
        legal_area: "general"
      });
      
      const text = response.data.response;
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        setAlerts(JSON.parse(jsonMatch[0]));
      }
    } catch (error) {
      toast.error("Error al cargar alertas");
    } finally {
      setAlertsLoading(false);
    }
  };

  useEffect(() => {
    cargarAlertas();
  }, []);

  return (
    <div className="min-h-screen bg-pattern" data-testid="lawyer-tools-page">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
              ← Volver
            </Button>
            <h1 className="text-xl font-bold">Herramientas Legales</h1>
          </div>
          <Briefcase className="text-amber-400" size={24} />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: "laboral", label: "Indemnización", icon: "💼" },
            { id: "pension", label: "Pensión", icon: "👨‍👧‍👦" },
            { id: "plazos", label: "Plazos", icon: "📅" },
            { id: "alertas", label: "Alertas", icon: "🔔" }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                activeTab === tab.id
                  ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                  : "bg-slate-800/50 text-slate-400 hover:bg-slate-800"
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* CALCULADORA LABORAL */}
        {activeTab === "laboral" && (
          <Card className="glass border-slate-800">
            <CardHeader>
              <CardTitle className="text-amber-400">💼 Calculadora de Indemnización Laboral</CardTitle>
              <CardDescription>Calcula preaviso, antigüedad, vacaciones y XIII mes según el Código de Trabajo de Panamá</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Salario Mensual (B/.)</label>
                  <Input
                    type="number"
                    placeholder="Ej: 1500"
                    value={laboralData.salarioMensual}
                    onChange={(e) => setLaboralData({...laboralData, salarioMensual: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Motivo de terminación</label>
                  <select 
                    value={laboralData.motivoDespido} 
                    onChange={(e) => setLaboralData({...laboralData, motivoDespido: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                  >
                    <option value="injustificado">Despido Injustificado</option>
                    <option value="justificado">Despido Justificado</option>
                    <option value="renuncia">Renuncia Voluntaria</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Fecha de Inicio</label>
                  <Input
                    type="date"
                    value={laboralData.fechaInicio}
                    onChange={(e) => setLaboralData({...laboralData, fechaInicio: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Fecha de Terminación</label>
                  <Input
                    type="date"
                    value={laboralData.fechaFin}
                    onChange={(e) => setLaboralData({...laboralData, fechaFin: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Días de vacaciones pendientes</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={laboralData.vacacionesPendientes}
                    onChange={(e) => setLaboralData({...laboralData, vacacionesPendientes: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
              
              <Button onClick={calcularLaboral} className="w-full bg-amber-600 hover:bg-amber-500">
                Calcular Indemnización
              </Button>
              
              {laboralResult && (
                <div className="mt-6 p-4 bg-amber-500/10 rounded-xl border border-amber-500/30">
                  <h4 className="text-amber-400 font-medium mb-4">Resultado del Cálculo:</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="text-slate-400">Tiempo trabajado:</div>
                    <div className="text-white">{laboralResult.añosTrabajados} años</div>
                    
                    <div className="text-slate-400">Salario diario:</div>
                    <div className="text-white">B/. {laboralResult.salarioDiario}</div>
                    
                    <div className="col-span-2 border-t border-slate-700 my-2"></div>
                    
                    <div className="text-slate-400">Preaviso:</div>
                    <div className="text-white">B/. {laboralResult.preaviso}</div>
                    
                    <div className="text-slate-400">Indemnización antigüedad:</div>
                    <div className="text-white">B/. {laboralResult.antiguedad}</div>
                    
                    <div className="text-slate-400">Prima de antigüedad:</div>
                    <div className="text-white">B/. {laboralResult.primaAntiguedad}</div>
                    
                    <div className="text-slate-400">Vacaciones proporcionales:</div>
                    <div className="text-white">B/. {laboralResult.vacaciones}</div>
                    
                    <div className="text-slate-400">XIII mes proporcional:</div>
                    <div className="text-white">B/. {laboralResult.decimoTercer}</div>
                    
                    <div className="col-span-2 border-t border-slate-700 my-2"></div>
                    
                    <div className="text-lg text-amber-400 font-bold">TOTAL:</div>
                    <div className="text-lg text-amber-400 font-bold">B/. {laboralResult.total}</div>
                  </div>
                  <p className="text-xs text-slate-500 mt-4">* Cálculo basado en el Código de Trabajo de Panamá. Consulte con un abogado para casos específicos.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* CALCULADORA PENSIÓN ALIMENTICIA */}
        {activeTab === "pension" && (
          <Card className="glass border-slate-800">
            <CardHeader>
              <CardTitle className="text-pink-400">Calculadora de Pensión Alimenticia</CardTitle>
              <CardDescription>Calcula la pensión según el Código de Familia de Panamá (Art. 377)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Ingresos Mensuales del Obligado (B/.)</label>
                  <Input
                    type="number"
                    placeholder="Ej: 2000"
                    value={pensionData.ingresosMensuales}
                    onChange={(e) => setPensionData({...pensionData, ingresosMensuales: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Número de Hijos</label>
                  <select 
                    value={pensionData.numHijos} 
                    onChange={(e) => setPensionData({...pensionData, numHijos: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                  >
                    <option value="1">1 hijo</option>
                    <option value="2">2 hijos</option>
                    <option value="3">3 hijos</option>
                    <option value="4">4 o más hijos</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Edad de los hijos</label>
                  <select 
                    value={pensionData.edadHijos} 
                    onChange={(e) => setPensionData({...pensionData, edadHijos: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                  >
                    <option value="menores">Menores de edad</option>
                    <option value="mayores_estudiando">Mayores de edad estudiando</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Otras pensiones que paga (B/.)</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={pensionData.otrasPensiones}
                    onChange={(e) => setPensionData({...pensionData, otrasPensiones: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
              
              <Button onClick={calcularPension} className="w-full bg-pink-600 hover:bg-pink-500">
                Calcular Pensión
              </Button>
              
              {pensionResult && (
                <div className="mt-6 p-4 bg-pink-500/10 rounded-xl border border-pink-500/30">
                  <h4 className="text-pink-400 font-medium mb-4">Resultado del Cálculo:</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="text-slate-400">Base de cálculo:</div>
                    <div className="text-white">B/. {pensionResult.baseCalculo}</div>
                    
                    <div className="text-slate-400">Porcentaje aplicado:</div>
                    <div className="text-white">{pensionResult.porcentaje}%</div>
                    
                    <div className="col-span-2 border-t border-slate-700 my-2"></div>
                    
                    <div className="text-lg text-pink-400 font-bold">Pensión Mensual Total:</div>
                    <div className="text-lg text-pink-400 font-bold">B/. {pensionResult.pensionTotal}</div>
                    
                    <div className="text-slate-400">Por cada hijo:</div>
                    <div className="text-white">B/. {pensionResult.pensionPorHijo}</div>
                    
                    <div className="text-slate-400">Total Anual:</div>
                    <div className="text-white">B/. {pensionResult.pensionAnual}</div>
                  </div>
                  <p className="text-xs text-slate-500 mt-4">* Basado en el Código de Familia de Panamá. El juez puede ajustar según circunstancias específicas.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* CALCULADORA DE PLAZOS */}
        {activeTab === "plazos" && (
          <Card className="glass border-slate-800">
            <CardHeader>
              <CardTitle className="text-cyan-400">📅 Calculadora de Plazos Procesales</CardTitle>
              <CardDescription>Calcula fechas de vencimiento según el Código Judicial de Panamá</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Fecha de Notificación</label>
                  <Input
                    type="date"
                    value={plazosData.fechaNotificacion}
                    onChange={(e) => setPlazosData({...plazosData, fechaNotificacion: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Área Legal</label>
                  <select 
                    value={plazosData.areaLegal} 
                    onChange={(e) => setPlazosData({...plazosData, areaLegal: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                  >
                    <option value="civil">Civil</option>
                    <option value="penal">Penal</option>
                    <option value="laboral">Laboral</option>
                    <option value="familia">Familia</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-slate-400 mb-1 block">Tipo de Plazo</label>
                  <select 
                    value={plazosData.tipoPlazo} 
                    onChange={(e) => setPlazosData({...plazosData, tipoPlazo: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                  >
                    <option value="apelacion">Apelación</option>
                    <option value="contestacion">Contestación de demanda</option>
                    <option value="recurso">Recurso de reconsideración</option>
                    <option value="prescripcion">Prescripción</option>
                  </select>
                </div>
              </div>
              
              <Button onClick={calcularPlazos} className="w-full bg-cyan-600 hover:bg-cyan-500">
                Calcular Vencimiento
              </Button>
              
              {plazosResult && (
                <div className={`mt-6 p-4 rounded-xl border ${plazosResult.vencido ? 'bg-red-500/10 border-red-500/30' : 'bg-cyan-500/10 border-cyan-500/30'}`}>
                  <h4 className={`font-medium mb-4 ${plazosResult.vencido ? 'text-red-400' : 'text-cyan-400'}`}>
                    {plazosResult.plazoNombre}
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="text-slate-400">Plazo:</div>
                    <div className="text-white">{plazosResult.diasPlazo} días {plazosResult.tipoDias}</div>
                    
                    <div className="text-slate-400">Fecha notificación:</div>
                    <div className="text-white">{plazosResult.fechaNotificacion}</div>
                    
                    <div className="col-span-2 border-t border-slate-700 my-2"></div>
                    
                    <div className="text-lg font-bold text-slate-300">Fecha de Vencimiento:</div>
                    <div className={`text-lg font-bold ${plazosResult.vencido ? 'text-red-400' : 'text-cyan-400'}`}>
                      {plazosResult.fechaVencimiento}
                    </div>
                    
                    <div className="text-slate-400">Estado:</div>
                    <div className={`font-medium ${plazosResult.vencido ? 'text-red-400' : plazosResult.diasRestantes <= 2 ? 'text-amber-400' : 'text-green-400'}`}>
                      {plazosResult.vencido 
                        ? `¡VENCIDO hace ${Math.abs(plazosResult.diasRestantes)} días!` 
                        : plazosResult.diasRestantes <= 2 
                          ? `⚠️ ¡Urgente! ${plazosResult.diasRestantes} días restantes`
                          : `${plazosResult.diasRestantes} días restantes`}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ALERTAS DE CAMBIOS LEGALES */}
        {activeTab === "alertas" && (
          <Card className="glass border-slate-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-purple-400">🔔 Alertas de Cambios Legales</CardTitle>
                  <CardDescription>Mantente actualizado con las últimas modificaciones legales en Panamá</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={cargarAlertas} disabled={alertsLoading} className="text-purple-400">
                  {alertsLoading ? "Cargando..." : "Actualizar"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {alertsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-400 border-t-transparent mx-auto mb-4"></div>
                  <p className="text-slate-400">Cargando alertas...</p>
                </div>
              ) : alerts.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <p>No hay alertas disponibles</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {alerts.map((alert, idx) => (
                    <div key={idx} className={`p-4 rounded-xl border ${
                      alert.importancia === 'alta' ? 'bg-red-500/10 border-red-500/30' :
                      alert.importancia === 'media' ? 'bg-amber-500/10 border-amber-500/30' :
                      'bg-slate-800/50 border-slate-700'
                    }`}>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-white">{alert.titulo}</h4>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          alert.importancia === 'alta' ? 'bg-red-500/20 text-red-400' :
                          alert.importancia === 'media' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-slate-700 text-slate-400'
                        }`}>
                          {alert.importancia}
                        </span>
                      </div>
                      <p className="text-sm text-slate-300 mb-2">{alert.descripcion}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>📅 {alert.fecha}</span>
                        <span>📂 {alert.area}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

// Documents Page
const DocumentsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [correctedDocs, setCorrectedDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("generated");
  
  // Estados para corrección de documentos
  const [showCorrectModal, setShowCorrectModal] = useState(false);
  const [correctingFile, setCorrectingFile] = useState(null);
  const [correctionType, setCorrectionType] = useState("general");
  const [legalArea, setLegalArea] = useState("general");
  const [isCorrectLoading, setIsCorrectLoading] = useState(false);

  useEffect(() => {
    fetchDocuments();
    fetchCorrectedDocs();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`);
      setDocuments(response.data);
    } catch (error) {
      toast.error("Error al cargar documentos");
    } finally {
      setLoading(false);
    }
  };

  const fetchCorrectedDocs = async () => {
    try {
      const response = await axios.get(`${API}/documents/corrected`);
      setCorrectedDocs(response.data);
    } catch (error) {
      console.error("Error fetching corrected docs:", error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['doc', 'docx', 'pdf', 'txt'].includes(ext)) {
        toast.error("Solo se permiten archivos Word, PDF o TXT");
        return;
      }
      setCorrectingFile(file);
    }
  };

  const handleCorrectDocument = async () => {
    if (!correctingFile) {
      toast.error("Selecciona un archivo primero");
      return;
    }

    setIsCorrectLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', correctingFile);
      formData.append('correction_type', correctionType);
      formData.append('legal_area', legalArea);

      const response = await axios.post(`${API}/documents/correct`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success("¡Documento corregido exitosamente!");
      
      // Descargar automáticamente
      const downloadResponse = await axios.get(
        `${API}/documents/correct/${response.data.id}/download`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([downloadResponse.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      URL.revokeObjectURL(url);

      setShowCorrectModal(false);
      setCorrectingFile(null);
      fetchCorrectedDocs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al corregir documento");
    } finally {
      setIsCorrectLoading(false);
    }
  };

  const downloadCorrectedDoc = async (doc) => {
    try {
      const response = await axios.get(
        `${API}/documents/correct/${doc.id}/download`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.corrected_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Error al descargar documento");
    }
  };

  const correctionTypes = [
    { id: "general", name: "Corrección Completa", desc: "Ortografía, gramática, formato y referencias legales" },
    { id: "legal", name: "Solo Legal", desc: "Referencias a leyes, artículos y tribunales" },
    { id: "grammar", name: "Solo Gramática", desc: "Ortografía y gramática únicamente" },
    { id: "format", name: "Solo Formato", desc: "Mejora el formato profesional" }
  ];

  return (
    <div className="min-h-screen bg-pattern" data-testid="documents-page">
      <header className="glass sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
              ← Volver
            </Button>
            <h1 className="text-xl font-bold">Mis Documentos</h1>
          </div>
          <Button 
            onClick={() => setShowCorrectModal(true)}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500"
          >
            <Upload size={16} className="mr-2" />
            Corregir Documento
          </Button>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("generated")}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeTab === "generated" 
                ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" 
                : "text-slate-400 hover:bg-slate-800"
            }`}
          >
            Documentos Generados ({documents.length})
          </button>
          <button
            onClick={() => setActiveTab("corrected")}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeTab === "corrected" 
                ? "bg-green-500/20 text-green-400 border border-green-500/30" 
                : "text-slate-400 hover:bg-slate-800"
            }`}
          >
            Documentos Corregidos ({correctedDocs.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="loading-dots flex justify-center gap-1">
              <span className="w-3 h-3 bg-cyan-400 rounded-full"></span>
              <span className="w-3 h-3 bg-cyan-400 rounded-full"></span>
              <span className="w-3 h-3 bg-cyan-400 rounded-full"></span>
            </div>
          </div>
        ) : activeTab === "generated" ? (
          documents.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="mx-auto mb-4 text-slate-600" size={48} />
              <p className="text-slate-400">No hay documentos generados aún</p>
              <Button onClick={() => navigate("/dashboard")} className="mt-4 btn-primary">
                Generar Primer Documento
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {documents.map((doc) => (
                <Card key={doc.id} className="glass border-slate-800">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg capitalize">{doc.doc_type}</CardTitle>
                        <CardDescription>{doc.legal_area} • {new Date(doc.created_at).toLocaleDateString()}</CardDescription>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-cyan-500/30 text-cyan-400"
                        onClick={() => {
                          const blob = new Blob([doc.content], { type: "text/plain" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `${doc.doc_type}_${doc.id}.txt`;
                          a.click();
                        }}
                      >
                        <Download size={14} className="mr-1" />
                        Descargar
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-400 line-clamp-3">{doc.content.substring(0, 300)}...</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        ) : (
          correctedDocs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto mb-4 text-slate-600" size={48} />
              <p className="text-slate-400">No hay documentos corregidos aún</p>
              <Button onClick={() => setShowCorrectModal(true)} className="mt-4 bg-green-600 hover:bg-green-500">
                <Upload size={16} className="mr-2" />
                Corregir Primer Documento
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {correctedDocs.map((doc) => (
                <Card key={doc.id} className="glass border-green-800/30">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg text-green-400">{doc.corrected_filename}</CardTitle>
                        <CardDescription>
                          Original: {doc.original_filename} • {doc.correction_type} • {new Date(doc.created_at).toLocaleDateString()}
                        </CardDescription>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-green-500/30 text-green-400"
                        onClick={() => downloadCorrectedDoc(doc)}
                      >
                        <Download size={14} className="mr-1" />
                        Descargar
                      </Button>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )
        )}
      </main>

      {/* Modal para corregir documento */}
      <Dialog open={showCorrectModal} onOpenChange={setShowCorrectModal}>
        <DialogContent className="glass border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-green-400">Corregir Documento</DialogTitle>
            <DialogDescription>
              Sube un documento Word o PDF y LILA lo corregirá automáticamente
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Selector de archivo */}
            <div className="space-y-2">
              <label className="text-sm text-slate-300">Documento a corregir</label>
              <div className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center hover:border-green-500/50 transition-colors">
                <input
                  type="file"
                  accept=".doc,.docx,.pdf,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="correct-file-input"
                />
                <label htmlFor="correct-file-input" className="cursor-pointer">
                  {correctingFile ? (
                    <div className="text-green-400">
                      <FileText className="mx-auto mb-2" size={32} />
                      <p className="font-medium">{correctingFile.name}</p>
                      <p className="text-xs text-slate-400 mt-1">Clic para cambiar</p>
                    </div>
                  ) : (
                    <div className="text-slate-400">
                      <Upload className="mx-auto mb-2" size={32} />
                      <p>Arrastra o haz clic para subir</p>
                      <p className="text-xs mt-1">Word, PDF o TXT</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Tipo de corrección */}
            <div className="space-y-2">
              <label className="text-sm text-slate-300">Tipo de corrección</label>
              <div className="grid grid-cols-2 gap-2">
                {correctionTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setCorrectionType(type.id)}
                    className={`p-3 rounded-lg text-left transition-colors ${
                      correctionType === type.id
                        ? "bg-green-500/20 border border-green-500/50"
                        : "bg-slate-800/50 border border-slate-700 hover:border-slate-600"
                    }`}
                  >
                    <p className={`text-sm font-medium ${correctionType === type.id ? "text-green-400" : "text-white"}`}>
                      {type.name}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{type.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Área legal */}
            <div className="space-y-2">
              <label className="text-sm text-slate-300">Área legal (opcional)</label>
              <Select value={legalArea} onValueChange={setLegalArea}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="familia">Familia</SelectItem>
                  <SelectItem value="civil">Civil</SelectItem>
                  <SelectItem value="penal">Penal</SelectItem>
                  <SelectItem value="laboral">Laboral</SelectItem>
                  <SelectItem value="mercantil">Mercantil</SelectItem>
                  <SelectItem value="migratorio">Migratorio</SelectItem>
                  <SelectItem value="maritimo">Marítimo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => { setShowCorrectModal(false); setCorrectingFile(null); }}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCorrectDocument}
              disabled={!correctingFile || isCorrectLoading}
              className="flex-1 bg-green-600 hover:bg-green-500"
            >
              {isCorrectLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Corrigiendo...
                </>
              ) : (
                <>
                  <FileText size={16} className="mr-2" />
                  Corregir Documento
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Cases Page
const CasesPage = () => {
  const navigate = useNavigate();
  const [description, setDescription] = useState("");
  const [legalArea, setLegalArea] = useState("civil");
  const [perspective, setPerspective] = useState("abogado");
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const perspectives = [
    { 
      id: "juez", 
      name: "Como Juez", 
      icon: Gavel,
      description: "¿Cómo vería un juez este caso? ¿Qué decidiría?",
      color: "purple"
    },
    { 
      id: "fiscal", 
      name: "Como Fiscal", 
      icon: Shield,
      description: "¿Qué argumentos usaría el fiscal? ¿Qué pruebas buscaría?",
      color: "cyan"
    },
    { 
      id: "abogado", 
      name: "Como Abogado", 
      icon: Briefcase,
      description: "¿Cuál es la mejor estrategia de defensa/ataque?",
      color: "emerald"
    }
  ];

  // Estados para archivos
  const [files, setFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length + files.length > 5) {
      toast.error("Máximo 5 archivos permitidos");
      return;
    }
    setFiles([...files, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const analyzeCase = async () => {
    if (!description.trim()) {
      toast.error("Por favor, describe el caso");
      return;
    }

    setLoading(true);
    try {
      // Si hay archivos, usar FormData
      if (files.length > 0) {
        const formData = new FormData();
        formData.append('description', description);
        formData.append('legal_area', legalArea);
        formData.append('perspective', perspective);
        files.forEach((file) => {
          formData.append('files', file);
        });
        
        const response = await axios.post(`${API}/cases/analyze-with-files`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setAnalysis(response.data);
      } else {
        const response = await axios.post(`${API}/cases/analyze`, {
          description,
          legal_area: legalArea,
          perspective: perspective
        });
        setAnalysis(response.data);
      }
      toast.success("Caso analizado");
    } catch (error) {
      toast.error("Error al analizar caso");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-pattern" data-testid="cases-page">
      <header className="glass sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
              ← Volver
            </Button>
            <h1 className="text-xl font-bold">Análisis de Casos</h1>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="glass border-slate-800">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Scale className="text-purple-400" size={28} />
              <div>
                <CardTitle className="gradient-text">Analizar Caso</CardTitle>
                <CardDescription>
                  {legalArea === "civil" ? "Derecho Civil" : 
                   legalArea === "penal" ? "Derecho Penal" :
                   legalArea === "laboral" ? "Derecho Laboral" :
                   legalArea === "familia" ? "Derecho de Familia" :
                   legalArea === "mercantil" ? "Derecho Mercantil" :
                   "Derecho Constitucional"} - Perspectiva Legal
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Área Legal */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Área Legal</label>
              <Select value={legalArea} onValueChange={setLegalArea}>
                <SelectTrigger data-testid="case-area-select" className="bg-slate-900/50 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-white z-[100]">
                  <SelectItem value="familia" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Derecho de Familia</SelectItem>
                  <SelectItem value="civil" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Derecho Civil</SelectItem>
                  <SelectItem value="laboral" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Derecho Laboral</SelectItem>
                  <SelectItem value="penal" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Derecho Penal</SelectItem>
                  <SelectItem value="mercantil" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Derecho Mercantil</SelectItem>
                  <SelectItem value="constitucional" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Derecho Constitucional</SelectItem>
                  <SelectItem value="migratorio" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Derecho Migratorio</SelectItem>
                  <SelectItem value="maritimo" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Derecho Marítimo</SelectItem>
                  <SelectItem value="administrativo" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Derecho Administrativo</SelectItem>
                  <SelectItem value="tributario" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Derecho Tributario</SelectItem>
                  <SelectItem value="ambiental" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Derecho Ambiental</SelectItem>
                  <SelectItem value="agrario" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Derecho Agrario</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Perspectivas */}
            <div>
              <label className="text-sm text-slate-400 mb-3 block">¿Desde qué perspectiva analizar?</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {perspectives.map((p) => {
                  const Icon = p.icon;
                  const isSelected = perspective === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setPerspective(p.id);
                      }}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        isSelected 
                          ? p.color === "purple" 
                            ? "border-purple-500 bg-purple-500/10" 
                            : p.color === "cyan"
                            ? "border-cyan-500 bg-cyan-500/10"
                            : "border-emerald-500 bg-emerald-500/10"
                          : "border-slate-700 hover:border-slate-600 bg-slate-900/30"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Icon size={20} className={
                          isSelected 
                            ? p.color === "purple" ? "text-purple-400" 
                              : p.color === "cyan" ? "text-cyan-400" 
                              : "text-emerald-400"
                            : "text-slate-400"
                        } />
                        <span className={`font-semibold ${
                          isSelected 
                            ? p.color === "purple" ? "text-purple-400" 
                              : p.color === "cyan" ? "text-cyan-400" 
                              : "text-emerald-400"
                            : "text-white"
                        }`}>{p.name}</span>
                      </div>
                      <p className="text-xs text-slate-400">{p.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Descripción del caso */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Describe el caso *</label>
              <Textarea
                data-testid="case-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describa los hechos del caso, las partes involucradas, las pruebas disponibles, y cualquier información relevante..."
                className="bg-slate-900/50 border-slate-700 min-h-[150px]"
              />
            </div>

            {/* Subir archivos */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                Adjuntar documentos o fotos (opcional)
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-700 hover:border-purple-500/50 rounded-xl p-4 text-center cursor-pointer transition-colors"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.webp"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload className="mx-auto mb-2 text-slate-500" size={24} />
                <p className="text-sm text-slate-400">
                  Haz clic para subir archivos
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  PDF, Word, imágenes (máx. 5 archivos)
                </p>
              </div>
              
              {/* Lista de archivos seleccionados */}
              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-2 border border-slate-700">
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-purple-400" />
                        <span className="text-sm text-slate-300 truncate max-w-[200px]">
                          {file.name}
                        </span>
                        <span className="text-xs text-slate-500">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                        className="text-red-400 hover:text-red-300 p-1"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <Button
              data-testid="analyze-btn"
              onClick={analyzeCase}
              disabled={loading}
              className="w-full btn-primary py-6 text-lg"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Analizando...
                </>
              ) : (
                <>
                  <Scale size={20} className="mr-2" />
                  Analizar Caso
                </>
              )}
            </Button>
            
            {analysis && (
              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-2">
                  <Scale className="text-cyan-400" size={20} />
                  <h3 className="font-semibold text-cyan-400">
                    Análisis {perspective === "juez" ? "del Juez" : perspective === "fiscal" ? "del Fiscal" : "del Abogado"}:
                  </h3>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                  <pre className="whitespace-pre-wrap text-sm text-slate-300">
                    {analysis.analysis}
                  </pre>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-pattern flex items-center justify-center">
        <div className="text-center">
          <ScaleAvatar size={80} />
          <p className="mt-4 text-slate-400">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// Premium Page
const PremiumPage = () => {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [referralSource, setReferralSource] = useState("");
  const [referrerName, setReferrerName] = useState("");
  const [referrerCode, setReferrerCode] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("trial");
  const [showPayPal, setShowPayPal] = useState(false);
  const [showReferralStep, setShowReferralStep] = useState(false);
  const paypalRef = React.useRef(null);
  
  // Auth form states
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const sources = [
    { id: "redes", label: "Redes Sociales (Facebook, Instagram, etc.)", icon: Globe },
    { id: "colega", label: "Un colega abogado me lo recomendó", icon: Users, hasReferrer: true },
    { id: "vendedor", label: "Un vendedor/representante", icon: Briefcase, hasReferrer: true },
    { id: "google", label: "Búsqueda en Google", icon: Search },
    { id: "evento", label: "Evento o conferencia legal", icon: BookOpen },
    { id: "otro", label: "Otro", icon: HelpCircle }
  ];

  const plans = [
    { 
      id: "trial", 
      name: "Prueba Gratis", 
      price: 0, 
      period: "5 días",
      badge: "¡Sin tarjeta!",
      isTrial: true,
      features: ["Acceso completo 5 días", "Consultas ilimitadas", "Generación de documentos", "Análisis de casos", "Voz de Lila"],
      highlight: true
    },
    { 
      id: "mensual", 
      name: "Plan Mensual", 
      originalPrice: 14.99,
      price: 7.99, 
      period: "/mes",
      badge: "Más Popular",
      paypalPlanId: "P-0AC072068B3250056NGXUEEQ",
      features: ["Consultas ilimitadas", "Generación de documentos", "Análisis de casos", "Voz de Lila", "Calculadoras legales"],
      popular: true
    },
    { 
      id: "trimestral", 
      name: "Plan Trimestral", 
      originalPrice: 23.97,
      price: 19.99, 
      period: "/3 meses",
      badge: "Ahorra 17%",
      paypalPlanId: "P-8L939158P68672731NHHRMLI",
      features: ["Todo lo del plan mensual", "Ahorra $4", "3 meses de acceso", "Soporte prioritario"]
    },
    { 
      id: "anual", 
      name: "Plan Anual", 
      originalPrice: 95.88,
      price: 49.99, 
      period: "/año",
      badge: "Mejor Valor",
      paypalPlanId: "P-4HR74631PT8493901NGXUHMQ",
      features: ["Todo lo del plan mensual", "Ahorra $46", "Soporte prioritario", "Alertas legales exclusivas"]
    }
  ];

  const [activatingTrial, setActivatingTrial] = useState(false);

  const activateTrial = async () => {
    setActivatingTrial(true);
    try {
      await axios.post(`${API}/subscription/trial`, {
        referral_source: referralSource,
        referrer_name: referrerName,
        referrer_code: referrerCode
      });
      toast.success("¡Prueba gratuita activada! Tienes 5 días de acceso completo.");
      navigate('/dashboard');
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al activar prueba";
      toast.error(msg);
    } finally {
      setActivatingTrial(false);
    }
  };

  const saveSubscriptionWithReferral = async (subscriptionId, plan) => {
    try {
      await axios.post(`${API}/subscription/complete`, {
        subscription_id: subscriptionId,
        plan: plan,
        referral_source: referralSource,
        referrer_name: referrerName,
        referrer_code: referrerCode
      });
    } catch (error) {
      console.error("Error saving subscription:", error);
    }
  };

  // Load PayPal script
  useEffect(() => {
    if (showPayPal && !document.getElementById('paypal-sdk')) {
      const script = document.createElement('script');
      script.id = 'paypal-sdk';
      script.src = 'https://www.paypal.com/sdk/js?client-id=AU_pKUf0XBny6iKqJ4LOiVRuSioiB6XlHE37e9v0MldoOThNjosmyD4ZQb0daQMJt4tqeAcmkRZ2haUb&vault=true&intent=subscription';
      script.setAttribute('data-sdk-integration-source', 'button-factory');
      script.onload = () => renderPayPalButton();
      document.body.appendChild(script);
    } else if (showPayPal && window.paypal) {
      // Pequeño delay para asegurar que el DOM está listo
      setTimeout(() => renderPayPalButton(), 100);
    }
  }, [showPayPal, selectedPlan]);

  const renderPayPalButton = () => {
    if (paypalRef.current && window.paypal) {
      // Limpiar contenido anterior de forma segura
      while (paypalRef.current.firstChild) {
        paypalRef.current.removeChild(paypalRef.current.firstChild);
      }
      
      const selectedPlanData = plans.find(p => p.id === selectedPlan);
      
      window.paypal.Buttons({
        style: {
          shape: 'rect',
          color: 'silver',
          layout: 'vertical',
          label: 'subscribe'
        },
        createSubscription: function(data, actions) {
          return actions.subscription.create({
            plan_id: selectedPlanData.paypalPlanId
          });
        },
        onApprove: function(data, actions) {
          toast.success('¡Suscripción exitosa! ID: ' + data.subscriptionID);
          // Guardar la suscripción con referral en la base de datos
          saveSubscriptionWithReferral(data.subscriptionID, selectedPlan);
          navigate('/dashboard');
        },
        onError: function(err) {
          toast.error('Error en el pago');
          console.error(err);
        }
      }).render(paypalRef.current);
    }
  };

  const handleSubscribe = () => {
    const selectedPlanData = plans.find(p => p.id === selectedPlan);
    
    // Si no está logueado, mostrar formulario de auth
    if (!user) {
      setShowAuthForm(true);
      return;
    }
    
    // Validar que si seleccionó vendedor/colega, tenga el nombre
    if ((referralSource === "vendedor" || referralSource === "colega") && !referrerName) {
      toast.error("Por favor ingresa el nombre del vendedor o colega");
      return;
    }
    
    if (selectedPlanData?.isTrial) {
      activateTrial();
    } else {
      setShowPayPal(true);
    }
  };

  // Manejar registro/login y activar trial automáticamente
  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, fullName);
      }
      toast.success(isLogin ? "¡Bienvenido!" : "¡Cuenta creada!");
      setShowAuthForm(false);
      
      // Activar trial automáticamente después de auth
      const selectedPlanData = plans.find(p => p.id === selectedPlan);
      if (selectedPlanData?.isTrial) {
        // Pequeña espera para que se actualice el token
        setTimeout(() => activateTrial(), 500);
      } else {
        setShowPayPal(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error en la autenticación");
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="premium-page">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
            ← Volver
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 pb-32">
        {/* Header Premium */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Crown className="text-white" size={40} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">LILA Premium</h1>
          <p className="text-slate-400">
            Desbloquea todo el potencial de tu asistente legal. Pago único, sin suscripciones recurrentes.
          </p>
        </div>

        {/* Referral Source */}
        <Card className="glass border-slate-800 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <MessageSquare className="text-purple-400" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-white">¿Cómo supiste de nosotros?</h3>
                <p className="text-sm text-slate-400">Cuéntanos cómo llegaste a LILA</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              {sources.map((source) => {
                const IconComponent = source.icon;
                return (
                  <button
                    key={source.id}
                    onClick={() => setReferralSource(source.id)}
                    className={`p-4 rounded-xl border transition-all flex flex-col items-center gap-2 ${
                      referralSource === source.id 
                        ? "border-purple-500 bg-purple-500/10 text-purple-400" 
                        : "border-slate-700 bg-slate-900/50 text-slate-400 hover:border-slate-600"
                    }`}
                  >
                    <IconComponent size={24} />
                    <span className="text-sm text-center">{source.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Referral form - siempre renderizado */}
            <div className={`space-y-4 mt-4 p-4 bg-purple-500/10 rounded-xl border border-purple-500/30 transition-all ${
              (referralSource === "colega" || referralSource === "vendedor") ? 'block' : 'hidden'
            }`}>
              <div>
                <label className="text-sm text-purple-300 mb-1 block">
                  {referralSource === "vendedor" ? "Nombre del vendedor *" : "Nombre del colega *"}
                </label>
                <Input
                  value={referrerName}
                  onChange={(e) => setReferrerName(e.target.value)}
                  placeholder={referralSource === "vendedor" ? "Ej: Juan Pérez" : "Ej: Licdo. María González"}
                  className="bg-slate-900/50 border-slate-700"
                />
              </div>
              <div>
                <label className="text-sm text-purple-300 mb-1 block">
                  Código de referido (si tiene)
                </label>
                <Input
                  value={referrerCode}
                  onChange={(e) => setReferrerCode(e.target.value.toUpperCase())}
                  placeholder="Ej: REF-001"
                  className="bg-slate-900/50 border-slate-700 uppercase"
                />
              </div>
              <p className="text-xs text-purple-400">
                💰 Si tienes un código válido, tu referidor recibirá una comisión
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Plans */}
        <div className="space-y-4">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`cursor-pointer transition-all ${
                selectedPlan === plan.id 
                  ? "border-cyan-500 bg-cyan-500/5" 
                  : "glass border-slate-800 hover:border-slate-700"
              } ${plan.popular ? "ring-2 ring-purple-500/50" : ""} ${plan.highlight ? "ring-2 ring-green-500/50 bg-green-500/5" : ""}`}
              onClick={() => setSelectedPlan(plan.id)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-lg text-white">{plan.name}</h3>
                      <span className={`text-xs bg-purple-500 text-white px-2 py-0.5 rounded-full ${plan.popular ? 'inline' : 'hidden'}`}>
                        Popular
                      </span>
                      <span className={`text-xs bg-green-500 text-white px-2 py-0.5 rounded-full animate-pulse ${plan.highlight ? 'inline' : 'hidden'}`}>
                        Recomendado
                      </span>
                      <span className={`text-xs ${plan.isTrial ? 'bg-green-600' : 'bg-emerald-500'} text-white px-2 py-0.5 rounded-full ${plan.badge ? 'inline' : 'hidden'}`}>
                        {plan.badge || ''}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className={`text-lg text-slate-500 line-through ${plan.originalPrice ? 'inline' : 'hidden'}`}>${plan.originalPrice || ''}</span>
                      <span className={`text-3xl font-bold ${plan.isTrial ? 'text-green-400' : 'text-cyan-400'}`}>
                        {plan.price === 0 ? 'GRATIS' : `$${plan.price}`}
                      </span>
                      <span className="text-slate-400 text-sm">{plan.period}</span>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === plan.id 
                      ? plan.isTrial ? "border-green-500 bg-green-500" : "border-cyan-500 bg-cyan-500"
                      : "border-slate-600"
                  }`}>
                    <div className={`w-2 h-2 bg-white rounded-full transition-opacity ${selectedPlan === plan.id ? 'opacity-100' : 'opacity-0'}`} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {plan.features.map((feature, idx) => (
                    <span key={idx} className={`text-xs ${plan.isTrial ? 'text-green-400 bg-green-900/30' : 'text-slate-400 bg-slate-800'} px-2 py-1 rounded`}>
                      {feature}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Subscribe Button - Fixed at bottom */}
        <div className="fixed bottom-0 left-0 right-0 p-4 glass border-t border-slate-800">
          <div className="max-w-4xl mx-auto">
            {/* Botón de suscripción - siempre renderizado */}
            <div className={`transition-all duration-200 ${showPayPal ? 'hidden' : 'block'}`}>
              <Button
                onClick={handleSubscribe}
                disabled={activatingTrial}
                className={`w-full py-6 text-lg rounded-xl ${
                  selectedPlan === 'trial' 
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500' 
                    : 'btn-primary'
                }`}
              >
                {activatingTrial ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Activando prueba...
                  </>
                ) : selectedPlan === 'trial' ? (
                  <>
                    <Star size={20} className="mr-2" />
                    Comenzar Prueba Gratis de 5 Días
                  </>
                ) : (
                  <>
                    <Crown size={20} className="mr-2" />
                    Suscribirse a Premium
                  </>
                )}
              </Button>
              <p className="text-center text-xs text-slate-500 mt-2">
                {selectedPlan === 'trial' 
                  ? "Sin tarjeta de crédito • Acceso completo por 5 días"
                  : "Pago seguro con PayPal • Cancela cuando quieras"
                }
              </p>
            </div>
            
            {/* Container de PayPal - siempre renderizado */}
            <div className={`bg-white rounded-xl p-4 transition-all duration-200 ${showPayPal ? 'block' : 'hidden'}`}>
              <p className="text-slate-800 text-sm text-center mb-3 font-medium">
                {plans.find(p => p.id === selectedPlan)?.name} - ${plans.find(p => p.id === selectedPlan)?.price}{plans.find(p => p.id === selectedPlan)?.period}
              </p>
              {showPayPal && (
                <div key={`paypal-${selectedPlan}`} ref={paypalRef} id="paypal-button-container"></div>
              )}
              <button 
                onClick={() => setShowPayPal(false)}
                className="w-full mt-3 text-slate-500 text-sm hover:text-slate-700"
              >
                ← Volver a elegir plan
              </button>
            </div>
          </div>
        </div>
      </main>
      
      {/* Auth Modal for non-logged users - siempre renderizado, controlado por clase hidden */}
      <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${showAuthForm ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/80" onClick={() => setShowAuthForm(false)} />
        <div className="relative z-50 w-full max-w-md glass border border-slate-700 rounded-2xl p-6">
          <button 
            onClick={() => setShowAuthForm(false)}
            className="absolute top-4 right-4 text-slate-400 hover:text-white"
          >
            <X size={20} />
          </button>
          
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Star className="text-white" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {isLogin ? "Inicia Sesión" : "Crea tu Cuenta"}
            </h2>
            <p className="text-slate-400 text-sm">
              {isLogin 
                ? "Ingresa para activar tu prueba gratis" 
                : "Regístrate para comenzar tu prueba gratis de 5 días"
              }
            </p>
          </div>
          
          <form onSubmit={handleAuthSubmit} className="space-y-4">
            <div className={isLogin ? 'hidden' : 'block'}>
              <label className="text-sm text-slate-400 mb-1 block">Nombre Completo</label>
              <Input
                type="text"
                placeholder="Lic. Juan Pérez"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="bg-slate-900/50 border-slate-700 h-12"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Email</label>
              <Input
                type="email"
                placeholder="abogado@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-900/50 border-slate-700 h-12"
                required
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Contraseña</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-900/50 border-slate-700 h-12 pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 h-12 text-base"
              disabled={authLoading}
            >
              {authLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {isLogin ? "Ingresando..." : "Creando cuenta..."}
                </>
              ) : (
                <>
                  <Star size={18} className="mr-2" />
                  {isLogin ? "Ingresar y Activar Prueba" : "Crear Cuenta y Comenzar Prueba"}
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-cyan-400 hover:text-cyan-300 text-sm"
            >
              {isLogin ? "¿No tienes cuenta? Regístrate" : "¿Ya tienes cuenta? Inicia sesión"}
            </button>
          </div>
          
          <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-xs text-green-400 text-center">
              ✓ Sin tarjeta de crédito • ✓ Acceso completo por 5 días • ✓ Cancela cuando quieras
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard Page
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchUser, setSearchUser] = useState("");
  const [extendingUser, setExtendingUser] = useState(null);
  const [daysToAdd, setDaysToAdd] = useState(5);
  const [actionLoading, setActionLoading] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showCRM, setShowCRM] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [dashboardRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/admin/users?limit=100`)
      ]);
      setStats(dashboardRes.data);
      setUsers(usersRes.data.users);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("No tienes permisos de administrador");
        navigate("/dashboard");
      } else {
        toast.error("Error al cargar datos");
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchUser.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(searchUser.toLowerCase())
  );

  // Función para extender trial
  const extendTrial = async (userId, days) => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/admin/extend-trial`, {
        user_id: userId,
        days: days
      });
      toast.success(response.data.message);
      setExtendingUser(null);
      setDaysToAdd(5);
      // Recargar usuarios
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al extender trial");
    } finally {
      setActionLoading(false);
    }
  };

  // Función para cambiar plan
  const setUserPlan = async (userId, plan) => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/admin/set-subscription`, {
        user_id: userId,
        plan: plan
      });
      toast.success(response.data.message);
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar plan");
    } finally {
      setActionLoading(false);
    }
  };

  // Función para resetear contraseña de usuario
  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 4) {
      toast.error("La contraseña debe tener al menos 4 caracteres");
      return;
    }
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/admin/reset-password`, {
        user_id: userId,
        new_password: newPassword
      });
      toast.success(response.data.message);
      setResetPasswordUser(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al resetear contraseña");
    } finally {
      setActionLoading(false);
    }
  };

  // Calcular días restantes
  const getDaysLeft = (subscription) => {
    if (!subscription) return null;
    
    const endDate = subscription.trial_ends_at || subscription.subscription_ends_at;
    if (!endDate) return null;
    
    try {
      const end = new Date(endDate);
      const now = new Date();
      const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
      return diff;
    } catch {
      return null;
    }
  };

  const getStatusBadge = (subscription) => {
    if (!subscription) return <span className="px-2 py-1 bg-slate-700 text-slate-300 rounded-full text-xs">Sin suscripción</span>;
    
    const plan = subscription.plan;
    const status = subscription.status;
    
    if (plan === "admin") return <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs">Admin</span>;
    if (plan === "anual" && status === "active") return <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs">Anual ✓</span>;
    if (plan === "mensual" && status === "active") return <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-xs">Mensual ✓</span>;
    if (plan === "trial" && status === "active") return <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded-full text-xs">Trial</span>;
    if (status === "expired") return <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">Expirado</span>;
    
    return <span className="px-2 py-1 bg-slate-700 text-slate-300 rounded-full text-xs">{plan}</span>;
  };

  const sourceLabels = {
    redes: "Redes Sociales",
    colega: "Colega Abogado",
    vendedor: "Vendedor",
    google: "Google",
    evento: "Evento/Conferencia",
    otro: "Otro"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-pattern flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pattern">
      <header className="glass sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
              ← Volver
            </Button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <BarChart3 className="text-purple-400" size={24} />
              Panel de Administración
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Admin:</span>
            <span className="text-sm text-purple-400">{user?.email}</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: "overview", label: "Resumen", icon: BarChart3 },
            { id: "analytics", label: "Analíticas", icon: Eye },
            { id: "encuestas", label: "Encuestas", icon: Star },
            { id: "users", label: "Usuarios", icon: Users },
            { id: "referrals", label: "Referidos", icon: Globe },
            { id: "crm", label: "CRM Ventas", icon: Target }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === tab.id 
                    ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" 
                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                }`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && stats && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="glass border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-500/20 rounded-xl">
                      <Users className="text-blue-400" size={24} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{stats.users.total}</p>
                      <p className="text-xs text-slate-400">Usuarios Totales</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-500/20 rounded-xl">
                      <Crown className="text-green-400" size={24} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{stats.subscriptions.total_paid}</p>
                      <p className="text-xs text-slate-400">Suscriptores Pagos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-amber-500/20 rounded-xl">
                      <Star className="text-amber-400" size={24} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{stats.subscriptions.trials_active}</p>
                      <p className="text-xs text-slate-400">Trials Activos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-emerald-500/20 rounded-xl">
                      <Receipt className="text-emerald-400" size={24} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">${stats.revenue.total.toFixed(2)}</p>
                      <p className="text-xs text-slate-400">Ingresos Totales</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="glass border-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-400">Registros</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Hoy</span>
                      <span className="text-white font-medium">{stats.users.today}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Últimos 30 días</span>
                      <span className="text-white font-medium">{stats.users.last_30_days}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass border-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-400">Suscripciones</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Plan Mensual</span>
                      <span className="text-cyan-400 font-medium">{stats.subscriptions.paid_monthly}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Plan Anual</span>
                      <span className="text-emerald-400 font-medium">{stats.subscriptions.paid_annual}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Trials Expirados</span>
                      <span className="text-red-400 font-medium">{stats.subscriptions.trials_expired}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass border-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-400">Comisiones</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Pendientes</span>
                      <span className="text-amber-400 font-medium">${stats.commissions.pending.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Pagadas</span>
                      <span className="text-green-400 font-medium">${stats.commissions.paid.toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <AnalyticsTab token={token} />
        )}

        {/* Encuestas Tab */}
        {activeTab === "encuestas" && (
          <EncuestasTab token={token} />
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="text"
                  placeholder="Buscar por email o nombre..."
                  value={searchUser}
                  onChange={(e) => setSearchUser(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500"
                />
              </div>
              <span className="text-sm text-slate-400">{filteredUsers.length} usuarios</span>
            </div>
            
            <Card className="glass border-slate-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900/50">
                    <tr>
                      <th className="text-left p-3 text-xs text-slate-400 font-medium">Usuario</th>
                      <th className="text-left p-3 text-xs text-slate-400 font-medium">Estado</th>
                      <th className="text-left p-3 text-xs text-slate-400 font-medium">Días Restantes</th>
                      <th className="text-left p-3 text-xs text-slate-400 font-medium">Referido</th>
                      <th className="text-left p-3 text-xs text-slate-400 font-medium">Registro</th>
                      <th className="text-left p-3 text-xs text-slate-400 font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {filteredUsers.map((u) => {
                      const daysLeft = getDaysLeft(u.subscription);
                      const isExpired = daysLeft !== null && daysLeft <= 0;
                      const isLow = daysLeft !== null && daysLeft > 0 && daysLeft <= 2;
                      
                      return (
                        <tr key={u.id} className="hover:bg-slate-800/30">
                          <td className="p-3">
                            <div>
                              <p className="text-white text-sm">{u.full_name || "Sin nombre"}</p>
                              <p className="text-xs text-slate-400">{u.email}</p>
                            </div>
                          </td>
                          <td className="p-3">
                            {getStatusBadge(u.subscription)}
                          </td>
                          <td className="p-3">
                            {daysLeft !== null ? (
                              <span className={`text-sm font-medium ${
                                isExpired ? 'text-red-400' : 
                                isLow ? 'text-amber-400' : 
                                'text-green-400'
                              }`}>
                                {isExpired ? `Expiró hace ${Math.abs(daysLeft)} días` : `${daysLeft} días`}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-500">-</span>
                            )}
                          </td>
                          <td className="p-3">
                            {u.subscription?.referral_source ? (
                              <div>
                                <p className="text-xs text-slate-300">{sourceLabels[u.subscription.referral_source] || u.subscription.referral_source}</p>
                                {u.subscription.referrer_name && (
                                  <p className="text-xs text-purple-400">{u.subscription.referrer_name}</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-500">-</span>
                            )}
                          </td>
                          <td className="p-3">
                            <span className="text-xs text-slate-400">
                              {new Date(u.created_at).toLocaleDateString("es-PA")}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {/* Botón extender días */}
                              {extendingUser === u.id ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    min="1"
                                    max="365"
                                    value={daysToAdd}
                                    onChange={(e) => setDaysToAdd(parseInt(e.target.value) || 5)}
                                    className="w-16 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                                  />
                                  <button
                                    onClick={() => extendTrial(u.id, daysToAdd)}
                                    disabled={actionLoading}
                                    className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs hover:bg-green-500/30"
                                  >
                                    {actionLoading ? "..." : "✓"}
                                  </button>
                                  <button
                                    onClick={() => { setExtendingUser(null); setDaysToAdd(5); }}
                                    className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs hover:bg-red-500/30"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <button
                                    onClick={() => setExtendingUser(u.id)}
                                    className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs hover:bg-amber-500/30"
                                    title="Agregar días"
                                  >
                                    +Días
                                  </button>
                                  {/* Selector de plan */}
                                  <select
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        setUserPlan(u.id, e.target.value);
                                        e.target.value = "";
                                      }
                                    }}
                                    className="px-2 py-1 bg-slate-800 border border-slate-600 rounded text-xs text-slate-300"
                                    defaultValue=""
                                  >
                                    <option value="" disabled>Plan</option>
                                    <option value="trial">Trial 5d</option>
                                    <option value="mensual">Mensual 30d</option>
                                    <option value="anual">Anual 365d</option>
                                  </select>
                                  {/* Botón resetear contraseña */}
                                  <button
                                    onClick={() => setResetPasswordUser(u.id)}
                                    className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs hover:bg-blue-500/30"
                                    title="Resetear contraseña"
                                  >
                                    <Key size={12} />
                                  </button>
                                </>
                              )}
                              {/* Modal inline para resetear contraseña */}
                              {resetPasswordUser === u.id && (
                                <div className="flex items-center gap-2 mt-2">
                                  <input
                                    type="text"
                                    placeholder="Nueva contraseña"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-32 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                                  />
                                  <button
                                    onClick={() => handleResetPassword(u.id)}
                                    disabled={actionLoading}
                                    className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs hover:bg-green-500/30"
                                  >
                                    {actionLoading ? "..." : "✓"}
                                  </button>
                                  <button
                                    onClick={() => { setResetPasswordUser(null); setNewPassword(""); }}
                                    className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs hover:bg-red-500/30"
                                  >
                                    ✕
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Referrals Tab */}
        {activeTab === "referrals" && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="glass border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg">¿Cómo nos conocieron?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats.referrals.by_source).length > 0 ? (
                    Object.entries(stats.referrals.by_source).map(([source, count]) => (
                      <div key={source} className="flex items-center justify-between">
                        <span className="text-slate-300">{sourceLabels[source] || source}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-slate-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-purple-500 rounded-full"
                              style={{ width: `${(count / stats.referrals.total) * 100}%` }}
                            />
                          </div>
                          <span className="text-white font-medium w-8 text-right">{count}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500 text-center py-4">Sin datos de referidos aún</p>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Card className="glass border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg">Vendedores Top</CardTitle>
              </CardHeader>
              <CardContent>
                {users.filter(u => u.subscription?.referrer_code).length > 0 ? (
                  <div className="space-y-2">
                    {/* Agrupar por código de referido */}
                    {Object.entries(
                      users.reduce((acc, u) => {
                        const code = u.subscription?.referrer_code;
                        const name = u.subscription?.referrer_name;
                        if (code) {
                          if (!acc[code]) acc[code] = { name, count: 0 };
                          acc[code].count++;
                        }
                        return acc;
                      }, {})
                    ).sort((a, b) => b[1].count - a[1].count).map(([code, data]) => (
                      <div key={code} className="flex items-center justify-between p-2 bg-slate-800/30 rounded-lg">
                        <div>
                          <p className="text-white text-sm">{data.name || "Sin nombre"}</p>
                          <p className="text-xs text-purple-400">{code}</p>
                        </div>
                        <span className="text-lg font-bold text-emerald-400">{data.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Sin vendedores registrados aún</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* CRM Tab */}
        {activeTab === "crm" && (
          <div className="space-y-6">
            <Card className="glass border-slate-800">
              <CardContent className="p-8 text-center">
                <Target className="mx-auto text-purple-400 mb-4" size={48} />
                <h3 className="text-xl font-bold text-white mb-2">CRM de Ventas</h3>
                <p className="text-slate-400 mb-6">
                  Gestiona prospectos, registra llamadas y envía emails de seguimiento automáticamente.
                </p>
                <Button 
                  onClick={() => setShowCRM(true)}
                  className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
                >
                  <Target className="mr-2" size={18} />
                  Abrir CRM de Ventas
                </Button>
              </CardContent>
            </Card>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="glass border-slate-800">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-white mb-2">Agente Virtual</h4>
                  <p className="text-sm text-slate-400">Para empresas que quieren atención 24/7</p>
                </CardContent>
              </Card>
              <Card className="glass border-slate-800">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-white mb-2">Tutora IA</h4>
                  <p className="text-sm text-slate-400">Para colegios y estudiantes</p>
                </CardContent>
              </Card>
              <Card className="glass border-slate-800">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-white mb-2">LILA Legal</h4>
                  <p className="text-sm text-slate-400">Para abogados panameños</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* CRM Modal */}
        {showCRM && (
          <CRMVentas 
            token={token} 
            onClose={() => setShowCRM(false)} 
          />
        )}
      </main>
    </div>
  );
};

// Resources Page - Portales Gubernamentales
const ResourcesPage = () => {
  const navigate = useNavigate();
  const [recursos, setRecursos] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    fetchRecursos();
  }, []);

  const fetchRecursos = async () => {
    try {
      const response = await axios.get(`${API}/recursos`);
      setRecursos(response.data);
    } catch (error) {
      toast.error("Error al cargar recursos");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const response = await axios.get(`${API}/recursos/buscar/${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      toast.error("Error en búsqueda");
    }
  };

  const iconMap = {
    registro_publico: Building,
    migracion: Plane,
    municipio_panama: Building,
    tribunal_electoral: Users,
    mitradel: Briefcase,
    organo_judicial: Gavel,
    ministerio_publico: Shield,
    contraloria: FileText,
    css: Users,
    dgi: FileText,
    anati: Globe,
    amp: Anchor,
    superintendencia_bancos: Building
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="resources-page">
      <header className="glass sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
            ← Volver
          </Button>
          <h1 className="text-lg font-bold gradient-text">Portales de Panamá</h1>
          <div></div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 pb-24">
        {/* Search */}
        <div className="mb-6">
          <div className="flex gap-2">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Buscar trámite (ej: paz y salvo, antecedentes...)"
              className="bg-slate-900/50 border-slate-700"
            />
            <Button onClick={handleSearch} className="btn-primary">
              <Search size={18} />
            </Button>
          </div>
          
          {searchResults.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm text-slate-400">Resultados:</p>
              {searchResults.map((result, idx) => (
                <a
                  key={idx}
                  href={result.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-3 glass rounded-lg hover:border-cyan-500/30 border border-slate-800"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{result.tramite}</p>
                      <p className="text-xs text-slate-400">{result.entidad}</p>
                    </div>
                    <ExternalLink size={16} className="text-cyan-400" />
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Resources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(recursos).map(([key, recurso]) => {
            const IconComponent = iconMap[key] || Globe;
            return (
              <Card key={key} className="glass border-slate-800">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                      <IconComponent className="text-cyan-400" size={20} />
                    </div>
                    <div>
                      <CardTitle className="text-base">{recurso.nombre}</CardTitle>
                      <CardDescription className="text-xs">{recurso.descripcion}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {recurso.tramites?.slice(0, 4).map((tramite, idx) => (
                      <a
                        key={idx}
                        href={tramite.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-2 rounded-lg bg-slate-900/50 hover:bg-slate-800/50 text-sm"
                      >
                        <span className="text-slate-300">{tramite.nombre}</span>
                        <ExternalLink size={14} className="text-slate-500" />
                      </a>
                    ))}
                  </div>
                  <a
                    href={recurso.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 flex items-center justify-center gap-2 text-cyan-400 text-sm hover:text-cyan-300"
                  >
                    Ir al portal <ExternalLink size={14} />
                  </a>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
};

// ===================== TRANSCRIPCIÓN DE AUDIO =====================
const TranscripcionPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [transcription, setTranscription] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validar tamaño (25MB)
      if (selectedFile.size > 25 * 1024 * 1024) {
        toast.error("Archivo muy grande. Máximo 25MB");
        return;
      }
      setFile(selectedFile);
      setTranscription(null);
    }
  };

  const transcribe = async () => {
    if (!file) {
      toast.error("Selecciona un archivo de audio");
      return;
    }

    setTranscribing(true);
    toast.info("Procesando audio... Esto puede tardar unos segundos", { duration: 5000 });
    
    try {
      console.log("Iniciando transcripción de:", file.name, "Tamaño:", file.size);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 2 minutos timeout para archivos grandes
      });

      console.log("Transcripción exitosa:", response.data);
      setTranscription(response.data);
      toast.success("¡Transcripción completada exitosamente!");
    } catch (error) {
      console.error("Error en transcripción:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Error desconocido";
      toast.error(`Error al transcribir: ${errorMsg}`, { duration: 5000 });
    } finally {
      setTranscribing(false);
    }
  };

  const copyToClipboard = () => {
    if (transcription?.text) {
      navigator.clipboard.writeText(transcription.text);
      toast.success("Copiado al portapapeles");
    }
  };

  return (
    <div className="min-h-screen bg-pattern" data-testid="transcripcion-page">
      <header className="glass sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
              ← Volver
            </Button>
            <h1 className="text-xl font-bold">🎙️ Transcripción con IA</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="glass border-slate-800 mb-6">
          <CardHeader>
            <CardTitle className="text-pink-400">Transcribir Audio o Video</CardTitle>
            <CardDescription>
              Sube grabaciones de audiencias, declaraciones o cualquier audio legal para transcribirlo automáticamente
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,video/*,.mp3,.wav,.m4a,.mp4,.webm,.ogg"
              onChange={handleFileSelect}
              className="hidden"
            />

            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center cursor-pointer hover:border-pink-500/50 transition-colors"
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <Mic className="text-pink-400" size={24} />
                  <div>
                    <p className="text-white font-medium">{file.name}</p>
                    <p className="text-slate-400 text-sm">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                    className="text-red-400"
                  >
                    <X size={16} />
                  </Button>
                </div>
              ) : (
                <div>
                  <Upload className="mx-auto text-slate-400 mb-2" size={40} />
                  <p className="text-slate-300">Haz clic para seleccionar un archivo</p>
                  <p className="text-slate-500 text-sm mt-1">MP3, WAV, M4A, MP4, WebM (máx. 25MB)</p>
                </div>
              )}
            </div>

            <Button 
              onClick={transcribe} 
              disabled={!file || transcribing}
              className="w-full bg-pink-600 hover:bg-pink-500 disabled:opacity-50"
            >
              {transcribing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Procesando Audio...
                </>
              ) : (
                <>
                  <Mic className="mr-2" size={18} />
                  Transcribir Audio
                </>
              )}
            </Button>

            {/* Indicador de progreso más visible */}
            {transcribing && (
              <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-pink-500/30">
                <div className="flex items-center gap-3">
                  <div className="animate-pulse flex space-x-1">
                    <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                    <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                    <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                  </div>
                  <div>
                    <p className="text-pink-400 font-medium">Procesando transcripción...</p>
                    <p className="text-slate-400 text-sm">Esto puede tardar entre 5-30 segundos dependiendo del tamaño del archivo</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {transcription && (
          <Card className="glass border-slate-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-green-400">Transcripción</CardTitle>
                <Button variant="ghost" size="sm" onClick={copyToClipboard}>
                  <FileText className="mr-1" size={16} />
                  Copiar
                </Button>
              </div>
              {transcription.duration && (
                <CardDescription>Duración: {Math.round(transcription.duration)} segundos</CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="bg-slate-800/50 rounded-lg p-4 max-h-96 overflow-y-auto">
                <p className="text-slate-200 whitespace-pre-wrap">{transcription.text}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

// ===================== GESTIÓN DE CASOS Y NOTAS =====================
const MisCasosPage = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewCase, setShowNewCase] = useState(false);
  const [newCase, setNewCase] = useState({ titulo: "", cliente: "", tipo: "civil", descripcion: "" });
  const [newNote, setNewNote] = useState("");

  useEffect(() => {
    loadCases();
  }, []);

  const loadCases = async () => {
    try {
      const response = await axios.get(`${API}/my-cases`);
      setCases(response.data);
    } catch (error) {
      toast.error("Error al cargar casos");
    } finally {
      setLoading(false);
    }
  };

  const createCase = async () => {
    if (!newCase.titulo || !newCase.cliente) {
      toast.error("Completa título y cliente");
      return;
    }
    try {
      const response = await axios.post(`${API}/my-cases`, newCase);
      setCases([response.data, ...cases]);
      setShowNewCase(false);
      setNewCase({ titulo: "", cliente: "", tipo: "civil", descripcion: "" });
      toast.success("Caso creado");
    } catch (error) {
      toast.error("Error al crear caso");
    }
  };

  const selectCase = async (caso) => {
    setSelectedCase(caso);
    try {
      const response = await axios.get(`${API}/my-cases/${caso.id}/notes`);
      setNotes(response.data);
    } catch (error) {
      toast.error("Error al cargar notas");
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedCase) return;
    try {
      const response = await axios.post(`${API}/my-cases/${selectedCase.id}/notes`, {
        caso_id: selectedCase.id,
        contenido: newNote,
        tipo: "general"
      });
      setNotes([response.data, ...notes]);
      setNewNote("");
      toast.success("Nota agregada");
    } catch (error) {
      toast.error("Error al agregar nota");
    }
  };

  const deleteCase = async (casoId) => {
    if (!confirm("¿Eliminar este caso y todas sus notas?")) return;
    try {
      await axios.delete(`${API}/my-cases/${casoId}`);
      setCases(cases.filter(c => c.id !== casoId));
      if (selectedCase?.id === casoId) {
        setSelectedCase(null);
        setNotes([]);
      }
      toast.success("Caso eliminado");
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const tipoColors = {
    civil: "bg-blue-500/20 text-blue-400",
    penal: "bg-red-500/20 text-red-400",
    laboral: "bg-amber-500/20 text-amber-400",
    familia: "bg-pink-500/20 text-pink-400",
    mercantil: "bg-green-500/20 text-green-400",
    constitucional: "bg-purple-500/20 text-purple-400"
  };

  return (
    <div className="min-h-screen bg-pattern" data-testid="mis-casos-page">
      <header className="glass sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
              ← Volver
            </Button>
            <h1 className="text-xl font-bold">📂 Mis Casos</h1>
          </div>
          <Button onClick={() => setShowNewCase(true)} className="bg-cyan-600 hover:bg-cyan-500">
            <Plus size={18} className="mr-1" /> Nuevo Caso
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista de Casos */}
          <div className="lg:col-span-1 space-y-3">
            <h2 className="text-lg font-medium text-slate-300 mb-3">
              {cases.length} Casos
            </h2>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-cyan-400 border-t-transparent mx-auto" />
              </div>
            ) : cases.length === 0 ? (
              <Card className="glass border-slate-800 p-6 text-center">
                <FolderOpen className="mx-auto text-slate-500 mb-2" size={40} />
                <p className="text-slate-400">No tienes casos aún</p>
                <Button onClick={() => setShowNewCase(true)} className="mt-3" variant="outline">
                  Crear primer caso
                </Button>
              </Card>
            ) : (
              cases.map(caso => (
                <Card 
                  key={caso.id}
                  onClick={() => selectCase(caso)}
                  className={`glass border-slate-800 cursor-pointer transition-all hover:border-cyan-500/50 ${selectedCase?.id === caso.id ? 'border-cyan-500' : ''}`}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-white">{caso.titulo}</h3>
                        <p className="text-sm text-slate-400">{caso.cliente}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${tipoColors[caso.tipo] || 'bg-slate-500/20 text-slate-400'}`}>
                            {caso.tipo}
                          </span>
                          <span className="text-xs text-slate-500">{caso.notas_count || 0} notas</span>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); deleteCase(caso.id); }}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Panel de Notas */}
          <div className="lg:col-span-2">
            {selectedCase ? (
              <Card className="glass border-slate-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-cyan-400">{selectedCase.titulo}</CardTitle>
                      <CardDescription>{selectedCase.cliente} • {selectedCase.tipo}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selectedCase.descripcion && (
                    <div className="bg-slate-800/50 rounded-lg p-3 text-sm text-slate-300">
                      {selectedCase.descripcion}
                    </div>
                  )}

                  {/* Nueva nota */}
                  <div className="flex gap-2">
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Agregar una nota..."
                      className="bg-slate-800 border-slate-700 flex-1"
                      rows={2}
                    />
                    <Button onClick={addNote} className="bg-cyan-600 hover:bg-cyan-500">
                      <Plus size={18} />
                    </Button>
                  </div>

                  {/* Lista de notas */}
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {notes.length === 0 ? (
                      <p className="text-center text-slate-500 py-4">No hay notas aún</p>
                    ) : (
                      notes.map(note => (
                        <div key={note.id} className="bg-slate-800/30 rounded-lg p-3 border border-slate-700">
                          <p className="text-slate-200 text-sm whitespace-pre-wrap">{note.contenido}</p>
                          <p className="text-xs text-slate-500 mt-2">
                            {new Date(note.created_at).toLocaleDateString('es-PA', { 
                              day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="glass border-slate-800 p-12 text-center">
                <FolderOpen className="mx-auto text-slate-500 mb-3" size={50} />
                <p className="text-slate-400">Selecciona un caso para ver sus notas</p>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Modal Nuevo Caso */}
      <Dialog open={showNewCase} onOpenChange={setShowNewCase}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle>Nuevo Caso</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400">Título del Caso</label>
              <Input
                value={newCase.titulo}
                onChange={(e) => setNewCase({...newCase, titulo: e.target.value})}
                placeholder="Ej: Divorcio García vs López"
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400">Cliente</label>
              <Input
                value={newCase.cliente}
                onChange={(e) => setNewCase({...newCase, cliente: e.target.value})}
                placeholder="Nombre del cliente"
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400">Tipo de Caso</label>
              <select
                value={newCase.tipo}
                onChange={(e) => setNewCase({...newCase, tipo: e.target.value})}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
              >
                <option value="civil">Civil</option>
                <option value="penal">Penal</option>
                <option value="laboral">Laboral</option>
                <option value="familia">Familia</option>
                <option value="mercantil">Mercantil</option>
                <option value="constitucional">Constitucional</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-400">Descripción (opcional)</label>
              <Textarea
                value={newCase.descripcion}
                onChange={(e) => setNewCase({...newCase, descripcion: e.target.value})}
                placeholder="Breve descripción del caso"
                className="bg-slate-800 border-slate-700"
                rows={3}
              />
            </div>
            <Button onClick={createCase} className="w-full bg-cyan-600 hover:bg-cyan-500">
              Crear Caso
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===================== ÓRGANO JUDICIAL - CONSULTAS =====================
const OrganoJudicialPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [urls, setUrls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consultaActiva, setConsultaActiva] = useState(null);
  const [searchParams, setSearchParams] = useState({});
  const [resultado, setResultado] = useState(null);
  const [buscando, setBuscando] = useState(false);

  useEffect(() => {
    fetchUrls();
  }, []);

  const fetchUrls = async () => {
    try {
      const response = await axios.get(`${API}/organo-judicial/urls`);
      setUrls(response.data);
    } catch (error) {
      toast.error("Error al cargar servicios");
    } finally {
      setLoading(false);
    }
  };

  const realizarConsulta = async (tipo) => {
    setBuscando(true);
    setResultado(null);
    try {
      let endpoint = "";
      let data = {};
      
      if (tipo === "idoneidad") {
        endpoint = "/organo-judicial/idoneidad";
        data = { cedula: searchParams.cedula, nombre: searchParams.nombre };
      } else if (tipo === "repartos") {
        endpoint = "/organo-judicial/repartos";
        data = { 
          abogado: searchParams.abogado, 
          demandante: searchParams.demandante,
          demandado: searchParams.demandado
        };
      } else if (tipo === "edictos") {
        endpoint = "/organo-judicial/edictos";
        data = { tribunal: searchParams.tribunal };
      }
      
      const response = await axios.post(`${API}${endpoint}`, data);
      setResultado(response.data);
    } catch (error) {
      toast.error("Error en la consulta");
    } finally {
      setBuscando(false);
    }
  };

  const consultas = [
    { 
      id: "idoneidad", 
      nombre: "Idoneidad de Abogados", 
      icon: <UserCheck size={24} />,
      descripcion: "Verificar si un abogado está habilitado para ejercer",
      color: "from-blue-500 to-cyan-500",
      campos: [
        { key: "cedula", label: "Cédula", placeholder: "Ej: 8-123-456" },
        { key: "nombre", label: "Nombre", placeholder: "Ej: García" }
      ]
    },
    { 
      id: "repartos", 
      nombre: "Reparto de Expedientes", 
      icon: <FileText size={24} />,
      descripcion: "Consultar casos asignados por fecha o partes",
      color: "from-purple-500 to-pink-500",
      campos: [
        { key: "abogado", label: "Abogado", placeholder: "Nombre del abogado" },
        { key: "demandante", label: "Demandante", placeholder: "Nombre demandante" },
        { key: "demandado", label: "Demandado", placeholder: "Nombre demandado" }
      ]
    },
    { 
      id: "edictos", 
      nombre: "Edictos Judiciales", 
      icon: <ScrollText size={24} />,
      descripcion: "Ver edictos publicados por tribunales",
      color: "from-amber-500 to-orange-500",
      campos: [
        { key: "tribunal", label: "Tribunal", placeholder: "Nombre del tribunal" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-pattern" data-testid="organo-judicial-page">
      <header className="glass border-b border-slate-800 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Órgano Judicial</h1>
            <p className="text-xs text-slate-400">Consultas Públicas</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 pb-24">
        {/* Info Banner */}
        <div className="glass border border-blue-500/30 rounded-xl p-4 mb-6 bg-blue-500/5">
          <div className="flex gap-3">
            <Info size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-300 font-medium text-sm">Integración con el Órgano Judicial de Panamá</p>
              <p className="text-slate-400 text-xs mt-1">
                LILA te proporciona acceso directo a los servicios de consulta pública del Órgano Judicial. 
                Algunas consultas requieren completar un CAPTCHA en el sitio oficial.
              </p>
            </div>
          </div>
        </div>

        {/* Consultas Grid */}
        <div className="grid gap-4 mb-6">
          {consultas.map((consulta) => (
            <div 
              key={consulta.id}
              className={`glass border rounded-xl overflow-hidden transition-all ${
                consultaActiva === consulta.id ? 'border-purple-500' : 'border-slate-700'
              }`}
            >
              {/* Header de la consulta */}
              <button
                onClick={() => {
                  setConsultaActiva(consultaActiva === consulta.id ? null : consulta.id);
                  setResultado(null);
                  setSearchParams({});
                }}
                className="w-full p-4 flex items-center gap-4 hover:bg-slate-800/50 transition-colors"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${consulta.color} flex items-center justify-center text-white`}>
                  {consulta.icon}
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-white">{consulta.nombre}</h3>
                  <p className="text-sm text-slate-400">{consulta.descripcion}</p>
                </div>
                <ChevronDown 
                  size={20} 
                  className={`text-slate-400 transition-transform ${consultaActiva === consulta.id ? 'rotate-180' : ''}`} 
                />
              </button>

              {/* Formulario expandido */}
              {consultaActiva === consulta.id && (
                <div className="border-t border-slate-700 p-4 space-y-4 animate-slide-up">
                  {consulta.campos.map((campo) => (
                    <div key={campo.key}>
                      <label className="text-sm text-slate-400 block mb-1">{campo.label}</label>
                      <input
                        type="text"
                        placeholder={campo.placeholder}
                        value={searchParams[campo.key] || ""}
                        onChange={(e) => setSearchParams({...searchParams, [campo.key]: e.target.value})}
                        className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                      />
                    </div>
                  ))}

                  <Button
                    onClick={() => realizarConsulta(consulta.id)}
                    disabled={buscando}
                    className={`w-full bg-gradient-to-r ${consulta.color} hover:opacity-90`}
                  >
                    {buscando ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Consultando...
                      </>
                    ) : (
                      <>
                        <Search size={18} className="mr-2" />
                        Consultar
                      </>
                    )}
                  </Button>

                  {/* Resultado */}
                  {resultado && (
                    <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-slate-600">
                      {resultado.mensaje && (
                        <p className="text-amber-400 text-sm mb-3">{resultado.mensaje}</p>
                      )}
                      
                      {resultado.instrucciones && (
                        <div className="mb-4">
                          <p className="text-slate-300 font-medium mb-2">Instrucciones:</p>
                          <ol className="text-slate-400 text-sm space-y-1">
                            {resultado.instrucciones.map((inst, i) => (
                              <li key={i}>{inst}</li>
                            ))}
                          </ol>
                        </div>
                      )}

                      {resultado.url_consulta && (
                        <a
                          href={resultado.url_consulta}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-white text-sm font-medium transition-colors"
                        >
                          <ExternalLink size={16} />
                          Abrir Consulta Oficial
                        </a>
                      )}

                      {resultado.jurisdicciones_disponibles && (
                        <div className="mt-4">
                          <p className="text-slate-400 text-xs">Jurisdicciones disponibles:</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {resultado.jurisdicciones_disponibles.map((j, i) => (
                              <span key={i} className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-300">{j}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Enlaces Directos */}
        <div className="glass border border-slate-700 rounded-xl p-4">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Scale size={20} className="text-purple-400" />
            Otros Servicios del Órgano Judicial
          </h3>
          <div className="grid gap-2">
            {[
              { nombre: "Remates Judiciales", url: "https://www.organojudicial.gob.pa/remates-judiciales-1" },
              { nombre: "Fallos y Registro Judicial", url: "https://www.organojudicial.gob.pa/registro-judicial" },
              { nombre: "Biblioteca Judicial", url: "https://biblioteca.organojudicial.gob.pa/" },
              { nombre: "Sistema Penal Acusatorio", url: "https://www.organojudicial.gob.pa/sistema-penal-acusatorio" },
              { nombre: "Repositorio Digital Judicial", url: "https://repositoriodigital.organojudicial.gob.pa/" }
            ].map((servicio, i) => (
              <a
                key={i}
                href={servicio.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg transition-colors group"
              >
                <span className="text-slate-300 text-sm">{servicio.nombre}</span>
                <ExternalLink size={16} className="text-slate-500 group-hover:text-purple-400 transition-colors" />
              </a>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

// ===================== AUDIENCIAS DEL ÓRGANO JUDICIAL =====================
const AudienciasPage = () => {
  const navigate = useNavigate();
  const [audiencias, setAudiencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoria, setCategoria] = useState("todas");
  const [busqueda, setBusqueda] = useState("");
  const [categorias, setCategorias] = useState([]);
  const [casos, setCasos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => {
    loadAudiencias();
  }, [categoria]);

  useEffect(() => {
    // Debounce search
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      loadAudiencias();
    }, 300);
    setSearchTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [busqueda]);

  const loadAudiencias = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (categoria !== "todas") params.append("categoria", categoria);
      if (busqueda) params.append("buscar", busqueda);
      
      const response = await axios.get(`${API}/audiencias?${params.toString()}`);
      setAudiencias(response.data.audiencias);
      setCategorias(response.data.categorias);
      setCasos(response.data.casos);
    } catch (error) {
      toast.error("Error al cargar audiencias");
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (duracion) => {
    return duracion;
  };

  const getCategoryColor = (cat) => {
    switch(cat.toLowerCase()) {
      case "penal": return "bg-red-500/20 text-red-400 border-red-500/30";
      case "civil": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "educativo": return "bg-green-500/20 text-green-400 border-green-500/30";
      default: return "bg-purple-500/20 text-purple-400 border-purple-500/30";
    }
  };

  return (
    <div className="min-h-screen bg-pattern">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
              <ArrowLeft size={20} />
            </Button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Play className="text-red-500" size={24} />
              Audiencias del Órgano Judicial
            </h1>
          </div>
          <span className="text-sm text-slate-400">{audiencias.length} videos</span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filtros */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por caso, delito, tema..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-purple-500 focus:outline-none"
            />
          </div>
          <select
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            className="px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-xl text-white focus:border-purple-500 focus:outline-none"
          >
            <option value="todas">Todas las categorías</option>
            {categorias.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Casos destacados */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {casos.map(caso => (
            <button
              key={caso}
              onClick={() => setBusqueda(caso)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                busqueda === caso 
                  ? "bg-purple-500 text-white" 
                  : "bg-slate-800 text-slate-300 hover:bg-slate-700"
              }`}
            >
              {caso}
            </button>
          ))}
        </div>

        {/* Modal de Video */}
        {selectedVideo && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedVideo(null)}>
            <div className="w-full max-w-5xl" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedVideo.titulo}</h2>
                  <p className="text-slate-400">{selectedVideo.caso} • {selectedVideo.fecha}</p>
                </div>
                <button 
                  onClick={() => setSelectedVideo(null)}
                  className="p-2 bg-slate-800 rounded-full hover:bg-slate-700"
                >
                  <X size={24} />
                </button>
              </div>
              
              {/* Video Embebido */}
              <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-xl"
                  src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}?autoplay=1`}
                  title={selectedVideo.titulo}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              
              <div className="mt-4 p-4 bg-slate-900/50 rounded-xl">
                <p className="text-slate-300 mb-3">{selectedVideo.descripcion}</p>
                {selectedVideo.delitos.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    <span className="text-slate-400 text-sm">Delitos:</span>
                    {selectedVideo.delitos.map((delito, i) => (
                      <span key={i} className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">
                        {delito}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Lista de Audiencias */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : audiencias.length === 0 ? (
          <div className="text-center py-12">
            <Play className="mx-auto text-slate-600 mb-4" size={48} />
            <p className="text-slate-400">No se encontraron audiencias</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {audiencias.map((audiencia) => (
              <div
                key={audiencia.id}
                onClick={() => setSelectedVideo(audiencia)}
                className="glass border border-slate-800 rounded-xl overflow-hidden cursor-pointer hover:border-purple-500/50 transition-all group"
              >
                {/* Thumbnail */}
                <div className="relative aspect-video bg-slate-900">
                  <img
                    src={`https://img.youtube.com/vi/${audiencia.youtube_id}/hqdefault.jpg`}
                    alt={audiencia.titulo}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                      <Play className="text-white ml-1" size={32} fill="white" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 rounded text-xs text-white">
                    {audiencia.duracion}
                  </div>
                  <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs border ${getCategoryColor(audiencia.categoria)}`}>
                    {audiencia.categoria}
                  </div>
                </div>
                
                {/* Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-white line-clamp-2 mb-2 group-hover:text-purple-400 transition-colors">
                    {audiencia.titulo}
                  </h3>
                  <div className="flex items-center justify-between text-sm text-slate-400">
                    <span>{audiencia.caso}</span>
                    <span>{audiencia.fecha}</span>
                  </div>
                  {audiencia.delitos.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {audiencia.delitos.slice(0, 2).map((delito, i) => (
                        <span key={i} className="px-2 py-0.5 bg-slate-800 text-slate-400 rounded text-xs">
                          {delito}
                        </span>
                      ))}
                      {audiencia.delitos.length > 2 && (
                        <span className="px-2 py-0.5 bg-slate-800 text-slate-400 rounded text-xs">
                          +{audiencia.delitos.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                    <Eye size={14} />
                    <span>{audiencia.vistas.toLocaleString()} vistas</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info del canal */}
        <div className="mt-8 p-6 glass border border-slate-800 rounded-xl">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
              <Play className="text-white" size={24} fill="white" />
            </div>
            <div>
              <h3 className="font-bold text-white">OJudicialPanamá</h3>
              <p className="text-sm text-slate-400">Canal oficial del Órgano Judicial de Panamá</p>
            </div>
          </div>
          <p className="text-slate-300 text-sm mb-4">
            Videos de audiencias públicas, conferencias, capacitaciones y programas educativos del sistema judicial panameño.
          </p>
          <a
            href="https://www.youtube.com/@ojudicialpanama"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <ExternalLink size={16} />
            Ver canal en YouTube
          </a>
        </div>
      </main>
    </div>
  );
};

// ===================== BASE DE JURISPRUDENCIA =====================
const JurisprudenciaPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [materia, setMateria] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedSentencia, setSelectedSentencia] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState("manual"); // manual o pdf
  const [uploading, setUploading] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [reloading, setReloading] = useState(false);
  const [newSentencia, setNewSentencia] = useState({
    numero: "", fecha: "", tribunal: "Corte Suprema de Justicia", 
    sala: "", materia: "civil", tema: "", resumen: "", palabras_clave: ""
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/jurisprudencia/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error loading stats");
    }
  };

  const search = async () => {
    setSearching(true);
    try {
      const response = await axios.post(`${API}/jurisprudencia/buscar`, {
        query,
        materia: materia || null
      });
      setResults(response.data);
      if (response.data.length === 0) {
        toast.info("No se encontraron resultados");
      }
    } catch (error) {
      toast.error("Error en la búsqueda");
    } finally {
      setSearching(false);
    }
  };

  const materiaColors = {
    civil: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    penal: "bg-red-500/20 text-red-400 border-red-500/30",
    laboral: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    familia: "bg-pink-500/20 text-pink-400 border-pink-500/30",
    constitucional: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    maritimo: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
  };

  return (
    <div className="min-h-screen bg-pattern" data-testid="jurisprudencia-page">
      <header className="glass sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
              ← Volver
            </Button>
            <h1 className="text-xl font-bold">⚖️ Jurisprudencia Panameña</h1>
          </div>
          <div className="flex gap-2">
            {user?.email?.toLowerCase() === "admin@linorttech.com" && (
              <Button 
                onClick={async () => {
                  if (!confirm("¿Recargar todas las sentencias? Esto reemplazará las existentes.")) return;
                  setReloading(true);
                  try {
                    const response = await axios.post(`${API}/jurisprudencia/recargar`);
                    toast.success(response.data.message);
                    loadStats();
                  } catch (error) {
                    toast.error("Error al recargar");
                  } finally {
                    setReloading(false);
                  }
                }}
                disabled={reloading}
                variant="outline"
                className="border-amber-500 text-amber-400"
              >
                {reloading ? "Recargando..." : "🔄 Recargar BD"}
              </Button>
            )}
            <Button onClick={() => setShowAddModal(true)} className="bg-emerald-600 hover:bg-emerald-500">
              <Plus size={18} className="mr-1" /> Agregar
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="glass border-slate-800 p-4 text-center">
              <p className="text-3xl font-bold text-emerald-400">{stats.total_sentencias}</p>
              <p className="text-sm text-slate-400">Sentencias</p>
            </Card>
            <Card className="glass border-slate-800 p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">{stats.por_materia?.civil || 0}</p>
              <p className="text-sm text-slate-400">Civil</p>
            </Card>
            <Card className="glass border-slate-800 p-4 text-center">
              <p className="text-3xl font-bold text-red-400">{stats.por_materia?.penal || 0}</p>
              <p className="text-sm text-slate-400">Penal</p>
            </Card>
            <Card className="glass border-slate-800 p-4 text-center">
              <p className="text-3xl font-bold text-amber-400">{stats.por_materia?.laboral || 0}</p>
              <p className="text-sm text-slate-400">Laboral</p>
            </Card>
          </div>
        )}

        {/* Búsqueda */}
        <Card className="glass border-slate-800 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1">
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar por tema, número de sentencia, palabras clave..."
                  className="bg-slate-800 border-slate-700"
                  onKeyPress={(e) => e.key === 'Enter' && search()}
                />
              </div>
              <select
                value={materia}
                onChange={(e) => setMateria(e.target.value)}
                className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
              >
                <option value="">Todas las materias</option>
                <option value="civil">Civil</option>
                <option value="penal">Penal</option>
                <option value="laboral">Laboral</option>
                <option value="familia">Familia</option>
                <option value="constitucional">Constitucional</option>
                <option value="maritimo">Marítimo</option>
              </select>
              <Button onClick={search} disabled={searching} className="bg-emerald-600 hover:bg-emerald-500">
                {searching ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <><Search size={18} className="mr-1" /> Buscar</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Resultados */}
        <div className="space-y-4">
          {results.map(sentencia => (
            <Card 
              key={sentencia.id}
              className="glass border-slate-800 hover:border-emerald-500/50 transition-colors cursor-pointer"
              onClick={() => setSelectedSentencia(sentencia)}
            >
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                  <div>
                    <span className="text-emerald-400 font-mono font-medium">{sentencia.numero}</span>
                    <span className="text-slate-500 mx-2">|</span>
                    <span className="text-slate-400">{sentencia.fecha}</span>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded border ${materiaColors[sentencia.materia] || 'bg-slate-500/20 text-slate-400 border-slate-500/30'}`}>
                    {sentencia.materia}
                  </span>
                </div>
                <h3 className="text-white font-medium mb-2">{sentencia.tema}</h3>
                <p className="text-sm text-slate-400 line-clamp-2">{sentencia.resumen}</p>
                <div className="flex flex-wrap gap-1 mt-3">
                  {sentencia.palabras_clave?.slice(0, 4).map((palabra, i) => (
                    <span key={i} className="text-xs bg-slate-700/50 text-slate-300 px-2 py-0.5 rounded">
                      {palabra}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {results.length === 0 && !searching && (
          <Card className="glass border-slate-800 p-8 text-center">
            <Gavel className="mx-auto text-slate-500 mb-3" size={50} />
            <p className="text-slate-400">Busca sentencias por tema, número o palabras clave</p>
          </Card>
        )}
      </main>

      {/* Modal Detalle Sentencia */}
      <Dialog open={!!selectedSentencia} onOpenChange={() => setSelectedSentencia(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedSentencia && (
            <>
              <DialogHeader>
                <DialogTitle className="text-emerald-400">{selectedSentencia.numero}</DialogTitle>
                <DialogDescription>
                  {selectedSentencia.tribunal} • {selectedSentencia.sala} • {selectedSentencia.fecha}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm text-slate-400 mb-1">Tema</h4>
                  <p className="text-white">{selectedSentencia.tema}</p>
                </div>
                <div>
                  <h4 className="text-sm text-slate-400 mb-1">Resumen</h4>
                  <p className="text-slate-300 text-sm">{selectedSentencia.resumen}</p>
                </div>
                <div>
                  <h4 className="text-sm text-slate-400 mb-1">Palabras Clave</h4>
                  <div className="flex flex-wrap gap-1">
                    {selectedSentencia.palabras_clave?.map((palabra, i) => (
                      <span key={i} className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">
                        {palabra}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Agregar Sentencia */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Agregar Sentencia</DialogTitle>
            <DialogDescription>
              Sube un PDF o ingresa los datos manualmente
            </DialogDescription>
          </DialogHeader>
          
          {/* Tabs */}
          <div className="flex gap-2 mb-4">
            <Button 
              variant={addMode === "pdf" ? "default" : "outline"}
              onClick={() => setAddMode("pdf")}
              className={addMode === "pdf" ? "bg-emerald-600" : ""}
            >
              <Upload size={16} className="mr-1" /> Subir PDF
            </Button>
            <Button 
              variant={addMode === "manual" ? "default" : "outline"}
              onClick={() => setAddMode("manual")}
              className={addMode === "manual" ? "bg-emerald-600" : ""}
            >
              <FileText size={16} className="mr-1" /> Manual
            </Button>
          </div>

          {addMode === "pdf" && (
            <div className="space-y-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  setUploading(true);
                  setPdfData(null);
                  try {
                    const formData = new FormData();
                    formData.append('file', file);
                    const response = await axios.post(`${API}/jurisprudencia/procesar-pdf`, formData);
                    setPdfData(response.data.datos);
                    if (response.data.duplicada) {
                      toast.warning("Esta sentencia ya existe en la base de datos");
                    } else {
                      toast.success("PDF procesado. Verifica los datos.");
                    }
                  } catch (error) {
                    toast.error(error.response?.data?.detail || "Error al procesar PDF");
                  } finally {
                    setUploading(false);
                  }
                }}
                className="hidden"
              />
              
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-500/50"
              >
                {uploading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-emerald-400 border-t-transparent" />
                    <span>Procesando con IA...</span>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto text-slate-400 mb-2" size={40} />
                    <p className="text-slate-300">Haz clic para seleccionar un PDF</p>
                    <p className="text-slate-500 text-sm mt-1">La IA extraerá los datos automáticamente</p>
                  </>
                )}
              </div>

              {pdfData && (
                <div className="space-y-3 bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-emerald-400 font-medium">Datos extraídos:</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-400">Número:</span> <span className="text-white">{pdfData.numero}</span></div>
                    <div><span className="text-slate-400">Fecha:</span> <span className="text-white">{pdfData.fecha}</span></div>
                    <div><span className="text-slate-400">Tribunal:</span> <span className="text-white">{pdfData.tribunal}</span></div>
                    <div><span className="text-slate-400">Materia:</span> <span className="text-white">{pdfData.materia}</span></div>
                  </div>
                  <div className="text-sm">
                    <span className="text-slate-400">Tema:</span>
                    <p className="text-white">{pdfData.tema}</p>
                  </div>
                  <div className="text-sm">
                    <span className="text-slate-400">Resumen:</span>
                    <p className="text-white text-xs">{pdfData.resumen}</p>
                  </div>
                  <Button 
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/jurisprudencia/confirmar-pdf`, pdfData);
                        toast.success("Sentencia guardada");
                        setShowAddModal(false);
                        setPdfData(null);
                        loadStats();
                      } catch (error) {
                        toast.error("Error al guardar");
                      }
                    }}
                    className="w-full bg-emerald-600 hover:bg-emerald-500"
                  >
                    Confirmar y Guardar
                  </Button>
                </div>
              )}
            </div>
          )}

          {addMode === "manual" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400">Número *</label>
                  <Input
                    value={newSentencia.numero}
                    onChange={(e) => setNewSentencia({...newSentencia, numero: e.target.value})}
                    placeholder="SC-123-2024"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Fecha *</label>
                  <Input
                    type="date"
                    value={newSentencia.fecha}
                    onChange={(e) => setNewSentencia({...newSentencia, fecha: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400">Tribunal</label>
                  <Input
                    value={newSentencia.tribunal}
                    onChange={(e) => setNewSentencia({...newSentencia, tribunal: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Sala</label>
                  <Input
                    value={newSentencia.sala}
                    onChange={(e) => setNewSentencia({...newSentencia, sala: e.target.value})}
                    placeholder="Sala Penal"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400">Materia *</label>
                <select
                  value={newSentencia.materia}
                  onChange={(e) => setNewSentencia({...newSentencia, materia: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
                >
                  <option value="civil">Civil</option>
                  <option value="penal">Penal</option>
                  <option value="laboral">Laboral</option>
                  <option value="familia">Familia</option>
                  <option value="constitucional">Constitucional</option>
                  <option value="maritimo">Marítimo</option>
                  <option value="mercantil">Mercantil</option>
                  <option value="administrativo">Administrativo</option>
                  <option value="migracion">Migración</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400">Tema *</label>
                <Input
                  value={newSentencia.tema}
                  onChange={(e) => setNewSentencia({...newSentencia, tema: e.target.value})}
                  placeholder="Tema principal de la sentencia"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400">Resumen *</label>
                <Textarea
                  value={newSentencia.resumen}
                  onChange={(e) => setNewSentencia({...newSentencia, resumen: e.target.value})}
                  placeholder="Resumen del caso y la decisión..."
                  className="bg-slate-800 border-slate-700"
                  rows={4}
                />
              </div>
              <div>
                <label className="text-xs text-slate-400">Palabras clave (separadas por coma)</label>
                <Input
                  value={newSentencia.palabras_clave}
                  onChange={(e) => setNewSentencia({...newSentencia, palabras_clave: e.target.value})}
                  placeholder="palabra1, palabra2, palabra3"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <Button 
                onClick={async () => {
                  if (!newSentencia.numero || !newSentencia.tema || !newSentencia.resumen) {
                    toast.error("Completa los campos obligatorios");
                    return;
                  }
                  try {
                    await axios.post(`${API}/jurisprudencia/agregar`, {
                      ...newSentencia,
                      palabras_clave: newSentencia.palabras_clave.split(',').map(p => p.trim()).filter(p => p)
                    });
                    toast.success("Sentencia agregada");
                    setShowAddModal(false);
                    setNewSentencia({numero: "", fecha: "", tribunal: "Corte Suprema de Justicia", sala: "", materia: "civil", tema: "", resumen: "", palabras_clave: ""});
                    loadStats();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || "Error al guardar");
                  }
                }}
                className="w-full bg-emerald-600 hover:bg-emerald-500"
              >
                Guardar Sentencia
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===================== MAPAS MENTALES =====================
const MapasMentalesPage = () => {
  const navigate = useNavigate();
  const [tema, setTema] = useState("");
  const [tipo, setTipo] = useState("concepto");
  const [generating, setGenerating] = useState(false);
  const [mapa, setMapa] = useState(null);

  const generateMapa = async () => {
    if (!tema.trim()) {
      toast.error("Ingresa un tema legal");
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/mapas-mentales/generar`, { tema, tipo });
      setMapa(response.data);
      toast.success("Mapa generado");
    } catch (error) {
      toast.error("Error al generar mapa");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-pattern" data-testid="mapas-mentales-page">
      <header className="glass sticky top-0 z-50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-400">
              ← Volver
            </Button>
            <h1 className="text-xl font-bold">🗺️ Mapas Mentales Legales</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="glass border-slate-800 mb-6">
          <CardHeader>
            <CardTitle className="text-violet-400">Generar Mapa Mental</CardTitle>
            <CardDescription>
              Crea visualizaciones de conceptos legales, leyes o casos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-slate-400">Tema o Concepto Legal</label>
              <Input
                value={tema}
                onChange={(e) => setTema(e.target.value)}
                placeholder="Ej: Proceso de divorcio en Panamá, Elementos del delito de estafa..."
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400">Tipo</label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-white"
              >
                <option value="concepto">Concepto Legal</option>
                <option value="proceso">Proceso/Procedimiento</option>
                <option value="ley">Estructura de una Ley</option>
                <option value="caso">Análisis de Caso</option>
              </select>
            </div>
            <Button 
              onClick={generateMapa} 
              disabled={generating}
              className="w-full bg-violet-600 hover:bg-violet-500"
            >
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Generando...
                </>
              ) : (
                "Generar Mapa Mental"
              )}
            </Button>
          </CardContent>
        </Card>

        {mapa && (
          <Card className="glass border-slate-800">
            <CardHeader>
              <CardTitle className="text-violet-400">{mapa.titulo || tema}</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Nodo Central */}
              <div className="flex justify-center mb-8">
                <div className="bg-violet-500/20 border-2 border-violet-500 rounded-full px-6 py-3 text-violet-300 font-medium">
                  {mapa.nodo_central || tema}
                </div>
              </div>

              {/* Ramas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {mapa.ramas?.map((rama, i) => (
                  <div 
                    key={i} 
                    className="rounded-lg p-4 border"
                    style={{ 
                      backgroundColor: `${rama.color}15` || '#4ade8015',
                      borderColor: `${rama.color}50` || '#4ade8050'
                    }}
                  >
                    <h4 
                      className="font-medium mb-2"
                      style={{ color: rama.color || '#4ade80' }}
                    >
                      {rama.nombre}
                    </h4>
                    <ul className="space-y-1">
                      {rama.subnodos?.map((sub, j) => (
                        <li key={j} className="text-sm text-slate-300 flex items-start gap-2">
                          <span style={{ color: rama.color || '#4ade80' }}>•</span>
                          {sub}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Notas */}
              {mapa.notas?.length > 0 && (
                <div className="mt-6 p-4 bg-slate-800/50 rounded-lg">
                  <h4 className="text-sm text-slate-400 mb-2">Notas importantes:</h4>
                  <ul className="space-y-1">
                    {mapa.notas.map((nota, i) => (
                      <li key={i} className="text-sm text-slate-300">• {nota}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

// Main App
// Page Tracker Component
const PageTracker = () => {
  usePageTracking();
  return null;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageTracker />
        <Toaster position="top-center" richColors toastOptions={{ duration: 2000 }} />
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
          <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
          <Route path="/cases" element={<ProtectedRoute><CasesPage /></ProtectedRoute>} />
          <Route path="/call" element={<ProtectedRoute><CallMode /></ProtectedRoute>} />
          <Route path="/estudiante" element={<ProtectedRoute><StudentMode /></ProtectedRoute>} />
          <Route path="/herramientas" element={<ProtectedRoute><LawyerTools /></ProtectedRoute>} />
          <Route path="/premium" element={<PremiumPage />} />
          <Route path="/recursos" element={<ProtectedRoute><ResourcesPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/transcripcion" element={<ProtectedRoute><TranscripcionPage /></ProtectedRoute>} />
          <Route path="/mis-casos" element={<ProtectedRoute><MisCasosPage /></ProtectedRoute>} />
          <Route path="/jurisprudencia" element={<ProtectedRoute><JurisprudenciaPage /></ProtectedRoute>} />
          <Route path="/mapas-mentales" element={<ProtectedRoute><MapasMentalesPage /></ProtectedRoute>} />
          <Route path="/organo-judicial" element={<ProtectedRoute><OrganoJudicialPage /></ProtectedRoute>} />
          <Route path="/audiencias" element={<ProtectedRoute><AudienciasPage /></ProtectedRoute>} />
          <Route path="/encuesta" element={<EncuestaPage />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
